import React, { useState, useEffect, useRef } from 'react';
import { FiX, FiMonitor, FiImage, FiType, FiChevronDown, FiChevronUp, FiChevronLeft, FiMenu, FiCalendar, FiMessageSquare, FiUmbrella, FiCheckSquare, FiList, FiFileText, FiUsers, FiClock, FiActivity, FiSettings, FiLayout, FiEye, FiEyeOff, FiFilter, FiBarChart2 } from 'react-icons/fi';
import ModalCloseButton from '../Common/ModalCloseButton';
import { applyFrontendSettings } from '../../utils/frontendSettings';
import { useApp } from '../../context/AppContext';
import { ORG_DEPARTMENTS, JOB_TITLES } from '../../utils/orgStructure';

// Список вкладок
const TABS = [
  { id: 'primary', name: 'Основная', icon: FiLayout },
  { id: 'frontend', name: 'Фронтенд', icon: FiMonitor },
  { id: 'clock', name: 'Время/дата', icon: FiClock },
  { id: 'main-menu', name: 'Главное меню', icon: FiMenu },
  { id: 'calendar', name: 'Общий календарь', icon: FiCalendar },
  { id: 'chats', name: 'Чаты', icon: FiMessageSquare },
  { id: 'leaves', name: 'Отгулы', icon: FiUmbrella },
  { id: 'tasks', name: 'Задачи', icon: FiCheckSquare },
  { id: 'todo', name: 'Список дел', icon: FiList },
  { id: 'news', name: 'Новости', icon: FiFileText },
  { id: 'employees', name: 'Сотрудники компании', icon: FiUsers },
  { id: 'monitoring', name: 'Мониторинг времени', icon: FiClock },
  { id: 'worktime', name: 'Отработка отгулов', icon: FiActivity },
  { id: 'management', name: 'Управление', icon: FiSettings },
  { id: 'orders', name: 'Заказы', icon: FiList },
  { id: 'vpr', name: 'ВПР', icon: FiBarChart2 },
  { id: 'sidebar-visibility', name: 'Видимость кнопок сайтбара', icon: FiEye, restricted: true },
];

// Список кнопок сайтбара для управления видимостью
const SIDEBAR_BUTTONS = [
  { key: 'news', label: 'Новости' },
  { key: 'employees', label: 'Сотрудники' },
  { key: 'chats', label: 'Чаты' },
  { key: 'todo', label: 'Список дел' },
  { key: 'tasks', label: 'Задачи' },
  { key: 'all-leaves', label: 'Общий календарь' },
  { key: 'leaves', label: 'Отгулы' },
  { key: 'leaves-worktime', label: 'Отработка отгулов' },
  { key: 'worktime', label: 'Мониторинг времени' },
  { key: 'admin', label: 'Управление' },
];

// Список отделов + должностей для видимости кнопок (совместимость со старыми настройками)
const ALL_DEPARTMENTS = [...ORG_DEPARTMENTS, ...JOB_TITLES];

const DEFAULT_SETTINGS = {
  // Основной фон
  mainBackground: '#111827',
  mainBackgroundImage: '',
  mainBackgroundImageOpacity: 1,
  selectedMonitoringReport: 'app-usage-report',
  
  // Фон модалок (общий дефолт)
  modalBackground: '#1f2937',
  modalBackgroundImage: '',
  modalBackgroundImageOpacity: 1,
  applyGlobalModalBackground: true,
  modalTextColor: '#ffffff',
  modalDateTextColor: '#ffffff',
  modalHeaderTextColor: '#60a5fa',
  modalFieldLabelTextColor: '#d1d5db',
  frontendSettingsModalTextColor: '#ffffff',
  modalBorderColor: 'rgba(75, 85, 99, 0.3)',
  modalButtonBackground: '#3b82f6',
  modalButtonTextColor: '#ffffff',
  modalInputBackground: '#2d3748',
  modalInputTextColor: '#ffffff',
  modalFilterBackground: 'rgba(255, 193, 7, 0.08)',
  modalFilterTextColor: '#a0aec0',
  modalFilterBorderColor: 'rgba(255, 193, 7, 0.3)',
  modalReportBlockBackground: 'rgba(67, 233, 123, 0.05)',
  modalActionButtonBackground: '#3b82f6',
  modalActionButtonTextColor: '#ffffff',
  modalActionButtonBorderColor: 'rgba(59, 130, 246, 0.6)',
  modalPrimaryActionButtonBackground: '#22c55e',
  modalPrimaryActionButtonTextColor: '#ffffff',
  modalPrimaryActionButtonBorderColor: 'rgba(34, 197, 94, 0.7)',
  modalDangerActionButtonBackground: '#ef4444',
  modalDangerActionButtonTextColor: '#ffffff',
  modalDangerActionButtonBorderColor: 'rgba(239, 68, 68, 0.7)',
  enableGlobalActionButtons: true,
  clockBackground: 'rgba(17, 24, 39, 0.7)',
  clockBorderColor: 'rgba(255, 255, 255, 0.16)',
  clockTimeColor: '#eaf2f9',
  clockWeekdayColor: '#cbd5f5',
  clockMonthColor: '#eaf2f9',
  clockOffsetX: 0,
  clockOffsetY: 0,
  
  // Сайдбар
  sidebarButtonBackground: '#3b82f6',
  sidebarButtonBackgroundImage: '',
  sidebarButtonBackgroundImageOpacity: 0.1,
  sidebarTextColor: '#ffffff',
  sidebarTextSize: '14px',
  sidebarTextAlign: 'left',
  sidebarFontFamily: 'Inter, system-ui, -apple-system, sans-serif',
  sidebarHeaderBackground: 'linear-gradient(135deg, #232526 0%, #232931 100%)',
  sidebarHeaderBackgroundImage: '',
  sidebarHeaderBackgroundImageOpacity: 1,
  sidebarHeaderNameColor: '#6dd5ed',
  sidebarHeaderDepartmentColor: '#b2bec3',
  sidebarHeaderAvatarOffsetX: 0,
  sidebarHeaderAvatarOffsetY: 0,
  sidebarUseHeaderBackgroundForButtons: false,

  // Стили ячеек таблиц и поле дедлайн
  tableCellBackground: 'rgba(44, 62, 80, 0.3)',
  tableCellTextColor: '#ffffff',
  tableCellHoverBackground: 'rgba(44, 62, 80, 0.5)',
  tableCellHoverTextColor: '#ffffff',
  deadlineFieldTextColor: '#e2e8f0',
  
  // Индивидуальные настройки для каждой модалки
  modals: {
    calendar: {
      background: '#1f2937',
      backgroundImage: '',
      backgroundImageOpacity: 1,
      useImageOnly: false, // Использовать только изображение, игнорировать цвет фона
      buttonBackground: '#3b82f6',
      buttonBackgroundImage: '',
      buttonBackgroundImageOpacity: 0.1,
      buttonTextColor: '#ffffff',
      buttonNormalBackground: 'rgba(67, 233, 123, 0.1)',
      iconButtonBackground: 'rgba(67, 233, 123, 0.15)',
      borderColor: 'rgba(67, 233, 123, 0.2)',
      inputBackground: '#2d3748',
      inputTextColor: '#ffffff',
      headerIconColor: '#43e97b',
      textColor: '#ffffff',
      textSize: '14px',
      textAlign: 'left',
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
      // Новые настройки
      filterBlockBackground: 'rgba(67, 233, 123, 0.08)',
      filterBlockBackgroundImage: '',
      navBlockBackground: 'rgba(67, 233, 123, 0.1)',
      navBlockBackgroundImage: '',
      calendarGridBackground: 'rgba(34, 40, 49, 0.97)',
      calendarGridBackgroundImage: '',
      calendarCellBackground: 'rgba(67, 233, 123, 0.05)',
      calendarCellTextColor: '#ffffff',
      calendarWeekdayColor: '#43e97b',
    },
    chats: {
      background: '#1f2937',
      backgroundImage: '',
      backgroundImageOpacity: 1,
      buttonBackground: '#3b82f6',
      buttonBackgroundImage: '',
      buttonBackgroundImageOpacity: 0.1,
      textColor: '#ffffff',
      textSize: '14px',
      textAlign: 'left',
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
    },
    leaves: {
      background: '#1f2937',
      backgroundImage: '',
      backgroundImageOpacity: 1,
      buttonBackground: '#3b82f6',
      buttonBackgroundImage: '',
      buttonBackgroundImageOpacity: 0.1,
      buttonTextColor: '#ffffff',
      buttonNormalBackground: 'rgba(33, 147, 176, 0.15)',
      iconButtonBackground: 'rgba(33, 147, 176, 0.2)',
      borderColor: 'rgba(33, 147, 176, 0.3)',
      inputBackground: 'rgba(255, 255, 255, 0.92)',
      inputTextColor: '#333333',
      headerBackground: 'rgba(35, 41, 49, 0.95)',
      headerIconColor: '#ffffff',
      titleColor: '#43e97b',
      formBackground: 'rgba(44, 62, 80, 0.22)',
      cardBackground: 'linear-gradient(135deg, rgba(255,255,255,0.93) 0%, rgba(33,147,176,0.13) 100%)',
      cardTextColor: '#2193b0',
      cardReasonColor: '#636e72',
      textColor: '#ffffff',
      textSize: '14px',
      textAlign: 'left',
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
    },
    tasks: {
      background: '#1f2937',
      backgroundImage: '',
      backgroundImageOpacity: 1,
      buttonBackground: '#3b82f6',
      buttonBackgroundImage: '',
      buttonBackgroundImageOpacity: 0.1,
      textColor: '#ffffff',
      textSize: '14px',
      textAlign: 'left',
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
    },
    'warehouse-orders': {
      background: 'linear-gradient(160deg, #f8fafc 0%, #f1f5f9 45%, #e8eef4 100%)',
      textColor: '#1e293b',
      headerTextColor: '#0f766e',
      buttonBackground: 'rgba(255, 255, 255, 0.92)',
      buttonTextColor: '#334155',
    },
    'warehouse-picking': {
      textColor: '#1e293b',
      headerTextColor: '#0f766e',
      productBlockTextColor: '#1e293b',
      productBlockBackground: 'rgba(255, 255, 255, 0.75)',
      productRowBackground: 'rgba(255, 255, 255, 0.95)',
      noStockButtonTextColor: '#b91c1c',
      counterTextColor: '#0f766e',
      counterButtonTextColor: '#ffffff',
      counterButtonBackground: '#0d7377',
      counterInputBackground: '#ffffff',
    },
    todo: {
      background: '#1f2937',
      backgroundImage: '',
      backgroundImageOpacity: 1,
      buttonBackground: '#3b82f6',
      buttonBackgroundImage: '',
      buttonBackgroundImageOpacity: 0.1,
      textColor: '#ffffff',
      textSize: '14px',
      textAlign: 'left',
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
    },
    news: {
      background: '#1f2937',
      backgroundImage: '',
      backgroundImageOpacity: 1,
      buttonBackground: '#3b82f6',
      buttonBackgroundImage: '',
      buttonBackgroundImageOpacity: 0.1,
      textColor: '#ffffff',
      textSize: '14px',
      textAlign: 'left',
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
    },
    employees: {
      background: '#1f2937',
      backgroundImage: '',
      backgroundImageOpacity: 1,
      buttonBackground: '#3b82f6',
      buttonBackgroundImage: '',
      buttonBackgroundImageOpacity: 0.1,
      textColor: '#ffffff',
      textSize: '14px',
      textAlign: 'left',
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
    },
    monitoring: {
      background: '#1f2937',
      backgroundImage: '',
      backgroundImageOpacity: 1,
      buttonBackground: '#3b82f6',
      buttonBackgroundImage: '',
      buttonBackgroundImageOpacity: 0.1,
      textColor: '#ffffff',
      textSize: '14px',
      textAlign: 'left',
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
    },
    'remote-report': {
      background: '#1f2937',
      backgroundImage: '',
      backgroundImageOpacity: 1,
      buttonBackground: '#3b82f6',
      buttonBackgroundImage: '',
      buttonBackgroundImageOpacity: 0.1,
      textColor: '#ffffff',
      textSize: '14px',
      textAlign: 'left',
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
      showReportButtonWidth: 100,
      showReportButtonHeight: 48,
      showReportButtonBackground: '#43e97b',
      showReportButtonTextColor: '#ffffff',
      reportCardBackground: 'linear-gradient(135deg, rgba(67, 233, 123, 0.05) 0%, rgba(33, 147, 176, 0.05) 100%)',
      reportCardBorderColor: 'rgba(67, 233, 123, 0.2)',
      reportTextColor: '#ffffff',
      reportNameColor: '#43e97b',
      reportDateColor: '#ffffff',
      reportDetailsButtonBackground: 'linear-gradient(135deg, #2193b0 0%, #43e97b 100%)',
      reportDetailsButtonBorderColor: 'rgba(33, 147, 176, 0.5)',
      reportDetailsButtonTextColor: '#ffffff',
    },
    'remote-report-details': {
      background: '#1f2937',
      backgroundImage: '',
      backgroundImageOpacity: 1,
      buttonBackground: '#3b82f6',
      buttonBackgroundImage: '',
      buttonBackgroundImageOpacity: 0.1,
      textColor: '#ffffff',
      textSize: '14px',
      textAlign: 'left',
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
      detailCardBackground: 'linear-gradient(135deg, rgba(67, 233, 123, 0.1) 0%, rgba(67, 233, 123, 0.05) 100%)',
      detailCardBorderColor: 'rgba(67, 233, 123, 0.3)',
      detailTextColor: '#ffffff',
      detailDateColor: '#ffffff',
    },
    'local-pc-activity': {
      background: '#1f2937',
      backgroundImage: '',
      backgroundImageOpacity: 1,
      buttonBackground: '#3b82f6',
      buttonBackgroundImage: '',
      buttonBackgroundImageOpacity: 0.1,
      textColor: '#ffffff',
      textSize: '14px',
      textAlign: 'left',
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
      showReportButtonBackground: '#27ae60',
      showReportButtonTextColor: '#ffffff',
      deletePeriodButtonBackground: '#e74c3c',
      deletePeriodButtonTextColor: '#ffffff',
      deleteDayButtonBackground: '#e74c3c',
      deleteDayButtonTextColor: '#ffffff',
      deleteWeekButtonBackground: '#e74c3c',
      deleteWeekButtonTextColor: '#ffffff',
      deleteMonthButtonBackground: '#e74c3c',
      deleteMonthButtonTextColor: '#ffffff',
    },
    'local-pc-activity-details': {
      background: '#1f2937',
      backgroundImage: '',
      backgroundImageOpacity: 1,
      buttonBackground: '#3b82f6',
      buttonBackgroundImage: '',
      buttonBackgroundImageOpacity: 0.1,
      textColor: '#ffffff',
      textSize: '14px',
      textAlign: 'left',
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
      tabBarBackground: '#1f2937',
      tabBackground: 'rgba(255, 255, 255, 0.1)',
      tabTextColor: '#ffffff',
      tabBorderColor: 'transparent',
      tabActiveBackground: '#ffffff',
      tabActiveTextColor: '#000000',
      tabActiveBorderColor: '#000000',
      blockBackground: 'rgba(255, 255, 255, 0.08)',
      blockTextColor: '#ffffff',
      blockBorderColor: 'rgba(0, 0, 0, 0.6)',
      blockHeaderTextColor: '#000000',
      statsCardBackground: '#27ae60',
      statsCardBorderColor: '#000000',
      statsCardTitleColor: '#000000',
      statsCardValueColor: '#ffffff',
      statsCardSubtextColor: 'rgba(0, 0, 0, 0.6)',
      topAppsBackground: '#3d3a3a',
      topAppsBorderColor: '#000000',
      topAppsTextColor: 'rgba(251, 198, 84, 0.94)',
      eventCardBackground: 'rgba(67, 233, 123, 0.1)',
      eventCardBorderColor: 'rgba(67, 233, 123, 0.3)',
      eventTitleColor: '#43e97b',
      eventTextColor: '#111111',
      urlCardBackground: '#93cbe5',
      urlCardBorderColor: '#000000',
      urlTitleColor: '#000000',
      urlTextColor: '#f3ecec',
      urlMetaTextColor: 'rgba(252, 254, 255, 0.99)',
      appCardBackground: 'rgba(225, 233, 225, 1)',
      appCardBorderColor: '#000000',
      appTitleColor: '#000000',
      appTextColor: 'rgba(0, 0, 0, 0.7)',
      appMetaTextColor: '#070000',
      periodBlockBackground: 'rgba(0, 0, 0, 0.05)',
      periodBlockBorderColor: '#000000',
      periodTitleColor: '#000000',
      periodTextColor: 'rgba(134, 3, 3, 0.7)',
      screenshotCardBackground: 'rgba(255, 224, 130, 0.44)',
      screenshotCardBorderColor: 'rgba(0, 0, 0, 0.3)',
      screenshotTextColor: 'rgba(0, 0, 0, 0.5)',
      screenshotDownloadButtonBackground: 'rgb(13, 232, 86)',
      screenshotDownloadButtonTextColor: '#ffffff',
      screenshotPrintButtonBackground: 'rgb(0, 0, 0)',
      screenshotPrintButtonTextColor: '#ffe082',
    },
    'app-usage-report': {
      background: '#1f2937',
      backgroundImage: '',
      backgroundImageOpacity: 1,
      buttonBackground: '#3b82f6',
      buttonBackgroundImage: '',
      buttonBackgroundImageOpacity: 0.1,
      textColor: '#ffffff',
      textSize: '14px',
      textAlign: 'left',
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
      tableTextColor: '#ffffff',
      tableHeaderTextColor: '#ffffff',
      dateInputBackground: '#0f172a',
      dateInputTextColor: '#ffffff',
      navButtonBackground: 'rgba(163, 230, 53, 0.15)',
      navButtonTextColor: '#a3e635',
      navButtonBorderColor: 'rgba(163, 230, 53, 0.3)',
      navButtonHoverBackground: 'rgba(163, 230, 53, 0.25)',
      navButtonHoverTextColor: '#a3e635',
      navButtonHoverBorderColor: '#a3e635',
      closeButtonWidth: 100,
      closeButtonHeight: 40,
      closeButtonBackground: '#a3ffb0',
      closeButtonTextColor: '#232931',
    },
    'app-usage-details': {
      background: '#1f2937',
      backgroundImage: '',
      backgroundImageOpacity: 1,
      buttonBackground: '#3b82f6',
      buttonBackgroundImage: '',
      buttonBackgroundImageOpacity: 0.1,
      textColor: '#ffffff',
      textSize: '14px',
      textAlign: 'left',
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
    },
    worktime: {
      background: '#1f2937',
      backgroundImage: '',
      backgroundImageOpacity: 1,
      buttonBackground: '#3b82f6',
      buttonBackgroundImage: '',
      buttonBackgroundImageOpacity: 0.1,
      textColor: '#ffffff',
      textSize: '14px',
      textAlign: 'left',
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
      titleColor: '#43e97b',
      closeButtonWidth: 100,
      closeButtonHeight: 48,
      closeButtonBackground: '#a3ffb0',
      closeButtonTextColor: '#232931',
      filterAfterHoursBackground: 'rgba(255, 193, 7, 0.1)',
      filterWeekdaysBackground: 'rgba(255, 193, 7, 0.05)',
      filterWeekendsBackground: 'rgba(67, 233, 123, 0.05)',
      filterPendingBackground: 'transparent',
      filterCompletedBackground: 'transparent',
      filterAfterHoursBorderColor: 'rgba(109, 213, 237, 0.3)',
      filterWeekdaysBorderColor: 'rgba(255, 193, 7, 0.3)',
      filterWeekendsBorderColor: 'rgba(67, 233, 123, 0.3)',
      filterPendingBorderColor: '#4a5568',
      filterCompletedBorderColor: '#4a5568',
      filterAfterHoursTextColor: '#a0aec0',
      filterWeekdaysTextColor: '#a0aec0',
      filterWeekendsTextColor: '#a0aec0',
      filterPendingTextColor: '#a0aec0',
      filterCompletedTextColor: '#a0aec0',
    },
    management: {
      background: '#1f2937',
      backgroundImage: '',
      backgroundImageOpacity: 1,
      buttonBackground: '#3b82f6',
      buttonBackgroundImage: '',
      buttonBackgroundImageOpacity: 0.1,
      textColor: '#ffffff',
      textSize: '14px',
      textAlign: 'left',
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
    },
    vpr: {
      background: 'linear-gradient(135deg, #e8eef2 0%, #b0d4dc 100%)',
      backgroundImage: '',
      backgroundImageOpacity: 1,
      headerColor: '#2193b0',
      textColor: '#1a5f6e',
      fileLabelColor: '#1a5f6e',
      fileDescriptionColor: '#1a5f6e',
      buttonBackground: 'rgba(255,255,255,0.8)',
      buttonBorderColor: 'rgba(33, 147, 176, 0.5)',
      buttonTextColor: '#2193b0',
      dateLabelColor: '#1a5f6e',
      dateFieldBackground: 'rgba(255,255,255,0.9)',
      dateFieldColor: '#1a5f6e',
      dateFieldBorderColor: 'rgba(33, 147, 176, 0.5)',
      findDiscrepanciesButtonBackground: 'rgba(33, 147, 176, 0.25)',
      findDiscrepanciesButtonTextColor: '#2193b0',
      findDiscrepanciesButtonBorderColor: '#2193b0',
    },
    'vpr-discrepancies': {
      tabBackground: 'rgba(255,255,255,0.6)',
      tabTextColor: '#2193b0',
      tabBorderColor: 'rgba(33, 147, 176, 0.5)',
      tabHoverBackground: 'rgba(255,255,255,0.98)',
      tabHoverBorderColor: 'rgba(33, 147, 176, 0.9)',
      tabHoverTextColor: '#2193b0',
      tabActiveBackground: 'rgba(255,255,255,0.95)',
      tabActiveTextColor: '#2193b0',
      tabActiveBorderColor: 'rgba(33, 147, 176, 0.5)',
      headerColor: '#2193b0',
      textColor: '#1a5f6e',
      buttonBackground: 'rgba(255,255,255,0.9)',
      buttonTextColor: '#2193b0',
      buttonBorderColor: 'rgba(33, 147, 176, 0.5)',
      resultsBackground: 'rgba(255,255,255,0.7)',
      resultsTextColor: '#1a5f6e',
      resultsBorderColor: 'rgba(33, 147, 176, 0.35)',
      summaryLabelColor: '#1a5f6e',
      summaryValueColor: '#2193b0',
      summaryDateColor: '#1a5f6e',
      downloadButtonBackground: 'rgba(33, 147, 176, 0.2)',
      downloadButtonTextColor: '#2193b0',
      downloadButtonBorderColor: '#2193b0',
      telegramButtonBackground: 'rgba(45, 125, 74, 0.25)',
      telegramButtonTextColor: '#1a5f2e',
      telegramButtonBorderColor: '#2d7d4a',
      assignTaskButtonBackground: 'rgba(124, 58, 237, 0.2)',
      assignTaskButtonTextColor: '#5b21b6',
      assignTaskButtonBorderColor: '#7c3aed',
    },
  }
};

// Функция для преобразования rgba/rgb в hex
const rgbaToHex = (color) => {
  if (!color) return '#000000';
  
  // Если уже hex формат
  if (color.startsWith('#')) {
    return color;
  }
  
  // Если rgba или rgb с запятыми: rgb(35, 40, 47) или rgba(35, 40, 47, 0.5)
  const rgbaMatchWithCommas = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
  if (rgbaMatchWithCommas) {
    const r = parseInt(rgbaMatchWithCommas[1]);
    const g = parseInt(rgbaMatchWithCommas[2]);
    const b = parseInt(rgbaMatchWithCommas[3]);
    const hex = ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
    return `#${hex}`;
  }
  
  // Если rgb без запятых: rgb(35 40 47) или rgba(35 40 47 / 0.5)
  const rgbaMatchWithoutCommas = color.match(/rgba?\((\d+)\s+(\d+)\s+(\d+)(?:\s*\/\s*([\d.]+))?\)/);
  if (rgbaMatchWithoutCommas) {
    const r = parseInt(rgbaMatchWithoutCommas[1]);
    const g = parseInt(rgbaMatchWithoutCommas[2]);
    const b = parseInt(rgbaMatchWithoutCommas[3]);
    const hex = ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
    return `#${hex}`;
  }
  
  // Если не удалось распознать, возвращаем дефолт
  console.warn('⚠️ [rgbaToHex] Не удалось распознать формат цвета:', color);
  return '#000000';
};

// Компонент для настроек индивидуальной модалки
const ModalSettingsSection = ({ modalId, modalName, modalIcon: Icon, settings, onSettingsChange }) => {
  const modalDefaults = DEFAULT_SETTINGS.modals[modalId] || {};
  const modalSettings = {
    ...modalDefaults,
    ...(settings.modals?.[modalId] || {})
  };
  const hideDetailExtras = modalId === 'local-pc-activity-details';

  const handleBackgroundImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const base64 = await loadImageAsBase64(file);
      const updatedModals = {
        ...settings.modals,
        [modalId]: {
          ...modalSettings,
          backgroundImage: base64,
          // Автоматически включаем "Только изображение" при загрузке нового изображения
          useImageOnly: true
        }
      };
      onSettingsChange({ ...settings, modals: updatedModals });
      console.log('✅ [FrontendSettingsModal] Изображение загружено, useImageOnly установлен в true');
    } catch (error) {
      console.error('Ошибка загрузки изображения:', error);
      alert(`❌ ${error.message}`);
    }
  };

  const handleButtonImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const base64 = await loadImageAsBase64(file);
      const updatedModals = {
        ...settings.modals,
        [modalId]: {
          ...modalSettings,
          buttonBackgroundImage: base64
        }
      };
      onSettingsChange({ ...settings, modals: updatedModals });
    } catch (error) {
      console.error('Ошибка загрузки изображения кнопки:', error);
      alert(`❌ ${error.message}`);
    }
  };

  const updateModalSetting = (key, value) => {
    const updatedModals = {
      ...settings.modals,
      [modalId]: {
        ...modalSettings,
        [key]: value
      }
    };
    onSettingsChange({ ...settings, modals: updatedModals });
  };
  const updateModalSettings = (patch) => {
    const updatedModals = {
      ...settings.modals,
      [modalId]: {
        ...modalSettings,
        ...patch
      }
    };
    onSettingsChange({ ...settings, modals: updatedModals });
  };

  return (
    <>
      {/* Левая колонка - настройки */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '24px' }}>
        
        {/* Фон модалки */}
        <div style={{ 
          background: '#111827', 
          padding: '20px', 
          borderRadius: '12px',
          border: '1px solid rgba(75, 85, 99, 0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <FiImage size={18} color="#60a5fa" />
            <h3 style={{ margin: 0, fontSize: '1.1em', fontWeight: 600, color: '#60a5fa' }}>
              Фон модалки "{modalName}"
            </h3>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                Цвет фона
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.background)}
                  onChange={(e) => updateModalSetting('background', e.target.value)}
                  disabled={modalSettings.useImageOnly && !!modalSettings.backgroundImage}
                  style={{
                    flex: 1,
                    height: '50px',
                    border: '1px solid rgba(75, 85, 99, 0.5)',
                    borderRadius: '8px',
                    cursor: (modalSettings.useImageOnly && !!modalSettings.backgroundImage) ? 'not-allowed' : 'pointer',
                    backgroundColor: modalSettings.background,
                    opacity: (modalSettings.useImageOnly && !!modalSettings.backgroundImage) ? 0.5 : 1
                  }}
                />
                {modalSettings.backgroundImage && (
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#d1d5db',
                    whiteSpace: 'nowrap'
                  }}>
                    <input
                      type="checkbox"
                      checked={modalSettings.useImageOnly || false}
                      onChange={(e) => updateModalSetting('useImageOnly', e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>Только изображение</span>
                  </label>
                )}
              </div>
              
              {/* Набор градиентов светлых и темных тонов */}
              <div style={{ marginTop: '12px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 500, color: '#9ca3af' }}>
                  Готовые градиенты (светлые + темные тона)
                </label>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(5, 1fr)', 
                  gap: '8px',
                  marginBottom: '8px'
                }}>
                  {[
                    'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', // Небесно-голубой
                    'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', // Светло-желтый
                    'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)', // Розовый
                    'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)', // Лавандовый
                    'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)', // Мятный
                    'linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%)', // Персиковый
                    'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', // Светло-зеленый
                    'linear-gradient(135deg, #fefce8 0%, #fef9c3 100%)', // Лимонный
                    'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)', // Фиолетовый
                    'linear-gradient(135deg, #ecfeff 0%, #cffafe 100%)', // Бирюзовый
                    // Темные
                    'linear-gradient(135deg, #0f172a 0%, #1f2937 100%)', // Сланцевый
                    'linear-gradient(135deg, #111827 0%, #0f172a 100%)', // Графит
                    'linear-gradient(135deg, #1f2937 0%, #111827 100%)', // Ночной
                    'linear-gradient(135deg, #0b1020 0%, #111827 100%)', // Темный индиго
                    'linear-gradient(135deg, #1c1917 0%, #0f172a 100%)', // Угольный
                    'linear-gradient(135deg, #0f172a 0%, #0b132b 100%)', // Темный океан
                    'linear-gradient(135deg, #111827 0%, #1f2937 100%)', // Стальной
                    'linear-gradient(135deg, #0b1320 0%, #1e293b 100%)', // Полночь
                    'linear-gradient(135deg, #1a1b1f 0%, #0f172a 100%)', // Черный шелк
                    'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)'  // Темный фиолетовый
                  ].map((gradient, index) => {
                    const isSelected = modalSettings.background === gradient || 
                      (modalSettings.background.includes('gradient') && modalSettings.background === gradient);
                    return (
                      <div
                        key={index}
                        onClick={() => {
                          if (!modalSettings.useImageOnly || !modalSettings.backgroundImage) {
                            updateModalSettings({
                              background: gradient,
                              backgroundImage: '',
                              useImageOnly: false
                            });
                          }
                        }}
                        style={{
                          height: '40px',
                          background: gradient,
                          borderRadius: '6px',
                          cursor: (modalSettings.useImageOnly && !!modalSettings.backgroundImage) ? 'not-allowed' : 'pointer',
                          border: isSelected ? '3px solid #60a5fa' : '2px solid rgba(75, 85, 99, 0.3)',
                          opacity: (modalSettings.useImageOnly && !!modalSettings.backgroundImage) ? 0.5 : 1,
                          transition: 'all 0.2s ease',
                          boxShadow: isSelected ? '0 0 0 2px rgba(96, 165, 250, 0.3)' : 'none'
                        }}
                        title={`Градиент ${index + 1}`}
                      />
                    );
                  })}
                </div>
              </div>

              {/* Набор градиентов цветных тонов */}
              <div style={{ marginTop: '12px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 500, color: '#9ca3af' }}>
                  Готовые градиенты (цветные тона)
                </label>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(5, 1fr)',
                  gap: '8px',
                  marginBottom: '8px'
                }}>
                  {[
                    'linear-gradient(135deg, #ff6b6b 0%, #f9a8d4 100%)', // Коралловый
                    'linear-gradient(135deg, #f97316 0%, #facc15 100%)', // Солнечный
                    'linear-gradient(135deg, #22c55e 0%, #14b8a6 100%)', // Тропический
                    'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)', // Океан
                    'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)', // Неон
                    'linear-gradient(135deg, #ec4899 0%, #f97316 100%)', // Закат
                    'linear-gradient(135deg, #10b981 0%, #84cc16 100%)', // Салат
                    'linear-gradient(135deg, #0ea5e9 0%, #22d3ee 100%)', // Голубая волна
                    'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)', // Огненный
                    'linear-gradient(135deg, #8b5cf6 0%, #06b6d4 100%)'  // Радуга
                  ].map((gradient, index) => {
                    const isSelected = modalSettings.background === gradient ||
                      (modalSettings.background.includes('gradient') && modalSettings.background === gradient);
                    return (
                      <div
                        key={index}
                        onClick={() => {
                          if (!modalSettings.useImageOnly || !modalSettings.backgroundImage) {
                            updateModalSettings({
                              background: gradient,
                              backgroundImage: '',
                              useImageOnly: false
                            });
                          }
                        }}
                        style={{
                          height: '40px',
                          background: gradient,
                          borderRadius: '6px',
                          cursor: (modalSettings.useImageOnly && !!modalSettings.backgroundImage) ? 'not-allowed' : 'pointer',
                          border: isSelected ? '3px solid #60a5fa' : '2px solid rgba(75, 85, 99, 0.3)',
                          opacity: (modalSettings.useImageOnly && !!modalSettings.backgroundImage) ? 0.5 : 1,
                          transition: 'all 0.2s ease',
                          boxShadow: isSelected ? '0 0 0 2px rgba(96, 165, 250, 0.3)' : 'none'
                        }}
                        title={`Градиент ${index + 1}`}
                      />
                    );
                  })}
                </div>
              </div>
              
              {modalSettings.useImageOnly && modalSettings.backgroundImage && (
                <div style={{ 
                  marginTop: '8px', 
                  padding: '8px 12px', 
                  background: 'rgba(59, 130, 246, 0.1)', 
                  border: '1px solid rgba(59, 130, 246, 0.3)', 
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: '#60a5fa'
                }}>
                  ⓘ Цвет фона отключен, используется только изображение
                </div>
              )}
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                Изображение фона
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleBackgroundImageUpload}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid rgba(75, 85, 99, 0.5)',
                  borderRadius: '8px',
                  backgroundColor: '#1f2937',
                  color: '#d1d5db',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              />
              {modalSettings.backgroundImage && (
                <button
                  onClick={() => updateModalSetting('backgroundImage', '')}
                  style={{
                    marginTop: '8px',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid rgba(239, 68, 68, 0.5)',
                    background: 'rgba(239, 68, 68, 0.1)',
                    color: '#ef4444',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 500
                  }}
                >
                  Удалить изображение
                </button>
              )}
            </div>

            {modalSettings.backgroundImage && (
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Прозрачность изображения: {Math.round(modalSettings.backgroundImageOpacity * 100)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={modalSettings.backgroundImageOpacity}
                  onChange={(e) => updateModalSetting('backgroundImageOpacity', parseFloat(e.target.value))}
                  style={{
                    width: '100%',
                    cursor: 'pointer'
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {!hideDetailExtras && (
          <div style={{ 
            background: '#111827', 
            padding: '20px', 
            borderRadius: '12px',
            border: '1px solid rgba(75, 85, 99, 0.3)'
          }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <FiImage size={18} color="#60a5fa" />
            <h3 style={{ margin: 0, fontSize: '1.1em', fontWeight: 600, color: '#60a5fa' }}>
              Стили кнопок в "{modalName}"
            </h3>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                Цвет фона кнопок
              </label>
              <input
                type="color"
                value={modalSettings.buttonBackground}
                onChange={(e) => updateModalSetting('buttonBackground', e.target.value)}
                style={{
                  width: '100%',
                  height: '50px',
                  border: '1px solid rgba(75, 85, 99, 0.5)',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                Изображение фона кнопок
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleButtonImageUpload}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid rgba(75, 85, 99, 0.5)',
                  borderRadius: '8px',
                  backgroundColor: '#1f2937',
                  color: '#d1d5db',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              />
              {modalSettings.buttonBackgroundImage && (
                <button
                  onClick={() => updateModalSetting('buttonBackgroundImage', '')}
                  style={{
                    marginTop: '8px',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid rgba(239, 68, 68, 0.5)',
                    background: 'rgba(239, 68, 68, 0.1)',
                    color: '#ef4444',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 500
                  }}
                >
                  Удалить изображение
                </button>
              )}
            </div>

            {modalSettings.buttonBackgroundImage && (
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Прозрачность изображения: {Math.round(modalSettings.buttonBackgroundImageOpacity * 100)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={modalSettings.buttonBackgroundImageOpacity}
                  onChange={(e) => updateModalSetting('buttonBackgroundImageOpacity', parseFloat(e.target.value))}
                  style={{
                    width: '100%',
                    cursor: 'pointer'
                  }}
                />
              </div>
            )}

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                Цвет текста кнопок
              </label>
              <input
                type="color"
                value={modalSettings.buttonTextColor || '#ffffff'}
                onChange={(e) => updateModalSetting('buttonTextColor', e.target.value)}
                style={{
                  width: '100%',
                  height: '50px',
                  border: '1px solid rgba(75, 85, 99, 0.5)',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                Фон кнопок (обычное состояние)
              </label>
              <input
                type="color"
                value={rgbaToHex(modalSettings.buttonNormalBackground || '#1a5c3a')}
                onChange={(e) => updateModalSetting('buttonNormalBackground', e.target.value)}
                style={{
                  width: '100%',
                  height: '50px',
                  border: '1px solid rgba(75, 85, 99, 0.5)',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                Фон иконок-кнопок (стрелка назад, крестик, стрелки календаря)
              </label>
              <input
                type="color"
                value={rgbaToHex(modalSettings.iconButtonBackground || '#1a5c3a')}
                onChange={(e) => updateModalSetting('iconButtonBackground', e.target.value)}
                style={{
                  width: '100%',
                  height: '50px',
                  border: '1px solid rgba(75, 85, 99, 0.5)',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              />
            </div>
          </div>
          </div>
        )}

        {!hideDetailExtras && (
          <div style={{ 
            background: '#111827', 
            padding: '20px', 
            borderRadius: '12px',
            border: '1px solid rgba(75, 85, 99, 0.3)'
          }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <FiType size={18} color="#a78bfa" />
            <h3 style={{ margin: 0, fontSize: '1.1em', fontWeight: 600, color: '#a78bfa' }}>
              Стили полей ввода в "{modalName}"
            </h3>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                Фон полей ввода
              </label>
              <input
                type="color"
                value={modalSettings.inputBackground || '#2d3748'}
                onChange={(e) => updateModalSetting('inputBackground', e.target.value)}
                style={{
                  width: '100%',
                  height: '50px',
                  border: '1px solid rgba(75, 85, 99, 0.5)',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                Цвет рамок полей
              </label>
              <input
                type="color"
                value={rgbaToHex(modalSettings.borderColor || '#43e97b')}
                onChange={(e) => updateModalSetting('borderColor', e.target.value)}
                style={{
                  width: '100%',
                  height: '50px',
                  border: '1px solid rgba(75, 85, 99, 0.5)',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                Цвет текста полей
              </label>
              <input
                type="color"
                value={modalSettings.inputTextColor || '#ffffff'}
                onChange={(e) => updateModalSetting('inputTextColor', e.target.value)}
                style={{
                  width: '100%',
                  height: '50px',
                  border: '1px solid rgba(75, 85, 99, 0.5)',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              />
            </div>
          </div>
          </div>
        )}

        {!hideDetailExtras && (
          <div style={{ 
            background: '#111827', 
            padding: '20px', 
            borderRadius: '12px',
            border: '1px solid rgba(75, 85, 99, 0.3)'
          }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <FiX size={18} color="#f472b6" />
            <h3 style={{ margin: 0, fontSize: '1.1em', fontWeight: 600, color: '#f472b6' }}>
              Стили иконок заголовка в "{modalName}"
            </h3>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                Цвет стрелки назад и крестика
              </label>
              <input
                type="color"
                value={modalSettings.headerIconColor || '#43e97b'}
                onChange={(e) => updateModalSetting('headerIconColor', e.target.value)}
                style={{
                  width: '100%',
                  height: '50px',
                  border: '1px solid rgba(75, 85, 99, 0.5)',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              />
            </div>
          </div>
          </div>
        )}

        {/* Стили вкладок и блоков (для модалок с табами) */}
        {Object.prototype.hasOwnProperty.call(modalDefaults, 'tabBarBackground') && (
          <div style={{ 
            background: '#111827', 
            padding: '20px', 
            borderRadius: '12px',
            border: '1px solid rgba(75, 85, 99, 0.3)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <FiLayout size={18} color="#22d3ee" />
              <h3 style={{ margin: 0, fontSize: '1.1em', fontWeight: 600, color: '#22d3ee' }}>
                Стили вкладок и блоков
              </h3>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Фон панели вкладок
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.tabBarBackground || '#1f2937')}
                  onChange={(e) => updateModalSetting('tabBarBackground', e.target.value)}
                  style={{ width: '100%', height: '50px', border: '1px solid rgba(75, 85, 99, 0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Вкладка (фон)
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.tabBackground || '#ffffff')}
                  onChange={(e) => updateModalSetting('tabBackground', e.target.value)}
                  style={{ width: '100%', height: '50px', border: '1px solid rgba(75, 85, 99, 0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Вкладка (текст)
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.tabTextColor || '#ffffff')}
                  onChange={(e) => updateModalSetting('tabTextColor', e.target.value)}
                  style={{ width: '100%', height: '50px', border: '1px solid rgba(75, 85, 99, 0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Вкладка (обводка)
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.tabBorderColor || '#000000')}
                  onChange={(e) => updateModalSetting('tabBorderColor', e.target.value)}
                  style={{ width: '100%', height: '50px', border: '1px solid rgba(75, 85, 99, 0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Активная вкладка (фон)
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.tabActiveBackground || '#ffffff')}
                  onChange={(e) => updateModalSetting('tabActiveBackground', e.target.value)}
                  style={{ width: '100%', height: '50px', border: '1px solid rgba(75, 85, 99, 0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Активная вкладка (текст)
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.tabActiveTextColor || '#000000')}
                  onChange={(e) => updateModalSetting('tabActiveTextColor', e.target.value)}
                  style={{ width: '100%', height: '50px', border: '1px solid rgba(75, 85, 99, 0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Активная вкладка (обводка)
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.tabActiveBorderColor || '#000000')}
                  onChange={(e) => updateModalSetting('tabActiveBorderColor', e.target.value)}
                  style={{ width: '100%', height: '50px', border: '1px solid rgba(75, 85, 99, 0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Блоки (фон)
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.blockBackground || '#1f2937')}
                  onChange={(e) => updateModalSetting('blockBackground', e.target.value)}
                  style={{ width: '100%', height: '50px', border: '1px solid rgba(75, 85, 99, 0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Блоки (текст)
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.blockTextColor || '#ffffff')}
                  onChange={(e) => updateModalSetting('blockTextColor', e.target.value)}
                  style={{ width: '100%', height: '50px', border: '1px solid rgba(75, 85, 99, 0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Блоки (обводка)
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.blockBorderColor || '#000000')}
                  onChange={(e) => updateModalSetting('blockBorderColor', e.target.value)}
                  style={{ width: '100%', height: '50px', border: '1px solid rgba(75, 85, 99, 0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Заголовки в блоках
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.blockHeaderTextColor || '#000000')}
                  onChange={(e) => updateModalSetting('blockHeaderTextColor', e.target.value)}
                  style={{ width: '100%', height: '50px', border: '1px solid rgba(75, 85, 99, 0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
              </div>
            </div>
          </div>
        )}

        {modalId === 'worktime' && (
          <div style={{ 
            background: '#111827', 
            padding: '20px', 
            borderRadius: '12px',
            border: '1px solid rgba(75, 85, 99, 0.3)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <FiType size={18} color="#22c55e" />
              <h3 style={{ margin: 0, fontSize: '1.1em', fontWeight: 600, color: '#22c55e' }}>
                Заголовок модалки
              </h3>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                Цвет заголовка
              </label>
              <span style={{ display: 'block', marginBottom: '6px', fontSize: '11px', color: '#9ca3af' }}>
                Если не задан — используется глобальный «Цвет заголовков» из вкладки Основная
              </span>
              <input
                type="color"
                value={rgbaToHex(modalSettings.titleColor ?? settings.modalHeaderTextColor ?? '#43e97b')}
                onChange={(e) => updateModalSetting('titleColor', e.target.value)}
                style={{
                  width: '100%',
                  height: '50px',
                  border: '1px solid rgba(75, 85, 99, 0.5)',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              />
            </div>
          </div>
        )}

        {Object.prototype.hasOwnProperty.call(modalDefaults, 'closeButtonWidth') && (
          <div style={{ 
            background: '#111827', 
            padding: '20px', 
            borderRadius: '12px',
            border: '1px solid rgba(75, 85, 99, 0.3)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <FiCheckSquare size={18} color="#f97316" />
              <h3 style={{ margin: 0, fontSize: '1.1em', fontWeight: 600, color: '#f97316' }}>
                Кнопка "Закрыть"
              </h3>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Ширина ({modalSettings.closeButtonWidth || 100}%)
                </label>
                <input
                  type="range"
                  min="10"
                  max="100"
                  step="1"
                  value={modalSettings.closeButtonWidth || 100}
                  onChange={(e) => updateModalSetting('closeButtonWidth', parseInt(e.target.value, 10))}
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Высота ({modalSettings.closeButtonHeight || 48}px)
                </label>
                <input
                  type="range"
                  min="28"
                  max="72"
                  step="1"
                  value={modalSettings.closeButtonHeight || 48}
                  onChange={(e) => updateModalSetting('closeButtonHeight', parseInt(e.target.value, 10))}
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Цвет фона
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.closeButtonBackground || '#a3ffb0')}
                  onChange={(e) => updateModalSetting('closeButtonBackground', e.target.value)}
                  style={{
                    width: '100%',
                    height: '50px',
                    border: '1px solid rgba(75, 85, 99, 0.5)',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Цвет текста
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.closeButtonTextColor || '#232931')}
                  onChange={(e) => updateModalSetting('closeButtonTextColor', e.target.value)}
                  style={{
                    width: '100%',
                    height: '50px',
                    border: '1px solid rgba(75, 85, 99, 0.5)',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {modalId === 'worktime' && (
          <div style={{ 
            background: '#111827', 
            padding: '20px', 
            borderRadius: '12px',
            border: '1px solid rgba(75, 85, 99, 0.3)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <FiFilter size={18} color="#60a5fa" />
              <h3 style={{ margin: 0, fontSize: '1.1em', fontWeight: 600, color: '#60a5fa' }}>
                Фон фильтров
              </h3>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Отработка в нерабочее время
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.filterAfterHoursBackground || 'rgba(109, 213, 237, 0.05)')}
                  onChange={(e) => updateModalSetting('filterAfterHoursBackground', e.target.value)}
                  style={{ width: '100%', height: '50px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
                <label style={{ display: 'block', margin: '8px 0 8px', fontSize: '12px', color: '#9ca3af' }}>
                  Обводка
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.filterAfterHoursBorderColor || 'rgba(109, 213, 237, 0.3)')}
                  onChange={(e) => updateModalSetting('filterAfterHoursBorderColor', e.target.value)}
                  style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
                <label style={{ display: 'block', margin: '8px 0 8px', fontSize: '12px', color: '#9ca3af' }}>
                  Цвет текста
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.filterAfterHoursTextColor || '#a0aec0')}
                  onChange={(e) => updateModalSetting('filterAfterHoursTextColor', e.target.value)}
                  style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Отработка по будням
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.filterWeekdaysBackground || 'rgba(255, 193, 7, 0.05)')}
                  onChange={(e) => updateModalSetting('filterWeekdaysBackground', e.target.value)}
                  style={{ width: '100%', height: '50px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
                <label style={{ display: 'block', margin: '8px 0 8px', fontSize: '12px', color: '#9ca3af' }}>
                  Обводка
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.filterWeekdaysBorderColor || 'rgba(255, 193, 7, 0.3)')}
                  onChange={(e) => updateModalSetting('filterWeekdaysBorderColor', e.target.value)}
                  style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
                <label style={{ display: 'block', margin: '8px 0 8px', fontSize: '12px', color: '#9ca3af' }}>
                  Цвет текста
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.filterWeekdaysTextColor || '#a0aec0')}
                  onChange={(e) => updateModalSetting('filterWeekdaysTextColor', e.target.value)}
                  style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Отработка по выходным
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.filterWeekendsBackground || 'rgba(67, 233, 123, 0.05)')}
                  onChange={(e) => updateModalSetting('filterWeekendsBackground', e.target.value)}
                  style={{ width: '100%', height: '50px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
                <label style={{ display: 'block', margin: '8px 0 8px', fontSize: '12px', color: '#9ca3af' }}>
                  Обводка
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.filterWeekendsBorderColor || 'rgba(67, 233, 123, 0.3)')}
                  onChange={(e) => updateModalSetting('filterWeekendsBorderColor', e.target.value)}
                  style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
                <label style={{ display: 'block', margin: '8px 0 8px', fontSize: '12px', color: '#9ca3af' }}>
                  Цвет текста
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.filterWeekendsTextColor || '#a0aec0')}
                  onChange={(e) => updateModalSetting('filterWeekendsTextColor', e.target.value)}
                  style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  На очереди
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.filterPendingBackground || 'transparent')}
                  onChange={(e) => updateModalSetting('filterPendingBackground', e.target.value)}
                  style={{ width: '100%', height: '50px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
                <label style={{ display: 'block', margin: '8px 0 8px', fontSize: '12px', color: '#9ca3af' }}>
                  Обводка
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.filterPendingBorderColor || '#4a5568')}
                  onChange={(e) => updateModalSetting('filterPendingBorderColor', e.target.value)}
                  style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
                <label style={{ display: 'block', margin: '8px 0 8px', fontSize: '12px', color: '#9ca3af' }}>
                  Цвет текста
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.filterPendingTextColor || '#a0aec0')}
                  onChange={(e) => updateModalSetting('filterPendingTextColor', e.target.value)}
                  style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Отработанные
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.filterCompletedBackground || 'transparent')}
                  onChange={(e) => updateModalSetting('filterCompletedBackground', e.target.value)}
                  style={{ width: '100%', height: '50px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
                <label style={{ display: 'block', margin: '8px 0 8px', fontSize: '12px', color: '#9ca3af' }}>
                  Обводка
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.filterCompletedBorderColor || '#4a5568')}
                  onChange={(e) => updateModalSetting('filterCompletedBorderColor', e.target.value)}
                  style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
                <label style={{ display: 'block', margin: '8px 0 8px', fontSize: '12px', color: '#9ca3af' }}>
                  Цвет текста
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.filterCompletedTextColor || '#a0aec0')}
                  onChange={(e) => updateModalSetting('filterCompletedTextColor', e.target.value)}
                  style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
              </div>
            </div>
          </div>
        )}

        {modalId === 'local-pc-activity-details' && (
          <div style={{ 
            background: '#111827', 
            padding: '20px', 
            borderRadius: '12px',
            border: '1px solid rgba(75, 85, 99, 0.3)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <FiLayout size={18} color="#f59e0b" />
              <h3 style={{ margin: 0, fontSize: '1.1em', fontWeight: 600, color: '#f59e0b' }}>
                Стили блоков "Подробнее" (локальные ПК)
              </h3>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#d1d5db', marginBottom: '8px' }}>
                  Статистика за день (карточки)
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#9ca3af' }}>Фон</label>
                    <input type="color" value={rgbaToHex(modalSettings.statsCardBackground || '#27ae60')} onChange={(e) => updateModalSetting('statsCardBackground', e.target.value)} style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#9ca3af' }}>Обводка</label>
                    <input type="color" value={rgbaToHex(modalSettings.statsCardBorderColor || '#000000')} onChange={(e) => updateModalSetting('statsCardBorderColor', e.target.value)} style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#9ca3af' }}>Цвет заголовка</label>
                    <input type="color" value={rgbaToHex(modalSettings.statsCardTitleColor || '#000000')} onChange={(e) => updateModalSetting('statsCardTitleColor', e.target.value)} style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#9ca3af' }}>Цвет значения</label>
                    <input type="color" value={rgbaToHex(modalSettings.statsCardValueColor || '#ffffff')} onChange={(e) => updateModalSetting('statsCardValueColor', e.target.value)} style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#9ca3af' }}>Цвет подписи</label>
                    <input type="color" value={rgbaToHex(modalSettings.statsCardSubtextColor || 'rgba(0, 0, 0, 0.6)')} onChange={(e) => updateModalSetting('statsCardSubtextColor', e.target.value)} style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }} />
                  </div>
                </div>
              </div>

              <div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#d1d5db', marginBottom: '8px' }}>
                  Топ приложений (в статистике)
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#9ca3af' }}>Фон</label>
                    <input type="color" value={rgbaToHex(modalSettings.topAppsBackground || '#3d3a3a')} onChange={(e) => updateModalSetting('topAppsBackground', e.target.value)} style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#9ca3af' }}>Обводка</label>
                    <input type="color" value={rgbaToHex(modalSettings.topAppsBorderColor || '#000000')} onChange={(e) => updateModalSetting('topAppsBorderColor', e.target.value)} style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#9ca3af' }}>Цвет текста</label>
                    <input type="color" value={rgbaToHex(modalSettings.topAppsTextColor || 'rgba(251, 198, 84, 0.94)')} onChange={(e) => updateModalSetting('topAppsTextColor', e.target.value)} style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }} />
                  </div>
                </div>
              </div>

              <div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#d1d5db', marginBottom: '8px' }}>
                  Вход / Выход
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#9ca3af' }}>Фон</label>
                    <input type="color" value={rgbaToHex(modalSettings.eventCardBackground || 'rgba(67, 233, 123, 0.1)')} onChange={(e) => updateModalSetting('eventCardBackground', e.target.value)} style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#9ca3af' }}>Обводка</label>
                    <input type="color" value={rgbaToHex(modalSettings.eventCardBorderColor || 'rgba(67, 233, 123, 0.3)')} onChange={(e) => updateModalSetting('eventCardBorderColor', e.target.value)} style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#9ca3af' }}>Цвет заголовка</label>
                    <input type="color" value={rgbaToHex(modalSettings.eventTitleColor || '#43e97b')} onChange={(e) => updateModalSetting('eventTitleColor', e.target.value)} style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#9ca3af' }}>Цвет текста</label>
                    <input type="color" value={rgbaToHex(modalSettings.eventTextColor || '#111111')} onChange={(e) => updateModalSetting('eventTextColor', e.target.value)} style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }} />
                  </div>
                </div>
              </div>

              <div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#d1d5db', marginBottom: '8px' }}>
                  Сайты (карточки)
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#9ca3af' }}>Фон</label>
                    <input type="color" value={rgbaToHex(modalSettings.urlCardBackground || '#93cbe5')} onChange={(e) => updateModalSetting('urlCardBackground', e.target.value)} style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#9ca3af' }}>Обводка</label>
                    <input type="color" value={rgbaToHex(modalSettings.urlCardBorderColor || '#000000')} onChange={(e) => updateModalSetting('urlCardBorderColor', e.target.value)} style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#9ca3af' }}>Цвет заголовка</label>
                    <input type="color" value={rgbaToHex(modalSettings.urlTitleColor || '#000000')} onChange={(e) => updateModalSetting('urlTitleColor', e.target.value)} style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#9ca3af' }}>Цвет текста</label>
                    <input type="color" value={rgbaToHex(modalSettings.urlTextColor || '#f3ecec')} onChange={(e) => updateModalSetting('urlTextColor', e.target.value)} style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#9ca3af' }}>Цвет времени</label>
                    <input type="color" value={rgbaToHex(modalSettings.urlMetaTextColor || 'rgba(252, 254, 255, 0.99)')} onChange={(e) => updateModalSetting('urlMetaTextColor', e.target.value)} style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }} />
                  </div>
                </div>
              </div>

              <div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#d1d5db', marginBottom: '8px' }}>
                  Приложения (карточки)
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#9ca3af' }}>Фон</label>
                    <input type="color" value={rgbaToHex(modalSettings.appCardBackground || 'rgba(225, 233, 225, 1)')} onChange={(e) => updateModalSetting('appCardBackground', e.target.value)} style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#9ca3af' }}>Обводка</label>
                    <input type="color" value={rgbaToHex(modalSettings.appCardBorderColor || '#000000')} onChange={(e) => updateModalSetting('appCardBorderColor', e.target.value)} style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#9ca3af' }}>Цвет заголовка</label>
                    <input type="color" value={rgbaToHex(modalSettings.appTitleColor || '#000000')} onChange={(e) => updateModalSetting('appTitleColor', e.target.value)} style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#9ca3af' }}>Цвет текста</label>
                    <input type="color" value={rgbaToHex(modalSettings.appTextColor || 'rgba(0, 0, 0, 0.7)')} onChange={(e) => updateModalSetting('appTextColor', e.target.value)} style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#9ca3af' }}>Цвет времени</label>
                    <input type="color" value={rgbaToHex(modalSettings.appMetaTextColor || '#070000')} onChange={(e) => updateModalSetting('appMetaTextColor', e.target.value)} style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }} />
                  </div>
                </div>
              </div>

              <div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#d1d5db', marginBottom: '8px' }}>
                  Активность за период (вкладки)
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#9ca3af' }}>Фон</label>
                    <input type="color" value={rgbaToHex(modalSettings.periodBlockBackground || 'rgba(0, 0, 0, 0.05)')} onChange={(e) => updateModalSetting('periodBlockBackground', e.target.value)} style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#9ca3af' }}>Обводка</label>
                    <input type="color" value={rgbaToHex(modalSettings.periodBlockBorderColor || '#000000')} onChange={(e) => updateModalSetting('periodBlockBorderColor', e.target.value)} style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#9ca3af' }}>Цвет заголовка</label>
                    <input type="color" value={rgbaToHex(modalSettings.periodTitleColor || '#000000')} onChange={(e) => updateModalSetting('periodTitleColor', e.target.value)} style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#9ca3af' }}>Цвет текста</label>
                    <input type="color" value={rgbaToHex(modalSettings.periodTextColor || 'rgba(134, 3, 3, 0.7)')} onChange={(e) => updateModalSetting('periodTextColor', e.target.value)} style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }} />
                  </div>
                </div>
              </div>

              <div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#d1d5db', marginBottom: '8px' }}>
                  Скриншоты
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#9ca3af' }}>Фон карточек</label>
                    <input type="color" value={rgbaToHex(modalSettings.screenshotCardBackground || 'rgba(255, 224, 130, 0.44)')} onChange={(e) => updateModalSetting('screenshotCardBackground', e.target.value)} style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#9ca3af' }}>Обводка карточек</label>
                    <input type="color" value={rgbaToHex(modalSettings.screenshotCardBorderColor || 'rgba(0, 0, 0, 0.3)')} onChange={(e) => updateModalSetting('screenshotCardBorderColor', e.target.value)} style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#9ca3af' }}>Цвет текста</label>
                    <input type="color" value={rgbaToHex(modalSettings.screenshotTextColor || 'rgba(0, 0, 0, 0.5)')} onChange={(e) => updateModalSetting('screenshotTextColor', e.target.value)} style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#9ca3af' }}>Кнопка "Скачать" (фон)</label>
                    <input type="color" value={rgbaToHex(modalSettings.screenshotDownloadButtonBackground || 'rgb(13, 232, 86)')} onChange={(e) => updateModalSetting('screenshotDownloadButtonBackground', e.target.value)} style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#9ca3af' }}>Кнопка "Скачать" (текст)</label>
                    <input type="color" value={rgbaToHex(modalSettings.screenshotDownloadButtonTextColor || '#ffffff')} onChange={(e) => updateModalSetting('screenshotDownloadButtonTextColor', e.target.value)} style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#9ca3af' }}>Кнопка "Печать" (фон)</label>
                    <input type="color" value={rgbaToHex(modalSettings.screenshotPrintButtonBackground || 'rgb(0, 0, 0)')} onChange={(e) => updateModalSetting('screenshotPrintButtonBackground', e.target.value)} style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#9ca3af' }}>Кнопка "Печать" (текст)</label>
                    <input type="color" value={rgbaToHex(modalSettings.screenshotPrintButtonTextColor || '#ffe082')} onChange={(e) => updateModalSetting('screenshotPrintButtonTextColor', e.target.value)} style={{ width: '100%', height: '40px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {modalId === 'app-usage-report' && (
          <div style={{ 
            background: '#111827', 
            padding: '20px', 
            borderRadius: '12px',
            border: '1px solid rgba(75, 85, 99, 0.3)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <FiList size={18} color="#60a5fa" />
              <h3 style={{ margin: 0, fontSize: '1.1em', fontWeight: 600, color: '#60a5fa' }}>
                Таблица запусков
              </h3>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Цвет текста данных
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.tableTextColor || '#ffffff')}
                  onChange={(e) => updateModalSetting('tableTextColor', e.target.value)}
                  style={{ width: '100%', height: '50px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Цвет заголовков таблицы
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.tableHeaderTextColor || '#ffffff')}
                  onChange={(e) => updateModalSetting('tableHeaderTextColor', e.target.value)}
                  style={{ width: '100%', height: '50px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
              </div>
            </div>
          </div>
        )}

        {modalId === 'app-usage-report' && (
          <div style={{ 
            background: '#111827', 
            padding: '20px', 
            borderRadius: '12px',
            border: '1px solid rgba(75, 85, 99, 0.3)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <FiCalendar size={18} color="#60a5fa" />
              <h3 style={{ margin: 0, fontSize: '1.1em', fontWeight: 600, color: '#60a5fa' }}>
                Поле даты
              </h3>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Фон поля
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.dateInputBackground || '#0f172a')}
                  onChange={(e) => updateModalSetting('dateInputBackground', e.target.value)}
                  style={{ width: '100%', height: '50px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Цвет текста
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.dateInputTextColor || '#ffffff')}
                  onChange={(e) => updateModalSetting('dateInputTextColor', e.target.value)}
                  style={{ width: '100%', height: '50px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Стили кнопок навигации (назад/вперед) - для модалок с быстрым выбором дня */}
        {Object.prototype.hasOwnProperty.call(modalDefaults, 'navButtonBackground') && (
          <div style={{ 
            background: '#111827', 
            padding: '20px', 
            borderRadius: '12px',
            border: '1px solid rgba(75, 85, 99, 0.3)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <FiChevronLeft size={18} color="#22c55e" />
              <h3 style={{ margin: 0, fontSize: '1.1em', fontWeight: 600, color: '#22c55e' }}>
                Стили кнопок навигации (стрелки назад/вперед)
              </h3>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Фон кнопок
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.navButtonBackground || '#16a34a')}
                  onChange={(e) => updateModalSetting('navButtonBackground', e.target.value)}
                  style={{
                    width: '100%',
                    height: '50px',
                    border: '1px solid rgba(75, 85, 99, 0.5)',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Цвет текста
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.navButtonTextColor || '#16a34a')}
                  onChange={(e) => updateModalSetting('navButtonTextColor', e.target.value)}
                  style={{
                    width: '100%',
                    height: '50px',
                    border: '1px solid rgba(75, 85, 99, 0.5)',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Цвет обводки
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.navButtonBorderColor || '#16a34a')}
                  onChange={(e) => updateModalSetting('navButtonBorderColor', e.target.value)}
                  style={{
                    width: '100%',
                    height: '50px',
                    border: '1px solid rgba(75, 85, 99, 0.5)',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                />
              </div>

              <div style={{ borderTop: '1px solid rgba(75, 85, 99, 0.3)', paddingTop: '12px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#9ca3af', marginBottom: '10px' }}>
                  Состояние при наведении
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                      Фон
                    </label>
                    <input
                      type="color"
                      value={rgbaToHex(modalSettings.navButtonHoverBackground || '#22c55e')}
                      onChange={(e) => updateModalSetting('navButtonHoverBackground', e.target.value)}
                      style={{
                        width: '100%',
                        height: '50px',
                        border: '1px solid rgba(75, 85, 99, 0.5)',
                        borderRadius: '8px',
                        cursor: 'pointer'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                      Цвет текста
                    </label>
                    <input
                      type="color"
                      value={rgbaToHex(modalSettings.navButtonHoverTextColor || '#22c55e')}
                      onChange={(e) => updateModalSetting('navButtonHoverTextColor', e.target.value)}
                      style={{
                        width: '100%',
                        height: '50px',
                        border: '1px solid rgba(75, 85, 99, 0.5)',
                        borderRadius: '8px',
                        cursor: 'pointer'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                      Цвет обводки
                    </label>
                    <input
                      type="color"
                      value={rgbaToHex(modalSettings.navButtonHoverBorderColor || '#22c55e')}
                      onChange={(e) => updateModalSetting('navButtonHoverBorderColor', e.target.value)}
                      style={{
                        width: '100%',
                        height: '50px',
                        border: '1px solid rgba(75, 85, 99, 0.5)',
                        borderRadius: '8px',
                        cursor: 'pointer'
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Стили кнопки "Закрыть" внизу - для модалок с нижней кнопкой */}
        {Object.prototype.hasOwnProperty.call(modalDefaults, 'showReportButtonWidth') && (
          <div style={{ 
            background: '#111827', 
            padding: '20px', 
            borderRadius: '12px',
            border: '1px solid rgba(75, 85, 99, 0.3)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <FiCheckSquare size={18} color="#f97316" />
              <h3 style={{ margin: 0, fontSize: '1.1em', fontWeight: 600, color: '#f97316' }}>
                Кнопка "Показать отчет"
              </h3>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Ширина ({modalSettings.showReportButtonWidth || 100}%)
                </label>
                <input
                  type="range"
                  min="10"
                  max="100"
                  step="1"
                  value={modalSettings.showReportButtonWidth || 100}
                  onChange={(e) => updateModalSetting('showReportButtonWidth', parseInt(e.target.value, 10))}
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Высота ({modalSettings.showReportButtonHeight || 48}px)
                </label>
                <input
                  type="range"
                  min="28"
                  max="64"
                  step="1"
                  value={modalSettings.showReportButtonHeight || 48}
                  onChange={(e) => updateModalSetting('showReportButtonHeight', parseInt(e.target.value, 10))}
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Цвет фона
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.showReportButtonBackground || '#43e97b')}
                  onChange={(e) => updateModalSetting('showReportButtonBackground', e.target.value)}
                  style={{
                    width: '100%',
                    height: '50px',
                    border: '1px solid rgba(75, 85, 99, 0.5)',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Цвет текста
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.showReportButtonTextColor || '#ffffff')}
                  onChange={(e) => updateModalSetting('showReportButtonTextColor', e.target.value)}
                  style={{
                    width: '100%',
                    height: '50px',
                    border: '1px solid rgba(75, 85, 99, 0.5)',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Стили кнопок локального отчета (Показать/Удалить) */}
        {Object.prototype.hasOwnProperty.call(modalDefaults, 'showReportButtonBackground') && (
          <div style={{ 
            background: '#111827', 
            padding: '20px', 
            borderRadius: '12px',
            border: '1px solid rgba(75, 85, 99, 0.3)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <FiCheckSquare size={18} color="#f97316" />
              <h3 style={{ margin: 0, fontSize: '1.1em', fontWeight: 600, color: '#f97316' }}>
                Кнопки отчетов (локальные ПК)
              </h3>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  "Показать отчет" — фон
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.showReportButtonBackground || '#27ae60')}
                  onChange={(e) => updateModalSetting('showReportButtonBackground', e.target.value)}
                  style={{ width: '100%', height: '50px', border: '1px solid rgba(75, 85, 99, 0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  "Показать отчет" — текст
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.showReportButtonTextColor || '#ffffff')}
                  onChange={(e) => updateModalSetting('showReportButtonTextColor', e.target.value)}
                  style={{ width: '100%', height: '50px', border: '1px solid rgba(75, 85, 99, 0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  "Удалить за период" — фон
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.deletePeriodButtonBackground || '#e74c3c')}
                  onChange={(e) => updateModalSetting('deletePeriodButtonBackground', e.target.value)}
                  style={{ width: '100%', height: '50px', border: '1px solid rgba(75, 85, 99, 0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  "Удалить за период" — текст
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.deletePeriodButtonTextColor || '#ffffff')}
                  onChange={(e) => updateModalSetting('deletePeriodButtonTextColor', e.target.value)}
                  style={{ width: '100%', height: '50px', border: '1px solid rgba(75, 85, 99, 0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  "За сегодня" — фон
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.deleteDayButtonBackground || '#e74c3c')}
                  onChange={(e) => updateModalSetting('deleteDayButtonBackground', e.target.value)}
                  style={{ width: '100%', height: '50px', border: '1px solid rgba(75, 85, 99, 0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  "За сегодня" — текст
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.deleteDayButtonTextColor || '#ffffff')}
                  onChange={(e) => updateModalSetting('deleteDayButtonTextColor', e.target.value)}
                  style={{ width: '100%', height: '50px', border: '1px solid rgba(75, 85, 99, 0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  "За неделю" — фон
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.deleteWeekButtonBackground || '#e74c3c')}
                  onChange={(e) => updateModalSetting('deleteWeekButtonBackground', e.target.value)}
                  style={{ width: '100%', height: '50px', border: '1px solid rgba(75, 85, 99, 0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  "За неделю" — текст
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.deleteWeekButtonTextColor || '#ffffff')}
                  onChange={(e) => updateModalSetting('deleteWeekButtonTextColor', e.target.value)}
                  style={{ width: '100%', height: '50px', border: '1px solid rgba(75, 85, 99, 0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  "За месяц" — фон
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.deleteMonthButtonBackground || '#e74c3c')}
                  onChange={(e) => updateModalSetting('deleteMonthButtonBackground', e.target.value)}
                  style={{ width: '100%', height: '50px', border: '1px solid rgba(75, 85, 99, 0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  "За месяц" — текст
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.deleteMonthButtonTextColor || '#ffffff')}
                  onChange={(e) => updateModalSetting('deleteMonthButtonTextColor', e.target.value)}
                  style={{ width: '100%', height: '50px', border: '1px solid rgba(75, 85, 99, 0.5)', borderRadius: '8px', cursor: 'pointer' }}
                />
              </div>
            </div>
          </div>
        )}


        {/* Стили блока фильтров - только для календаря */}
        {modalId === 'calendar' && (
          <div style={{ 
            background: '#111827', 
            padding: '20px', 
            borderRadius: '12px',
            border: '1px solid rgba(75, 85, 99, 0.3)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <FiImage size={18} color="#fbbf24" />
              <h3 style={{ margin: 0, fontSize: '1.1em', fontWeight: 600, color: '#fbbf24' }}>
                Стили блока "Показать фильтры"
              </h3>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Фон блока фильтров
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.filterBlockBackground || '#1a3d2e')}
                  onChange={(e) => updateModalSetting('filterBlockBackground', e.target.value)}
                  style={{
                    width: '100%',
                    height: '50px',
                    border: '1px solid rgba(75, 85, 99, 0.5)',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Фоновое изображение блока фильтров (URL)
                </label>
                <input
                  type="text"
                  value={modalSettings.filterBlockBackgroundImage || ''}
                  onChange={(e) => updateModalSetting('filterBlockBackgroundImage', e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid rgba(75, 85, 99, 0.5)',
                    borderRadius: '8px',
                    background: '#1f2937',
                    color: '#fff',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Стили блока навигации (стрелки влево-вправо) - только для календаря */}
        {modalId === 'calendar' && (
          <div style={{ 
            background: '#111827', 
            padding: '20px', 
            borderRadius: '12px',
            border: '1px solid rgba(75, 85, 99, 0.3)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <FiImage size={18} color="#a855f7" />
              <h3 style={{ margin: 0, fontSize: '1.1em', fontWeight: 600, color: '#a855f7' }}>
                Стили блока навигации по месяцам
              </h3>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Фон блока со стрелками
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.navBlockBackground || '#1a3d2e')}
                  onChange={(e) => updateModalSetting('navBlockBackground', e.target.value)}
                  style={{
                    width: '100%',
                    height: '50px',
                    border: '1px solid rgba(75, 85, 99, 0.5)',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Фоновое изображение блока навигации (URL)
                </label>
                <input
                  type="text"
                  value={modalSettings.navBlockBackgroundImage || ''}
                  onChange={(e) => updateModalSetting('navBlockBackgroundImage', e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid rgba(75, 85, 99, 0.5)',
                    borderRadius: '8px',
                    background: '#1f2937',
                    color: '#fff',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Стили сетки календаря - только для календаря */}
        {modalId === 'calendar' && (
          <div style={{ 
            background: '#111827', 
            padding: '20px', 
            borderRadius: '12px',
            border: '1px solid rgba(75, 85, 99, 0.3)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <FiLayout size={18} color="#22d3ee" />
              <h3 style={{ margin: 0, fontSize: '1.1em', fontWeight: 600, color: '#22d3ee' }}>
                Стили сетки календаря
              </h3>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Фон сетки календаря
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.calendarGridBackground || '#222a35')}
                  onChange={(e) => updateModalSetting('calendarGridBackground', e.target.value)}
                  style={{
                    width: '100%',
                    height: '50px',
                    border: '1px solid rgba(75, 85, 99, 0.5)',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Фоновое изображение сетки календаря (URL)
                </label>
                <input
                  type="text"
                  value={modalSettings.calendarGridBackgroundImage || ''}
                  onChange={(e) => updateModalSetting('calendarGridBackgroundImage', e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid rgba(75, 85, 99, 0.5)',
                    borderRadius: '8px',
                    background: '#1f2937',
                    color: '#fff',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Фон ячеек календаря
                </label>
                <input
                  type="color"
                  value={rgbaToHex(modalSettings.calendarCellBackground || '#1a3d2e')}
                  onChange={(e) => updateModalSetting('calendarCellBackground', e.target.value)}
                  style={{
                    width: '100%',
                    height: '50px',
                    border: '1px solid rgba(75, 85, 99, 0.5)',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Цвет чисел (дней)
                </label>
                <input
                  type="color"
                  value={modalSettings.calendarCellTextColor || '#ffffff'}
                  onChange={(e) => updateModalSetting('calendarCellTextColor', e.target.value)}
                  style={{
                    width: '100%',
                    height: '50px',
                    border: '1px solid rgba(75, 85, 99, 0.5)',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Цвет дней недели
                </label>
                <input
                  type="color"
                  value={modalSettings.calendarWeekdayColor || '#43e97b'}
                  onChange={(e) => updateModalSetting('calendarWeekdayColor', e.target.value)}
                  style={{
                    width: '100%',
                    height: '50px',
                    border: '1px solid rgba(75, 85, 99, 0.5)',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Стили заголовка - только для отгулов */}
        {modalId === 'leaves' && (
          <div style={{ 
            background: '#111827', 
            padding: '20px', 
            borderRadius: '12px',
            border: '1px solid rgba(75, 85, 99, 0.3)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <FiType size={18} color="#fbbf24" />
              <h3 style={{ margin: 0, fontSize: '1.1em', fontWeight: 600, color: '#fbbf24' }}>
                Стили заголовка и хедера
              </h3>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Фон заголовка (хедер)
                </label>
                <input
                  type="color"
                  value={modalSettings.headerBackground || '#232931'}
                  onChange={(e) => updateModalSetting('headerBackground', e.target.value)}
                  style={{
                    width: '100%',
                    height: '50px',
                    border: '1px solid rgba(75, 85, 99, 0.5)',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Цвет заголовка "Заявка на отгулы"
                </label>
                <input
                  type="color"
                  value={modalSettings.titleColor || '#43e97b'}
                  onChange={(e) => updateModalSetting('titleColor', e.target.value)}
                  style={{
                    width: '100%',
                    height: '50px',
                    border: '1px solid rgba(75, 85, 99, 0.5)',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Стили формы - только для отгулов */}
        {modalId === 'leaves' && (
          <div style={{ 
            background: '#111827', 
            padding: '20px', 
            borderRadius: '12px',
            border: '1px solid rgba(75, 85, 99, 0.3)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <FiLayout size={18} color="#a855f7" />
              <h3 style={{ margin: 0, fontSize: '1.1em', fontWeight: 600, color: '#a855f7' }}>
                Стили формы заявки
              </h3>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Фон формы
                </label>
                <input
                  type="color"
                  value={modalSettings.formBackground || '#2c3e50'}
                  onChange={(e) => updateModalSetting('formBackground', e.target.value)}
                  style={{
                    width: '100%',
                    height: '50px',
                    border: '1px solid rgba(75, 85, 99, 0.5)',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Стили карточек - только для отгулов */}
        {modalId === 'leaves' && (
          <div style={{ 
            background: '#111827', 
            padding: '20px', 
            borderRadius: '12px',
            border: '1px solid rgba(75, 85, 99, 0.3)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <FiLayout size={18} color="#22d3ee" />
              <h3 style={{ margin: 0, fontSize: '1.1em', fontWeight: 600, color: '#22d3ee' }}>
                Стили карточек заявок
              </h3>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Фон карточки
                </label>
                <input
                  type="color"
                  value={modalSettings.cardBackground || '#ffffff'}
                  onChange={(e) => updateModalSetting('cardBackground', e.target.value)}
                  style={{
                    width: '100%',
                    height: '50px',
                    border: '1px solid rgba(75, 85, 99, 0.5)',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Цвет дат в карточке
                </label>
                <input
                  type="color"
                  value={modalSettings.cardTextColor || '#2193b0'}
                  onChange={(e) => updateModalSetting('cardTextColor', e.target.value)}
                  style={{
                    width: '100%',
                    height: '50px',
                    border: '1px solid rgba(75, 85, 99, 0.5)',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                  Цвет причины в карточке
                </label>
                <input
                  type="color"
                  value={modalSettings.cardReasonColor || '#636e72'}
                  onChange={(e) => updateModalSetting('cardReasonColor', e.target.value)}
                  style={{
                    width: '100%',
                    height: '50px',
                    border: '1px solid rgba(75, 85, 99, 0.5)',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Стили текста */}
        <div style={{ 
          background: '#111827', 
          padding: '20px', 
          borderRadius: '12px',
          border: '1px solid rgba(75, 85, 99, 0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <FiType size={18} color="#60a5fa" />
            <h3 style={{ margin: 0, fontSize: '1.1em', fontWeight: 600, color: '#60a5fa' }}>
              Стили текста в "{modalName}"
            </h3>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                Цвет текста
              </label>
              <input
                type="color"
                value={modalSettings.textColor}
                onChange={(e) => updateModalSetting('textColor', e.target.value)}
                style={{
                  width: '100%',
                  height: '50px',
                  border: '1px solid rgba(75, 85, 99, 0.5)',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                Размер текста: {modalSettings.textSize}
              </label>
              <input
                type="range"
                min="10"
                max="24"
                step="1"
                value={parseInt(modalSettings.textSize)}
                onChange={(e) => updateModalSetting('textSize', `${e.target.value}px`)}
                style={{
                  width: '100%',
                  cursor: 'pointer'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                Выравнивание текста
              </label>
              <select
                value={modalSettings.textAlign}
                onChange={(e) => updateModalSetting('textAlign', e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid rgba(75, 85, 99, 0.5)',
                  borderRadius: '8px',
                  backgroundColor: '#1f2937',
                  color: '#d1d5db',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                <option value="left">Слева</option>
                <option value="center">По центру</option>
                <option value="right">Справа</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                Шрифт
              </label>
              <select
                value={modalSettings.fontFamily}
                onChange={(e) => updateModalSetting('fontFamily', e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid rgba(75, 85, 99, 0.5)',
                  borderRadius: '8px',
                  backgroundColor: '#1f2937',
                  color: '#d1d5db',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                <option value="Inter, system-ui, -apple-system, sans-serif">Inter</option>
                <option value="Arial, sans-serif">Arial</option>
                <option value="Georgia, serif">Georgia</option>
                <option value="'Courier New', monospace">Courier New</option>
                <option value="'Times New Roman', serif">Times New Roman</option>
                <option value="Verdana, sans-serif">Verdana</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Правая колонка - превью */}
      <div style={{ flex: '0 0 400px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div style={{
          background: '#111827',
          padding: '20px',
          borderRadius: '12px',
          border: '1px solid rgba(75, 85, 99, 0.3)',
          height: 'fit-content'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <FiMonitor size={18} color="#60a5fa" />
            <h3 style={{ margin: 0, fontSize: '1.1em', fontWeight: 600, color: '#60a5fa' }}>
              Превью "{modalName}"
            </h3>
          </div>

          {/* Превью модалки */}
          <div
            style={{
              // Используем только отдельные свойства, не смешиваем с shorthand
              backgroundColor: modalSettings.background && !modalSettings.background.includes('gradient') && !modalSettings.background.includes('url') 
                ? modalSettings.background 
                : 'transparent',
              backgroundImage: modalSettings.backgroundImage 
                ? `url(${modalSettings.backgroundImage})` 
                : modalSettings.background && (modalSettings.background.includes('gradient') || modalSettings.background.includes('url'))
                  ? modalSettings.background
                  : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
              position: 'relative',
              overflow: 'hidden',
              minHeight: '400px'
            }}
          >
            {/* Overlay для прозрачности изображения фона */}
            {modalSettings.backgroundImage && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: modalSettings.background,
                  opacity: 1 - modalSettings.backgroundImageOpacity,
                  pointerEvents: 'none',
                  borderRadius: '12px'
                }}
              />
            )}
            
            <div style={{ position: 'relative', zIndex: 1 }}>
              {/* Заголовок модалки */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <Icon size={32} color="#60a5fa" />
                <div>
                  <div style={{ 
                    fontSize: '20px', 
                    fontWeight: 700, 
                    color: '#f3f4f6',
                    marginBottom: '4px'
                  }}>
                    {modalName}
                  </div>
                  <div style={{ 
                    fontSize: '13px', 
                    color: '#9ca3af'
                  }}>
                    Превью модального окна
                  </div>
                </div>
              </div>
              
              {/* Примеры текста */}
              <div style={{ 
                fontSize: modalSettings.textSize,
                color: modalSettings.textColor,
                fontFamily: modalSettings.fontFamily,
                textAlign: modalSettings.textAlign,
                lineHeight: 1.6,
                marginBottom: '20px',
                padding: '12px',
                background: 'rgba(0,0,0,0.2)',
                borderRadius: '8px'
              }}>
                Пример текста в модалке "{modalName}". Здесь показывается выбранный шрифт, размер и выравнивание текста.
              </div>
              
              {/* Примеры кнопок */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {['Кнопка 1', 'Кнопка 2', 'Кнопка 3'].map((label, i) => (
                  <div
                    key={i}
                    style={{
                      padding: '12px 20px',
                      background: modalSettings.buttonBackground,
                      backgroundImage: modalSettings.buttonBackgroundImage ? `url(${modalSettings.buttonBackgroundImage})` : 'none',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      border: '1px solid rgba(59, 130, 246, 0.4)',
                      borderRadius: '8px',
                      color: modalSettings.textColor,
                      fontSize: modalSettings.textSize,
                      fontFamily: modalSettings.fontFamily,
                      fontWeight: 500,
                      textAlign: modalSettings.textAlign,
                      position: 'relative',
                      overflow: 'hidden',
                      cursor: 'pointer'
                    }}
                  >
                    {/* Overlay для прозрачности изображения кнопки */}
                    {modalSettings.buttonBackgroundImage && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: modalSettings.buttonBackground,
                          opacity: 1 - modalSettings.buttonBackgroundImageOpacity,
                          pointerEvents: 'none'
                        }}
                      />
                    )}
                    <span style={{ position: 'relative', zIndex: 1 }}>{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const MODAL_IMG_ORIGINAL_MAX_BYTES = 4 * 1024 * 1024;
const MODAL_IMG_ORIGINAL_MAX_EDGE = 4096;

// Функция для сжатия изображения и конвертации в Base64 (при небольшом файле — исходный data URL без потерь)
const loadImageAsBase64 = (file, maxWidth = 3840, maxHeight = 2160, quality = 1) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      const img = new Image();
      img.onload = () => {
        const nw = img.naturalWidth;
        const nh = img.naturalHeight;

        if (nw < 1 || nh < 1) {
          reject(new Error('Некорректный размер изображения'));
          return;
        }

        if (
          file.size <= MODAL_IMG_ORIGINAL_MAX_BYTES &&
          nw <= MODAL_IMG_ORIGINAL_MAX_EDGE &&
          nh <= MODAL_IMG_ORIGINAL_MAX_EDGE
        ) {
          resolve(dataUrl);
          return;
        }

        let width = nw;
        let height = nh;

        const scale = Math.min(maxWidth / width, maxHeight / height, 1);
        width = Math.max(1, Math.round(width * scale));
        height = Math.max(1, Math.round(height * scale));

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);

        const q = Math.min(1, Math.max(0.01, quality));
        const webp = canvas.toDataURL('image/webp', q);
        const compressedBase64 = webp.startsWith('data:image/webp')
          ? webp
          : canvas.toDataURL('image/jpeg', q);
        
        // Проверяем размер (примерно, Base64 примерно на 33% больше оригинала)
        const sizeInMB = (compressedBase64.length * 0.75) / (1024 * 1024);
        console.log(`📦 Изображение сжато: ${img.width}x${img.height} → ${width}x${height}, размер: ${sizeInMB.toFixed(2)} MB`);
        
        resolve(compressedBase64);
      };
      img.onerror = reject;
      img.src = dataUrl;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

// Функция для проверки доступного места в localStorage
const checkLocalStorageSize = () => {
  let total = 0;
  for (let key in localStorage) {
    if (localStorage.hasOwnProperty(key)) {
      total += localStorage[key].length + key.length;
    }
  }
  const totalMB = (total * 2) / (1024 * 1024); // UTF-16, каждый символ = 2 байта
  return { totalMB, percentUsed: (totalMB / 5) * 100 }; // Предполагаем лимит 5MB
};

// Компонент для управления видимостью одной кнопки
function SidebarButtonVisibilityControl({ button, visibility, onVisibilityChange, onHasChanges }) {
  const isVisibleForAll = visibility === null || visibility === undefined;
  const selectedDepartments = Array.isArray(visibility) ? visibility : [];
  const [isExpanded, setIsExpanded] = useState(false);
  
  return (
    <div
      style={{
        padding: '16px',
        background: 'rgba(31, 41, 55, 0.5)',
        border: '1px solid rgba(75, 85, 99, 0.3)',
        borderRadius: '8px',
        transition: 'all 0.2s'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {isVisibleForAll ? (
            <FiEye size={20} color="#22c55e" />
          ) : selectedDepartments.length > 0 ? (
            <FiEye size={20} color="#3b82f6" />
          ) : (
            <FiEyeOff size={20} color="#ef4444" />
          )}
          <span style={{ 
            color: '#e5e7eb', 
            fontSize: '15px', 
            fontWeight: 500 
          }}>
            {button.label}
          </span>
        </div>
        <label
          style={{
            position: 'relative',
            display: 'inline-block',
            width: '48px',
            height: '24px',
            cursor: 'pointer'
          }}
        >
          <input
            type="checkbox"
            checked={isVisibleForAll}
            onChange={(e) => {
              onVisibilityChange(button.key, e.target.checked ? null : []);
              onHasChanges(true);
            }}
            style={{
              opacity: 0,
              width: 0,
              height: 0
            }}
          />
          <span
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: isVisibleForAll ? '#22c55e' : '#6b7280',
              borderRadius: '24px',
              transition: 'all 0.3s',
              boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.2)'
            }}
          >
            <span
              style={{
                position: 'absolute',
                content: '""',
                height: '18px',
                width: '18px',
                left: isVisibleForAll ? '24px' : '3px',
                bottom: '3px',
                backgroundColor: '#fff',
                borderRadius: '50%',
                transition: 'all 0.3s',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}
            />
          </span>
        </label>
      </div>
      
      <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>
        {isVisibleForAll 
          ? '✅ Видна всем пользователям' 
          : selectedDepartments.length > 0
          ? `👥 Видна только для ${selectedDepartments.length} департамент(ов)`
          : '❌ Скрыта для всех (кроме админа и HR)'}
      </div>
      
      {!isVisibleForAll && (
        <div>
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            style={{
              padding: '8px 12px',
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '6px',
              color: '#60a5fa',
              cursor: 'pointer',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              width: '100%',
              justifyContent: 'space-between'
            }}
          >
            <span>Выбрать департаменты</span>
            {isExpanded ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
          </button>
          
          {isExpanded && (
            <div style={{ 
              marginTop: '12px', 
              padding: '12px',
              background: 'rgba(17, 24, 39, 0.5)',
              borderRadius: '6px',
              border: '1px solid rgba(75, 85, 99, 0.3)',
              maxHeight: '300px',
              overflowY: 'auto'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {ALL_DEPARTMENTS.map(dept => (
                  <label
                    key={dept}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '6px',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.background = 'rgba(59, 130, 246, 0.1)'}
                    onMouseLeave={(e) => e.target.style.background = 'transparent'}
                  >
                    <input
                      type="checkbox"
                      checked={selectedDepartments.includes(dept)}
                      onChange={(e) => {
                        const newDepartments = e.target.checked
                          ? [...selectedDepartments, dept]
                          : selectedDepartments.filter(d => d !== dept);
                        onVisibilityChange(button.key, newDepartments);
                        onHasChanges(true);
                      }}
                      style={{
                        transform: 'scale(1.2)',
                        cursor: 'pointer'
                      }}
                    />
                    <span style={{ color: '#d1d5db', fontSize: '13px' }}>{dept}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function FrontendSettingsModal({ open, onClose, isMobile = false }) {
  const { state } = useApp();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState('main-menu'); // Активная вкладка
  const [sidebarVisibility, setSidebarVisibility] = useState({}); // Видимость кнопок сайтбара
  
  // Проверка доступа к настройкам видимости (только для Системного администратора и HR-менеджера)
  const canManageVisibility = () => {
    const department = state.user?.department;
    return department === 'Системный администратор' || department === 'HR-менеджер (менеджер по персоналу)';
  };
  
  // Состояния для сворачивания/разворачивания блоков
  const [expandedSections, setExpandedSections] = useState({
    presets: true,
    mainBackground: true,
    modalBackground: true,
    sidebarStyles: true,
    monitoringReportsList: false,
    monitoringReportEditor: true,
    monitoringBase: false,
    monitoringAppUsageDetails: false,
    monitoringRemoteReportCards: false,
    monitoringRemoteReportDetails: false,
    globalModalStyles: true,
    tableAndDeadlineStyles: true,
    tasksTableAndDeadlineStyles: true,
    clockBlock: true,
    vprMain: true,
    vprDiscrepancies: true,
    ordersModal: true,
    pickingModal: true
  });
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Обработчик изменения настроек с отслеживанием
  const handleSettingsChange = (newSettings) => {
    setSettings(newSettings);
    setHasChanges(true);
  };
  
  const updateModalSetting = (modalId, key, value) => {
    const baseModalSettings = {
      ...(DEFAULT_SETTINGS.modals?.[modalId] || {}),
      ...(settings.modals?.[modalId] || {})
    };
    const updatedModals = {
      ...settings.modals,
      [modalId]: {
        ...baseModalSettings,
        [key]: value
      }
    };
    handleSettingsChange({ ...settings, modals: updatedModals });
  };
  
  const remoteReportSettings = {
    ...(DEFAULT_SETTINGS.modals?.['remote-report'] || {}),
    ...(settings.modals?.['remote-report'] || {})
  };
  
  const remoteReportDetailsSettings = {
    ...(DEFAULT_SETTINGS.modals?.['remote-report-details'] || {}),
    ...(settings.modals?.['remote-report-details'] || {})
  };
  
  const renderPresetsSection = () => (
    <Section 
      title="🎨 Пресеты (темы)" 
      icon={<FiType size={18} />}
      expanded={expandedSections.presets}
      onToggle={() => toggleSection('presets')}
    >
      <button
        onClick={() => setShowPresetDialog(!showPresetDialog)}
        style={{
          width: '100%',
          padding: '12px',
          borderRadius: '8px',
          border: '1px solid rgba(59, 130, 246, 0.5)',
          background: 'rgba(59, 130, 246, 0.1)',
          color: '#60a5fa',
          cursor: 'pointer',
          fontWeight: 600,
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}
      >
        💾 Сохранить текущие настройки как пресет
      </button>
      <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
        <button
          onClick={() => presetFileInputRef.current?.click()}
          style={{
            flex: 1,
            padding: '10px',
            borderRadius: '8px',
            border: '1px solid rgba(59, 130, 246, 0.5)',
            background: 'rgba(59, 130, 246, 0.08)',
            color: '#93c5fd',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '13px'
          }}
        >
          📥 Загрузить пресет (JSON)
        </button>
        <input
          ref={presetFileInputRef}
          type="file"
          accept="application/json"
          onChange={handleImportPreset}
          style={{ display: 'none' }}
        />
      </div>

      {showPresetDialog && (
        <div style={{
          padding: '16px',
          borderRadius: '8px',
          background: '#111827',
          border: '1px solid rgba(75, 85, 99, 0.5)',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}>
          <input
            type="text"
            placeholder="Название (например: Темная тема)"
            value={presetName}
            onChange={(e) => setPresetName(e.target.value)}
            style={{
              padding: '10px 12px',
              borderRadius: '6px',
              border: '1px solid rgba(75, 85, 99, 0.5)',
              background: '#1f2937',
              color: '#fff',
              fontSize: '14px'
            }}
          />
          <input
            type="text"
            placeholder="Описание (опционально)"
            value={presetDescription}
            onChange={(e) => setPresetDescription(e.target.value)}
            style={{
              padding: '10px 12px',
              borderRadius: '6px',
              border: '1px solid rgba(75, 85, 99, 0.5)',
              background: '#1f2937',
              color: '#fff',
              fontSize: '14px'
            }}
          />
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={handleSavePreset}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '6px',
                border: 'none',
                background: '#10b981',
                color: '#fff',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '14px'
              }}
            >
              ✅ Сохранить
            </button>
            <button
              onClick={() => {
                setShowPresetDialog(false);
                setPresetName('');
                setPresetDescription('');
              }}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '6px',
                border: '1px solid rgba(75, 85, 99, 0.5)',
                background: 'transparent',
                color: '#9ca3af',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '14px'
              }}
            >
              Отмена
            </button>
          </div>
        </div>
      )}

      {presets.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: '#9ca3af', marginBottom: '4px' }}>
            Сохраненные пресеты:
          </div>
          {presets.map((preset) => (
            <div
              key={preset.id}
              style={{
                padding: '12px',
                borderRadius: '8px',
                background: '#111827',
                border: '1px solid rgba(75, 85, 99, 0.5)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: '#e5e7eb', fontSize: '14px' }}>
                  {preset.name}
                </div>
                {preset.description && (
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                    {preset.description}
                  </div>
                )}
              </div>
              <button
                onClick={() => handleApplyPreset(preset)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  background: '#10b981',
                  color: '#fff',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '13px'
                }}
              >
                ✅ Применить
              </button>
              <button
                onClick={() => handleDownloadPreset(preset)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid rgba(59, 130, 246, 0.5)',
                  background: 'rgba(59, 130, 246, 0.1)',
                  color: '#93c5fd',
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
                title="Скачать пресет"
              >
                ⬇️
              </button>
              <button
                onClick={() => handleDeletePreset(preset)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid rgba(239, 68, 68, 0.5)',
                  background: 'rgba(239, 68, 68, 0.1)',
                  color: '#ef4444',
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                🗑️
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div style={{
          padding: '16px',
          textAlign: 'center',
          color: '#6b7280',
          fontSize: '13px',
          fontStyle: 'italic'
        }}>
          Пока нет сохраненных пресетов
        </div>
      )}
    </Section>
  );

  // Состояния для пресетов
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState('');
  const [presetDescription, setPresetDescription] = useState('');
  const [showPresetDialog, setShowPresetDialog] = useState(false);
  const presetFileInputRef = useRef(null);

  // Загрузка настроек при открытии
  useEffect(() => {
    if (open) {
      // Сначала пытаемся загрузить с сервера
      const loadFromServer = async () => {
        try {
          const response = await fetch('/api/frontend-settings', {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          
          if (response.ok) {
            const result = await response.json();
            if (result.success && result.settings) {
              console.log('📥 Настройки фронтенда загружены с сервера');
              // Объединяем с DEFAULT_SETTINGS, чтобы гарантировать наличие всех полей
              const mergedSettings = {
                ...DEFAULT_SETTINGS,
                ...result.settings,
                modals: {
                  ...DEFAULT_SETTINGS.modals,
                  ...(result.settings.modals || {})
                }
              };
              setSettings(mergedSettings);
              // Сохраняем в localStorage для оффлайн доступа
              localStorage.setItem('frontendSettings', JSON.stringify(mergedSettings));
              return;
            }
          }
        } catch (error) {
          console.warn('⚠️ Не удалось загрузить настройки с сервера, используем локальные:', error);
        }
        
        // Fallback: загружаем из localStorage
        const saved = localStorage.getItem('frontendSettings');
        if (saved) {
          try {
            const loadedSettings = JSON.parse(saved);
            // Объединяем с DEFAULT_SETTINGS
            const mergedSettings = {
              ...DEFAULT_SETTINGS,
              ...loadedSettings,
              modals: {
                ...DEFAULT_SETTINGS.modals,
                ...(loadedSettings.modals || {})
              }
            };
            setSettings(mergedSettings);
            console.log('📥 Настройки фронтенда загружены из localStorage');
          } catch (error) {
            console.error('Ошибка загрузки настроек из localStorage:', error);
          }
        }
      };
      
      loadFromServer();
      
      // Загружаем настройки видимости кнопок сайтбара
      const loadSidebarVisibility = async () => {
        try {
          const response = await fetch('/api/sidebar-visibility', {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          
          if (response.ok) {
            const result = await response.json();
            if (result.success && result.visibility) {
              console.log('📥 Настройки видимости кнопок сайтбара загружены');
              // Конвертируем старый формат (true/false) в новый (массив департаментов)
              const convertedVisibility = {};
              SIDEBAR_BUTTONS.forEach(btn => {
                const oldValue = result.visibility[btn.key];
                if (oldValue === false) {
                  // Если была скрыта, делаем пустой массив (не видна никому, кроме админа/HR)
                  convertedVisibility[btn.key] = [];
                } else if (Array.isArray(oldValue)) {
                  // Уже новый формат
                  convertedVisibility[btn.key] = oldValue;
                } else {
                  // true или undefined - видна всем (null означает "видна всем")
                  convertedVisibility[btn.key] = null;
                }
              });
              setSidebarVisibility(convertedVisibility);
            } else {
              // По умолчанию все кнопки видимы всем (null = видна всем)
              const defaultVisibility = {};
              SIDEBAR_BUTTONS.forEach(btn => {
                defaultVisibility[btn.key] = null;
              });
              setSidebarVisibility(defaultVisibility);
            }
          } else {
            // Если ошибка (например, 403), просто используем настройки по умолчанию
            console.warn('⚠️ Не удалось загрузить настройки видимости (статус:', response.status, ')');
            const defaultVisibility = {};
            SIDEBAR_BUTTONS.forEach(btn => {
              defaultVisibility[btn.key] = null;
            });
            setSidebarVisibility(defaultVisibility);
          }
        } catch (error) {
          console.warn('⚠️ Не удалось загрузить настройки видимости:', error);
          // По умолчанию все кнопки видимы всем (null = видна всем)
          const defaultVisibility = {};
          SIDEBAR_BUTTONS.forEach(btn => {
            defaultVisibility[btn.key] = null;
          });
          setSidebarVisibility(defaultVisibility);
        }
      };
      
      if (canManageVisibility()) {
        loadSidebarVisibility();
      }
      
      // Загружаем список пресетов
      refreshPresets();
      setHasChanges(false);
    }
  }, [open]);

  // WebSocket listener для синхронизации настроек и пресетов
  useEffect(() => {
    const handleFrontendSettingsUpdate = (updatedSettings) => {
      if (updatedSettings === null) {
        // Сброс настроек к дефолтным
        console.log('📡 Получен сброс настроек через WebSocket');
        localStorage.removeItem('frontendSettings');
        setSettings(DEFAULT_SETTINGS);
        // Перезагружаем страницу для полного сброса
        setTimeout(() => {
          window.location.reload();
        }, 500);
      } else {
        console.log('📡 Получены обновленные настройки фронтенда через WebSocket');
        setSettings(updatedSettings);
        // Сохраняем в localStorage
        localStorage.setItem('frontendSettings', JSON.stringify(updatedSettings));
      }
    };
    
    const handlePresetsUpdate = (updatedPresets) => {
      console.log('📡 Получены обновленные пресеты через WebSocket');
      setPresets(updatedPresets);
    };
    
    if (window.socket) {
      window.socket.on('frontend-settings-updated', handleFrontendSettingsUpdate);
      window.socket.on('frontend-presets-updated', handlePresetsUpdate);
      
      return () => {
        if (window.socket) {
          window.socket.off('frontend-settings-updated', handleFrontendSettingsUpdate);
          window.socket.off('frontend-presets-updated', handlePresetsUpdate);
        }
      };
    }
  }, []);

  // Применение настроек к DOM (только для превью в модалке)
  useEffect(() => {
    if (!open) return;

    // Применяем основной фон
    // Применяем основной фон
    // Если это градиент, используем его как backgroundImage, иначе как backgroundColor
    const mainBg = settings.mainBackground || '#111827';
    if (mainBg.includes('gradient')) {
      document.documentElement.style.setProperty('--main-background', 'transparent');
      document.documentElement.style.setProperty('--main-background-image', mainBg);
    } else {
      document.documentElement.style.setProperty('--main-background', mainBg);
      document.documentElement.style.setProperty('--main-background-image', 'none');
    }
    
    // Применяем изображение фона
    if (settings.mainBackgroundImage && settings.mainBackgroundImage.trim() !== '') {
      // Base64 изображение уже содержит data:image/..., для CSS нужно использовать url()
      let imageUrl;
      if (settings.mainBackgroundImage.startsWith('data:')) {
        // Для data: URLs оборачиваем в url()
        imageUrl = `url("${settings.mainBackgroundImage}")`;
      } else if (settings.mainBackgroundImage.startsWith('url(')) {
        // Уже обернуто в url()
        imageUrl = settings.mainBackgroundImage;
      } else {
        // Обычный путь, оборачиваем в url()
        imageUrl = `url(${settings.mainBackgroundImage})`;
      }
      document.documentElement.style.setProperty('--main-background-image', imageUrl);
      document.documentElement.style.setProperty('--main-background-image-opacity', settings.mainBackgroundImageOpacity || 1);
      
      // Если есть изображение, используем его вместо цвета фона
      document.documentElement.style.setProperty('--main-background', 'transparent');
    } else {
      document.documentElement.style.setProperty('--main-background-image', 'none');
      document.documentElement.style.setProperty('--main-background-image-opacity', '1');
    }
    
    // Применяем фон модалок
    document.documentElement.style.setProperty('--modal-background', settings.modalBackground);
    if (settings.modalBackgroundImage) {
      document.documentElement.style.setProperty('--modal-background-image', `url(${settings.modalBackgroundImage})`);
      document.documentElement.style.setProperty('--modal-background-image-opacity', settings.modalBackgroundImageOpacity);
    } else {
      document.documentElement.style.setProperty('--modal-background-image', 'none');
    }
    
    // Применяем стили сайдбара
    const useHeaderBgForButtons = settings.sidebarUseHeaderBackgroundForButtons;
    const buttonBg = useHeaderBgForButtons
      ? (settings.sidebarHeaderBackgroundImage
        ? `url(${settings.sidebarHeaderBackgroundImage})`
        : settings.sidebarHeaderBackground)
      : (settings.sidebarButtonBackgroundImage
        ? `url(${settings.sidebarButtonBackgroundImage})`
        : settings.sidebarButtonBackground);
    document.documentElement.style.setProperty('--sidebar-button-background', buttonBg);
    const buttonOpacity = useHeaderBgForButtons && settings.sidebarHeaderBackgroundImage
      ? (settings.sidebarHeaderBackgroundImageOpacity || 1)
      : settings.sidebarButtonBackgroundImageOpacity;
    document.documentElement.style.setProperty('--sidebar-button-background-opacity', buttonOpacity);
    document.documentElement.style.setProperty('--sidebar-text-color', settings.sidebarTextColor);
    document.documentElement.style.setProperty('--sidebar-text-size', settings.sidebarTextSize);
    document.documentElement.style.setProperty('--sidebar-text-align', settings.sidebarTextAlign);
    document.documentElement.style.setProperty('--sidebar-font-family', settings.sidebarFontFamily);
  }, [open, settings]);
  
  useEffect(() => {
    if (!open || !settings) return;
    applyFrontendSettings(settings);
    window.dispatchEvent(new CustomEvent('frontendSettingsUpdated', { detail: { settings } }));
  }, [open, settings]);

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Пожалуйста, выберите изображение');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('⚠️ Размер файла не должен превышать 5 МБ\n\nИспользуйте изображение меньшего размера или сожмите его перед загрузкой.');
      return;
    }

    try {
      const base64 = await loadImageAsBase64(file);
      handleChange('mainBackgroundImage', base64);
      // Автоматически включаем "Только изображение" при загрузке нового изображения
      if (!settings.mainBackgroundUseImageOnly) {
        handleChange('mainBackgroundUseImageOnly', true);
      }
      console.log('✅ [FrontendSettingsModal] Изображение главного меню загружено, useImageOnly установлен в true');
      
      // Проверяем размер localStorage после загрузки
      const storageInfo = checkLocalStorageSize();
      if (storageInfo.percentUsed > 80) {
        alert(
          `⚠️ ВНИМАНИЕ: Память браузера заполнена на ${storageInfo.percentUsed.toFixed(0)}%\n\n` +
          `Используется: ${storageInfo.totalMB.toFixed(2)} MB из ~5 MB\n\n` +
          `💡 Рекомендация:\n` +
          `- Удалите ненужные изображения\n` +
          `- Или используйте изображения меньшего размера\n\n` +
          `⚠️ При превышении лимита настройки не сохранятся!`
        );
      }
    } catch (error) {
      console.error('Ошибка загрузки изображения:', error);
      alert('❌ Ошибка загрузки изображения: ' + error.message);
    }
  };

  const handleSave = async () => {
    try {
      // Проверяем размер перед сохранением
      const storageInfo = checkLocalStorageSize();
      console.log(`📊 localStorage используется: ${storageInfo.totalMB.toFixed(2)} MB (${storageInfo.percentUsed.toFixed(1)}%)`);
      
      // Пытаемся сохранить локально
      console.log('💾 [FrontendSettingsModal] Сохранение настроек...', {
        hasSettings: !!settings,
        hasModals: !!settings?.modals,
        hasCalendar: !!settings?.modals?.calendar,
        calendarHasImage: !!settings?.modals?.calendar?.backgroundImage,
        imageLength: settings?.modals?.calendar?.backgroundImage?.length || 0
      });
      
      localStorage.setItem('frontendSettings', JSON.stringify(settings));
      
      // Проверяем, что настройки действительно сохранились
      const saved = localStorage.getItem('frontendSettings');
      const parsed = saved ? JSON.parse(saved) : null;
      console.log('✅ [FrontendSettingsModal] Настройки сохранены, проверка:', {
        saved: !!saved,
        hasCalendar: !!parsed?.modals?.calendar,
        calendarHasImage: !!parsed?.modals?.calendar?.backgroundImage
      });
      
      // Отправляем кастомное событие для обновления в той же вкладке (с настройками, чтобы модалки вроде ВПР сразу подхватили цвета)
      window.dispatchEvent(new CustomEvent('frontendSettingsUpdated', { detail: { settings } }));
      
      // Применяем настройки глобально через утилиту
      applyFrontendSettings(settings);
      
      // Сохраняем на сервер для синхронизации между всеми устройствами
      try {
        console.log('📤 Отправка настроек фронтенда на сервер...');
        console.log('📦 Settings preview:', JSON.stringify(settings).substring(0, 200));
        const response = await fetch('/api/frontend-settings', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ settings })
        });
        
        console.log('📥 Ответ сервера - статус:', response.status, response.statusText);
        const result = await response.json();
        console.log('📥 Ответ сервера - результат:', result);
        
        if (response.ok && result.success) {
          console.log('✅ Настройки фронтенда сохранены на сервер и синхронизированы со всеми устройствами');
          
          // Сохраняем настройки видимости кнопок сайтбара, если есть доступ
          if (canManageVisibility() && Object.keys(sidebarVisibility).length > 0) {
            try {
              const visibilityResponse = await fetch('/api/sidebar-visibility', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ visibility: sidebarVisibility })
              });
              
              const visibilityResult = await visibilityResponse.json();
              if (visibilityResponse.ok && visibilityResult.success) {
                console.log('✅ Настройки видимости кнопок сайтбара сохранены');
                console.log('📦 Сохраненные настройки:', JSON.stringify(sidebarVisibility).substring(0, 300));
                // WebSocket событие отправляется сервером, не нужно отправлять с клиента
              } else {
                console.error('❌ Ошибка сохранения настроек видимости:', visibilityResult.error);
                alert('⚠️ Не удалось сохранить настройки видимости: ' + (visibilityResult.error || 'Неизвестная ошибка'));
              }
            } catch (error) {
              console.warn('⚠️ Не удалось сохранить настройки видимости:', error);
            }
          }
          
          setHasChanges(false);
          alert('✅ Настройки сохранены и синхронизированы со всеми устройствами!');
        } else {
          console.warn('⚠️ Настройки применены локально, но не удалось синхронизировать с сервером:', result.error);
          setHasChanges(false);
          alert('✅ Настройки сохранены локально!\n\n⚠️ Синхронизация с сервером не удалась. Настройки будут применены только на этом устройстве.');
        }
      } catch (syncError) {
        console.error('⚠️ Ошибка синхронизации с сервером:', syncError);
        setHasChanges(false);
        alert('✅ Настройки сохранены локально!\n\n⚠️ Синхронизация с сервером не удалась. Настройки будут применены только на этом устройстве.');
      }
    } catch (error) {
      console.error('Ошибка сохранения:', error);
      
      if (error.name === 'QuotaExceededError') {
        const hasImages = settings.mainBackgroundImage || settings.modalBackgroundImage || settings.sidebarButtonBackgroundImage;
        
        if (hasImages) {
          alert(
            '❌ ОШИБКА: Недостаточно места!\n\n' +
            '🖼️ Загруженные изображения слишком большие для сохранения в браузере.\n\n' +
            '💡 РЕШЕНИЕ:\n' +
            '1. Удалите некоторые изображения (нажмите 🗑️)\n' +
            '2. Или используйте изображения меньшего размера\n' +
            '3. Или используйте только цвета без изображений\n\n' +
            '⚠️ Настройки НЕ СОХРАНЕНЫ!'
          );
        } else {
          alert(
            '❌ ОШИБКА: Недостаточно места в браузере!\n\n' +
            'localStorage заполнен. Очистите данные браузера или используйте режим инкогнито.'
          );
        }
      } else {
        alert('❌ Ошибка сохранения настроек: ' + error.message);
      }
    }
  };

  const refreshPresets = async () => {
    try {
      const response = await fetch('/api/frontend-presets', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.presets) {
          console.log(`📥 Загружено ${result.presets.length} пресетов`);
          setPresets(result.presets);
        }
      }
    } catch (error) {
      console.warn('⚠️ Не удалось загрузить пресеты:', error);
    }
  };

  const safePresetFileName = (name) =>
    String(name || 'preset').replace(/[\\/:*?"<>|]+/g, '_').trim() || 'preset';

  const handleDownloadPreset = (preset) => {
    if (!preset?.settings) {
      alert('⚠️ У пресета нет настроек для экспорта');
      return;
    }
    const exportData = {
      type: 'frontend-preset',
      version: 1,
      name: preset.name,
      description: preset.description || null,
      settings: preset.settings,
      exportedAt: new Date().toISOString()
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${safePresetFileName(preset.name)}.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleImportPreset = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      if (file.size > 5 * 1024 * 1024) {
        alert('⚠️ Размер файла не должен превышать 5 МБ');
        return;
      }
      const text = await file.text();
      const parsed = JSON.parse(text);
      const detectedSettings = parsed?.settings || parsed?.preset || parsed;
      if (!detectedSettings || typeof detectedSettings !== 'object') {
        throw new Error('Неверный формат пресета');
      }
      const nameFromFile = parsed?.name || file.name.replace(/\.json$/i, '') || 'Импортированный пресет';
      const descriptionFromFile = parsed?.description || null;
      const response = await fetch('/api/frontend-presets', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          name: nameFromFile.trim() || 'Импортированный пресет',
          description: descriptionFromFile,
          settings: detectedSettings
        })
      });
      const result = await response.json();
      if (response.ok && result.success) {
        alert(`✅ Пресет "${nameFromFile}" загружен!`);
        await refreshPresets();
      } else {
        alert(`❌ Ошибка: ${result.error || 'Не удалось загрузить пресет'}`);
      }
    } catch (error) {
      console.error('Ошибка импорта пресета:', error);
      alert('❌ Ошибка импорта пресета: неверный файл');
    } finally {
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  // Сохранить текущие настройки как пресет
  const handleSavePreset = async () => {
    if (!presetName.trim()) {
      alert('⚠️ Введите название пресета!');
      return;
    }

    try {
      const presetSettings = {
        ...settings,
        ...(Object.keys(sidebarVisibility || {}).length > 0
          ? { sidebarVisibility: { ...sidebarVisibility } }
          : {})
      };
      const response = await fetch('/api/frontend-presets', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          name: presetName.trim(),
          description: presetDescription.trim() || null,
          settings: presetSettings
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        console.log('✅ Пресет сохранен:', presetName);
        alert(`✅ Пресет "${presetName}" сохранен!`);
        
        // Обновляем список пресетов
        await refreshPresets();
        
        // Очищаем форму
        setPresetName('');
        setPresetDescription('');
        setShowPresetDialog(false);
      } else {
        alert(`❌ Ошибка: ${result.error || 'Не удалось сохранить пресет'}`);
      }
    } catch (error) {
      console.error('Ошибка сохранения пресета:', error);
      alert('❌ Ошибка сохранения пресета');
    }
  };

  // Применить пресет
  const handleApplyPreset = (preset) => {
    if (!window.confirm(`Применить пресет "${preset.name}"?\n\nТекущие несохраненные изменения будут потеряны.`)) {
      return;
    }
    
    const presetSettings = preset.settings || {};
    const { sidebarVisibility: presetSidebarVisibility, ...restSettings } = presetSettings;
    setSettings(restSettings);
    // Применяем CSS переменные сразу для мгновенного отображения на всех устройствах
    applyFrontendSettings(restSettings);
    if (presetSidebarVisibility && typeof presetSidebarVisibility === 'object') {
      setSidebarVisibility(presetSidebarVisibility);
    }
    setHasChanges(true);
    alert(`✅ Пресет "${preset.name}" применен!\n\nНе забудьте нажать "Сохранить" для сохранения изменений.`);
  };

  // Удалить пресет
  const handleDeletePreset = async (preset) => {
    if (!window.confirm(`⚠️ Удалить пресет "${preset.name}"?\n\nЭто действие нельзя отменить!`)) {
      return;
    }

    try {
      const response = await fetch(`/api/frontend-presets/${preset.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const result = await response.json();

      if (response.ok && result.success) {
        console.log('✅ Пресет удален:', preset.name);
        alert(`✅ Пресет "${preset.name}" удален!`);
        
        // Обновляем список пресетов
        await refreshPresets();
      } else {
        alert(`❌ Ошибка: ${result.error || 'Не удалось удалить пресет'}`);
      }
    } catch (error) {
      console.error('Ошибка удаления пресета:', error);
      alert('❌ Ошибка удаления пресета');
    }
  };

  const handleReset = async () => {
    if (!window.confirm('⚠️ Вы уверены, что хотите сбросить ВСЕ настройки к значениям по умолчанию?\n\nЭто действие нельзя отменить!')) {
      return;
    }
    
    try {
      // Удаляем настройки с сервера
      const response = await fetch('/api/frontend-settings', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        let errorMessage = 'Ошибка удаления настроек с сервера';
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          try {
            const error = await response.json();
            errorMessage = error?.error || errorMessage;
          } catch (e) {
            console.warn('⚠️ Не удалось прочитать JSON ошибку сервера при сбросе:', e);
          }
        } else {
          try {
            const text = await response.text();
            if (text) {
              errorMessage = text.slice(0, 200);
            }
          } catch (e) {
            console.warn('⚠️ Не удалось прочитать текст ошибки сервера при сбросе:', e);
          }
        }
        console.warn('⚠️ Сброс на сервере не выполнен:', errorMessage);
      } else {
        console.log('✅ Настройки удалены с сервера');
      }
      
      // Удаляем все сохраненные настройки из localStorage
      localStorage.removeItem('frontendSettings');
      
      // Применяем дефолтные настройки
      setSettings(DEFAULT_SETTINGS);
      
      // Применяем дефолтные CSS переменные для всех модалок (включая мобильные)
      applyFrontendSettings(DEFAULT_SETTINGS);
      
      setHasChanges(false);
      
      // Уведомление и перезагрузка страницы для полного обновления
      alert('✅ Настройки сброшены к дефолтным значениям!\n\nСтраница будет перезагружена.');
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (error) {
      console.error('Ошибка сброса настроек:', error);
      alert(`❌ Ошибка при сбросе настроек: ${error.message}`);
    }
  };

  if (!open) return null;

  return (
    <>
      <style>{`
        /* Стилизация скроллбара для вкладок */
        .frontend-settings-tabs-container::-webkit-scrollbar {
          height: 10px;
        }
        .frontend-settings-tabs-container::-webkit-scrollbar-track {
          background: transparent;
          margin: 0 8px;
        }
        .frontend-settings-tabs-container::-webkit-scrollbar-thumb {
          background: rgba(75, 85, 99, 0.5);
          border-radius: 5px;
        }
        .frontend-settings-tabs-container::-webkit-scrollbar-thumb:hover {
          background: rgba(75, 85, 99, 0.7);
        }
        /* Для Firefox */
        .frontend-settings-tabs-container {
          scrollbar-width: thin;
          scrollbar-color: rgba(75, 85, 99, 0.5) transparent;
        }
      `}</style>
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: isMobile ? 0 : 320,
        right: 0,
        bottom: 0,
        width: isMobile ? '100vw' : 'calc(100vw - 320px)',
        height: '100vh',
        background: 'rgba(0,0,0,0.55)',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        zIndex: 200003
      }}
      onClick={onClose}
    >
      <div
        className="frontend-settings-modal"
        style={{
          background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          border: 'none',
          color: 'var(--modal-default-text-color, #e2e8f0)',
          boxSizing: 'border-box'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Заголовок */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 24px',
            borderBottom: '1px solid rgba(75, 85, 99, 0.3)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FiMonitor size={24} color="#60a5fa" />
            <h2 style={{ margin: 0, fontSize: '1.5em', fontWeight: 700, color: '#60a5fa' }}>
              Настройки фронтенда
            </h2>
          </div>
          <div style={{ position: 'relative', width: 52, flexShrink: 0 }}>
            <ModalCloseButton onClick={onClose} aria-label="Закрыть" />
          </div>
        </div>

        {/* Вкладки */}
        <div 
          className="frontend-settings-tabs-container"
          style={{ 
            display: 'flex', 
            overflowX: 'auto', 
            overflowY: 'visible',
            borderBottom: '1px solid rgba(75, 85, 99, 0.3)',
            backgroundColor: '#111827',
            padding: '0 8px',
            paddingTop: '8px',
            paddingBottom: '12px',
            position: 'relative',
            minHeight: '64px'
          }}
          onScroll={(e) => {
            e.stopPropagation();
          }}
        >
          {TABS.filter(tab => {
            // Фильтруем вкладки с ограниченным доступом
            if (tab.restricted && !canManageVisibility()) {
              return false;
            }
            return true;
          }).map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '14px 20px',
                  border: 'none',
                  background: isActive ? 'linear-gradient(135deg, #1f2937 0%, #374151 100%)' : 'rgba(75, 85, 99, 0.2)',
                  color: isActive ? '#60a5fa' : '#e5e7eb',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: isActive ? 700 : 500,
                  borderBottom: isActive ? '3px solid #60a5fa' : '3px solid transparent',
                  borderTop: isActive ? '1px solid rgba(96, 165, 250, 0.3)' : '1px solid transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s ease',
                  marginTop: '4px',
                  borderRadius: '8px 8px 0 0',
                  boxShadow: isActive ? '0 -2px 8px rgba(96, 165, 250, 0.2)' : 'none',
                  position: 'relative',
                  zIndex: isActive ? 1 : 0
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = 'rgba(75, 85, 99, 0.35)';
                    e.currentTarget.style.color = '#f3f4f6';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = 'rgba(75, 85, 99, 0.25)';
                    e.currentTarget.style.color = '#f3f4f6';
                  }
                }}
              >
                <Icon size={18} style={{ opacity: isActive ? 1 : 0.9 }} />
                {tab.name}
              </button>
            );
          })}
        </div>

        {/* Контент */}
        <div style={{ flex: 1, overflow: 'auto', padding: '24px', display: 'flex', gap: '24px' }}>
          {/* Вкладка: Основная */}
          {activeTab === 'primary' && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <Section 
                title="Фон всех модальных окон" 
                icon={<FiMonitor size={18} />}
                expanded={expandedSections.modalBackground}
                onToggle={() => toggleSection('modalBackground')}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <input
                      type="checkbox"
                      checked={settings.applyGlobalModalBackground !== false}
                      onChange={(e) => handleChange('applyGlobalModalBackground', e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span style={{ color: '#d1d5db', fontSize: '14px' }}>
                      Применять фон ко всем модалкам
                    </span>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                      Цвет фона модалок
                    </label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        type="color"
                        value={rgbaToHex(settings.modalBackground)}
                        onChange={(e) => handleChange('modalBackground', e.target.value)}
                        style={{
                          width: '60px',
                          height: '36px',
                          border: '1px solid rgba(75, 85, 99, 0.5)',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                      />
                      <input
                        type="text"
                        value={settings.modalBackground}
                        onChange={(e) => handleChange('modalBackground', e.target.value)}
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: '1px solid rgba(75, 85, 99, 0.5)',
                          background: '#111827',
                          color: '#fff',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 500, color: '#9ca3af' }}>
                      Готовые градиенты (светлые тона)
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
                      {[
                        'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                        'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                        'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)',
                        'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
                        'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
                        'linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%)',
                        'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                        'linear-gradient(135deg, #fefce8 0%, #fef9c3 100%)',
                        'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
                        'linear-gradient(135deg, #ecfeff 0%, #cffafe 100%)'
                      ].map((gradient, index) => {
                        const isSelected = settings.modalBackground === gradient ||
                          (settings.modalBackground.includes('gradient') && settings.modalBackground === gradient);
                        return (
                          <div
                            key={index}
                            onClick={() => {
                              handleChange('modalBackground', gradient);
                              if (settings.modalBackgroundImage) {
                                handleChange('modalBackgroundImage', '');
                              }
                            }}
                            style={{
                              height: '40px',
                              background: gradient,
                              borderRadius: '6px',
                              cursor: 'pointer',
                              border: isSelected ? '3px solid #60a5fa' : '2px solid rgba(75, 85, 99, 0.3)',
                              transition: 'all 0.2s ease',
                              boxShadow: isSelected ? '0 0 0 2px rgba(96, 165, 250, 0.3)' : 'none'
                            }}
                            title={`Градиент ${index + 1}`}
                          />
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 500, color: '#9ca3af' }}>
                      Готовые градиенты (темные тона)
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
                      {[
                        'linear-gradient(135deg, #0f172a 0%, #1f2937 100%)',
                        'linear-gradient(135deg, #111827 0%, #0f172a 100%)',
                        'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
                        'linear-gradient(135deg, #0b1020 0%, #111827 100%)',
                        'linear-gradient(135deg, #1c1917 0%, #0f172a 100%)',
                        'linear-gradient(135deg, #0f172a 0%, #0b132b 100%)',
                        'linear-gradient(135deg, #111827 0%, #1f2937 100%)',
                        'linear-gradient(135deg, #0b1320 0%, #1e293b 100%)',
                        'linear-gradient(135deg, #1a1b1f 0%, #0f172a 100%)',
                        'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)'
                      ].map((gradient, index) => {
                        const isSelected = settings.modalBackground === gradient ||
                          (settings.modalBackground.includes('gradient') && settings.modalBackground === gradient);
                        return (
                          <div
                            key={index}
                            onClick={() => {
                              handleChange('modalBackground', gradient);
                              if (settings.modalBackgroundImage) {
                                handleChange('modalBackgroundImage', '');
                              }
                            }}
                            style={{
                              height: '40px',
                              background: gradient,
                              borderRadius: '6px',
                              cursor: 'pointer',
                              border: isSelected ? '3px solid #60a5fa' : '2px solid rgba(75, 85, 99, 0.3)',
                              transition: 'all 0.2s ease',
                              boxShadow: isSelected ? '0 0 0 2px rgba(96, 165, 250, 0.3)' : 'none'
                            }}
                            title={`Градиент ${index + 1}`}
                          />
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 500, color: '#9ca3af' }}>
                      Готовые градиенты (цветные тона)
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
                      {[
                        'linear-gradient(135deg, #ff6b6b 0%, #f9a8d4 100%)',
                        'linear-gradient(135deg, #f97316 0%, #facc15 100%)',
                        'linear-gradient(135deg, #22c55e 0%, #14b8a6 100%)',
                        'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
                        'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                        'linear-gradient(135deg, #ec4899 0%, #f97316 100%)',
                        'linear-gradient(135deg, #10b981 0%, #84cc16 100%)',
                        'linear-gradient(135deg, #0ea5e9 0%, #22d3ee 100%)',
                        'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
                        'linear-gradient(135deg, #8b5cf6 0%, #06b6d4 100%)'
                      ].map((gradient, index) => {
                        const isSelected = settings.modalBackground === gradient ||
                          (settings.modalBackground.includes('gradient') && settings.modalBackground === gradient);
                        return (
                          <div
                            key={index}
                            onClick={() => {
                              handleChange('modalBackground', gradient);
                              if (settings.modalBackgroundImage) {
                                handleChange('modalBackgroundImage', '');
                              }
                            }}
                            style={{
                              height: '40px',
                              background: gradient,
                              borderRadius: '6px',
                              cursor: 'pointer',
                              border: isSelected ? '3px solid #60a5fa' : '2px solid rgba(75, 85, 99, 0.3)',
                              transition: 'all 0.2s ease',
                              boxShadow: isSelected ? '0 0 0 2px rgba(96, 165, 250, 0.3)' : 'none'
                            }}
                            title={`Градиент ${index + 1}`}
                          />
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                      Изображение фона
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (!file.type.startsWith('image/')) {
                          alert('Пожалуйста, выберите изображение');
                          return;
                        }
                        if (file.size > 5 * 1024 * 1024) {
                          alert('⚠️ Размер файла не должен превышать 5 МБ\n\nИспользуйте изображение меньшего размера или сожмите его перед загрузкой.');
                          return;
                        }
                        try {
                          const base64 = await loadImageAsBase64(file);
                          handleChange('modalBackgroundImage', base64);
                        } catch (error) {
                          console.error('Ошибка загрузки изображения:', error);
                          alert('❌ Ошибка загрузки изображения: ' + error.message);
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid rgba(75, 85, 99, 0.5)',
                        borderRadius: '8px',
                        backgroundColor: '#1f2937',
                        color: '#d1d5db',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    />
                    {settings.modalBackgroundImage && (
                      <button
                        onClick={() => handleChange('modalBackgroundImage', '')}
                        style={{
                          marginTop: '8px',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: '1px solid rgba(239, 68, 68, 0.5)',
                          background: 'rgba(239, 68, 68, 0.1)',
                          color: '#ef4444',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: 500
                        }}
                      >
                        Удалить изображение
                      </button>
                    )}
                  </div>

                  {settings.modalBackgroundImage && (
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>
                        Прозрачность изображения: {Math.round((settings.modalBackgroundImageOpacity || 1) * 100)}%
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={settings.modalBackgroundImageOpacity || 1}
                        onChange={(e) => handleChange('modalBackgroundImageOpacity', parseFloat(e.target.value))}
                        style={{ width: '100%', cursor: 'pointer' }}
                      />
                    </div>
                  )}
                </div>
              </Section>
              <Section
                title="Глобальные стили модалок"
                icon={<FiType size={18} />}
                expanded={expandedSections.globalModalStyles}
                onToggle={() => toggleSection('globalModalStyles')}
              >
                <div style={{
                  border: '2px solid #22c55e',
                  borderRadius: '12px',
                  padding: '16px',
                  background: 'rgba(34, 197, 94, 0.08)',
                  boxShadow: '0 0 0 2px rgba(34, 197, 94, 0.2)',
                  marginBottom: '12px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <FiCheckSquare size={18} color="#22c55e" />
                    <span style={{ color: '#22c55e', fontWeight: 700 }}>Глобальные стили модалок</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Цвет текста</label>
                    <input type="color" value={rgbaToHex(settings.modalTextColor)} onChange={(e) => handleChange('modalTextColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Цвет заголовков</label>
                    <input type="color" value={rgbaToHex(settings.modalHeaderTextColor)} onChange={(e) => handleChange('modalHeaderTextColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Цвет подписей полей</label>
                    <input type="color" value={rgbaToHex(settings.modalFieldLabelTextColor)} onChange={(e) => handleChange('modalFieldLabelTextColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Цвет дат/времени</label>
                    <input type="color" value={rgbaToHex(settings.modalDateTextColor)} onChange={(e) => handleChange('modalDateTextColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Цвет бордера</label>
                    <input type="color" value={rgbaToHex(settings.modalBorderColor)} onChange={(e) => handleChange('modalBorderColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Фон кнопок</label>
                    <input type="color" value={rgbaToHex(settings.modalButtonBackground)} onChange={(e) => handleChange('modalButtonBackground', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Текст кнопок</label>
                    <input type="color" value={rgbaToHex(settings.modalButtonTextColor)} onChange={(e) => handleChange('modalButtonTextColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Цвет полей</label>
                    <input type="color" value={rgbaToHex(settings.modalInputBackground)} onChange={(e) => handleChange('modalInputBackground', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Текст полей</label>
                    <input type="color" value={rgbaToHex(settings.modalInputTextColor)} onChange={(e) => handleChange('modalInputTextColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Фон фильтров</label>
                    <input type="color" value={rgbaToHex(settings.modalFilterBackground)} onChange={(e) => handleChange('modalFilterBackground', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Текст фильтров</label>
                    <input type="color" value={rgbaToHex(settings.modalFilterTextColor)} onChange={(e) => handleChange('modalFilterTextColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Бордер фильтров</label>
                    <input type="color" value={rgbaToHex(settings.modalFilterBorderColor)} onChange={(e) => handleChange('modalFilterBorderColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Фон блоков отчетов</label>
                    <input type="color" value={rgbaToHex(settings.modalReportBlockBackground)} onChange={(e) => handleChange('modalReportBlockBackground', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <div
                      style={{
                        border: '2px solid rgba(34, 197, 94, 0.8)',
                        borderRadius: '12px',
                        padding: '16px',
                        background: 'rgba(34, 197, 94, 0.08)',
                        boxShadow: '0 0 12px rgba(34, 197, 94, 0.15)'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <span style={{ color: '#22c55e', fontWeight: 700 }}>Глобальные кнопки в модалках</span>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#e5e7eb', fontSize: '14px' }}>
                          <input
                            type="checkbox"
                            checked={settings.enableGlobalActionButtons !== false}
                            onChange={(e) => handleChange('enableGlobalActionButtons', e.target.checked)}
                          />
                          Включить глобальные кнопки
                        </label>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '16px' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Фон кнопок (глобально)</label>
                          <input type="color" value={rgbaToHex(settings.modalActionButtonBackground)} onChange={(e) => handleChange('modalActionButtonBackground', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Текст кнопок (глобально)</label>
                          <input type="color" value={rgbaToHex(settings.modalActionButtonTextColor)} onChange={(e) => handleChange('modalActionButtonTextColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Бордер кнопок (глобально)</label>
                          <input type="color" value={rgbaToHex(settings.modalActionButtonBorderColor)} onChange={(e) => handleChange('modalActionButtonBorderColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Кнопки Открыть/Добавить — фон</label>
                          <input type="color" value={rgbaToHex(settings.modalPrimaryActionButtonBackground)} onChange={(e) => handleChange('modalPrimaryActionButtonBackground', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Кнопки Открыть/Добавить — текст</label>
                          <input type="color" value={rgbaToHex(settings.modalPrimaryActionButtonTextColor)} onChange={(e) => handleChange('modalPrimaryActionButtonTextColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Кнопки Открыть/Добавить — бордер</label>
                          <input type="color" value={rgbaToHex(settings.modalPrimaryActionButtonBorderColor)} onChange={(e) => handleChange('modalPrimaryActionButtonBorderColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Кнопки Удалить/Очистить — фон</label>
                          <input type="color" value={rgbaToHex(settings.modalDangerActionButtonBackground)} onChange={(e) => handleChange('modalDangerActionButtonBackground', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Кнопки Удалить/Очистить — текст</label>
                          <input type="color" value={rgbaToHex(settings.modalDangerActionButtonTextColor)} onChange={(e) => handleChange('modalDangerActionButtonTextColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Кнопки Удалить/Очистить — бордер</label>
                          <input type="color" value={rgbaToHex(settings.modalDangerActionButtonBorderColor)} onChange={(e) => handleChange('modalDangerActionButtonBorderColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                        </div>
                      </div>
                    </div>
                  </div>
                  </div>
                </div>
              </Section>
              <Section
                title="Стили таблицы и поле дедлайн"
                icon={<FiLayout size={18} />}
                expanded={expandedSections.tableAndDeadlineStyles}
                onToggle={() => toggleSection('tableAndDeadlineStyles')}
              >
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Ячейки таблицы — фон (обычное)</label>
                    <input type="color" value={rgbaToHex(settings.tableCellBackground)} onChange={(e) => handleChange('tableCellBackground', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Ячейки таблицы — текст (обычное)</label>
                    <input type="color" value={rgbaToHex(settings.tableCellTextColor)} onChange={(e) => handleChange('tableCellTextColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Ячейки таблицы — фон (при наведении)</label>
                    <input type="color" value={rgbaToHex(settings.tableCellHoverBackground)} onChange={(e) => handleChange('tableCellHoverBackground', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Ячейки таблицы — текст (при наведении)</label>
                    <input type="color" value={rgbaToHex(settings.tableCellHoverTextColor)} onChange={(e) => handleChange('tableCellHoverTextColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Поле «Дедлайн» (текст с часиками)</label>
                    <input type="color" value={rgbaToHex(settings.deadlineFieldTextColor)} onChange={(e) => handleChange('deadlineFieldTextColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                  </div>
                </div>
              </Section>
              {renderPresetsSection()}
            </div>
          )}

          {activeTab === 'frontend' && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <Section
                title="Настройки модалки фронтенда"
                icon={<FiMonitor size={18} />}
                expanded={true}
              >
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Цвет текста</label>
                    <input type="color" value={rgbaToHex(settings.frontendSettingsModalTextColor)} onChange={(e) => handleChange('frontendSettingsModalTextColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                  </div>
                </div>
              </Section>
            </div>
          )}

          {activeTab === 'clock' && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <Section
                title="Верхний блок времени"
                icon={<FiClock size={18} />}
                expanded={expandedSections.clockBlock}
                onToggle={() => toggleSection('clockBlock')}
              >
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Фон блока</label>
                    <input type="color" value={rgbaToHex(settings.clockBackground)} onChange={(e) => handleChange('clockBackground', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Цвет бордера</label>
                    <input type="color" value={rgbaToHex(settings.clockBorderColor)} onChange={(e) => handleChange('clockBorderColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Цвет времени</label>
                    <input type="color" value={rgbaToHex(settings.clockTimeColor)} onChange={(e) => handleChange('clockTimeColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Цвет дня недели</label>
                    <input type="color" value={rgbaToHex(settings.clockWeekdayColor)} onChange={(e) => handleChange('clockWeekdayColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Цвет текста месяца</label>
                    <input type="color" value={rgbaToHex(settings.clockMonthColor)} onChange={(e) => handleChange('clockMonthColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>
                      Сдвиг по X ({settings.clockOffsetX || 0}px)
                    </label>
                    <input
                      type="range"
                      min="-300"
                      max="300"
                      step="1"
                      value={settings.clockOffsetX || 0}
                      onChange={(e) => handleChange('clockOffsetX', parseInt(e.target.value, 10))}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>
                      Сдвиг по Y ({settings.clockOffsetY || 0}px)
                    </label>
                    <input
                      type="range"
                      min="-300"
                      max="300"
                      step="1"
                      value={settings.clockOffsetY || 0}
                      onChange={(e) => handleChange('clockOffsetY', parseInt(e.target.value, 10))}
                      style={{ width: '100%' }}
                    />
                  </div>
                </div>
              </Section>
            </div>
          )}

          {/* Вкладка: Главное меню */}
          {activeTab === 'main-menu' && (
            <>
          {/* Левая колонка - настройки */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '24px' }}>
            
            {renderPresetsSection()}
            
            {/* Основной фон */}
            <Section 
              title="Основной фон" 
              icon={<FiImage size={18} />}
              expanded={expandedSections.mainBackground}
              onToggle={() => toggleSection('mainBackground')}
            >
              <SettingRow label="Цвет фона">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={rgbaToHex(settings.mainBackground)}
                      onChange={(e) => handleChange('mainBackground', e.target.value)}
                      disabled={settings.mainBackgroundUseImageOnly && !!settings.mainBackgroundImage}
                      style={{
                        width: '60px',
                        height: '36px',
                        border: '1px solid rgba(75, 85, 99, 0.5)',
                        borderRadius: '6px',
                        cursor: (settings.mainBackgroundUseImageOnly && !!settings.mainBackgroundImage) ? 'not-allowed' : 'pointer',
                        opacity: (settings.mainBackgroundUseImageOnly && !!settings.mainBackgroundImage) ? 0.5 : 1
                      }}
                    />
                    <input
                      type="text"
                      value={settings.mainBackground}
                      onChange={(e) => handleChange('mainBackground', e.target.value)}
                      disabled={settings.mainBackgroundUseImageOnly && !!settings.mainBackgroundImage}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: '1px solid rgba(75, 85, 99, 0.5)',
                        background: '#111827',
                        color: '#fff',
                        fontSize: '14px',
                        opacity: (settings.mainBackgroundUseImageOnly && !!settings.mainBackgroundImage) ? 0.5 : 1
                      }}
                    />
                    {settings.mainBackgroundImage && (
                      <label style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px', 
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: '#d1d5db',
                        whiteSpace: 'nowrap'
                      }}>
                        <input
                          type="checkbox"
                          checked={settings.mainBackgroundUseImageOnly || false}
                          onChange={(e) => handleChange('mainBackgroundUseImageOnly', e.target.checked)}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <span>Только изображение</span>
                      </label>
                    )}
                  </div>
                  
                  {/* Набор градиентов светлых тонов */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 500, color: '#9ca3af' }}>
                      Готовые градиенты (светлые тона)
                    </label>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(5, 1fr)', 
                      gap: '8px'
                    }}>
                      {[
                        'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', // Небесно-голубой
                        'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', // Светло-желтый
                        'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)', // Розовый
                        'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)', // Лавандовый
                        'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)', // Мятный
                        'linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%)', // Персиковый
                        'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', // Светло-зеленый
                        'linear-gradient(135deg, #fefce8 0%, #fef9c3 100%)', // Лимонный
                        'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)', // Фиолетовый
                        'linear-gradient(135deg, #ecfeff 0%, #cffafe 100%)'  // Бирюзовый
                      ].map((gradient, index) => {
                        const isSelected = settings.mainBackground === gradient || 
                          (settings.mainBackground.includes('gradient') && settings.mainBackground === gradient);
                        return (
                          <div
                            key={index}
                            onClick={() => {
                              handleChange('mainBackground', gradient);
                              // Сбрасываем изображение при выборе градиента
                              if (settings.mainBackgroundImage) {
                                handleChange('mainBackgroundImage', '');
                              }
                            }}
                            style={{
                              height: '40px',
                              background: gradient,
                              borderRadius: '6px',
                              cursor: 'pointer',
                              border: isSelected ? '3px solid #60a5fa' : '2px solid rgba(75, 85, 99, 0.3)',
                              transition: 'all 0.2s ease',
                              boxShadow: isSelected ? '0 0 0 2px rgba(96, 165, 250, 0.3)' : 'none'
                            }}
                            title={`Градиент ${index + 1}`}
                          />
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 500, color: '#9ca3af' }}>
                      Готовые градиенты (темные тона)
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
                      {[
                        'linear-gradient(135deg, #0f172a 0%, #1f2937 100%)',
                        'linear-gradient(135deg, #111827 0%, #0f172a 100%)',
                        'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
                        'linear-gradient(135deg, #0b1020 0%, #111827 100%)',
                        'linear-gradient(135deg, #1c1917 0%, #0f172a 100%)',
                        'linear-gradient(135deg, #0f172a 0%, #0b132b 100%)',
                        'linear-gradient(135deg, #111827 0%, #1f2937 100%)',
                        'linear-gradient(135deg, #0b1320 0%, #1e293b 100%)',
                        'linear-gradient(135deg, #1a1b1f 0%, #0f172a 100%)',
                        'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)'
                      ].map((gradient, index) => {
                        const isSelected = settings.mainBackground === gradient ||
                          (settings.mainBackground.includes('gradient') && settings.mainBackground === gradient);
                        return (
                          <div
                            key={index}
                            onClick={() => {
                              handleChange('mainBackground', gradient);
                              if (settings.mainBackgroundImage) {
                                handleChange('mainBackgroundImage', '');
                              }
                            }}
                            style={{
                              height: '40px',
                              background: gradient,
                              borderRadius: '6px',
                              cursor: 'pointer',
                              border: isSelected ? '3px solid #60a5fa' : '2px solid rgba(75, 85, 99, 0.3)',
                              transition: 'all 0.2s ease',
                              boxShadow: isSelected ? '0 0 0 2px rgba(96, 165, 250, 0.3)' : 'none'
                            }}
                            title={`Градиент ${index + 1}`}
                          />
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 500, color: '#9ca3af' }}>
                      Готовые градиенты (цветные тона)
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
                      {[
                        'linear-gradient(135deg, #ff6b6b 0%, #f9a8d4 100%)',
                        'linear-gradient(135deg, #f97316 0%, #facc15 100%)',
                        'linear-gradient(135deg, #22c55e 0%, #14b8a6 100%)',
                        'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
                        'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                        'linear-gradient(135deg, #ec4899 0%, #f97316 100%)',
                        'linear-gradient(135deg, #10b981 0%, #84cc16 100%)',
                        'linear-gradient(135deg, #0ea5e9 0%, #22d3ee 100%)',
                        'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
                        'linear-gradient(135deg, #8b5cf6 0%, #06b6d4 100%)'
                      ].map((gradient, index) => {
                        const isSelected = settings.mainBackground === gradient ||
                          (settings.mainBackground.includes('gradient') && settings.mainBackground === gradient);
                        return (
                          <div
                            key={index}
                            onClick={() => {
                              handleChange('mainBackground', gradient);
                              if (settings.mainBackgroundImage) {
                                handleChange('mainBackgroundImage', '');
                              }
                            }}
                            style={{
                              height: '40px',
                              background: gradient,
                              borderRadius: '6px',
                              cursor: 'pointer',
                              border: isSelected ? '3px solid #60a5fa' : '2px solid rgba(75, 85, 99, 0.3)',
                              transition: 'all 0.2s ease',
                              boxShadow: isSelected ? '0 0 0 2px rgba(96, 165, 250, 0.3)' : 'none'
                            }}
                            title={`Градиент ${index + 1}`}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
              </SettingRow>

              <SettingRow label="Изображение фона">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                    id="bg-image-upload"
                  />
                  <label
                    htmlFor="bg-image-upload"
                    style={{
                      padding: '10px 16px',
                      borderRadius: '8px',
                      border: '1px solid rgba(59, 130, 246, 0.5)',
                      background: 'rgba(59, 130, 246, 0.1)',
                      color: '#60a5fa',
                      cursor: 'pointer',
                      textAlign: 'center',
                      fontSize: '14px',
                      fontWeight: 600
                    }}
                  >
                    📁 Выбрать изображение
                  </label>
                  {settings.mainBackgroundImage && (
                    <button
                      onClick={() => handleChange('mainBackgroundImage', '')}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: '1px solid rgba(239, 68, 68, 0.5)',
                        background: 'rgba(239, 68, 68, 0.1)',
                        color: '#ef4444',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      🗑️ Удалить изображение
                    </button>
                  )}
                </div>
              </SettingRow>

              {settings.mainBackgroundImage && (
                <SettingRow label="Прозрачность изображения">
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={settings.mainBackgroundImageOpacity}
                      onChange={(e) => handleChange('mainBackgroundImageOpacity', parseFloat(e.target.value))}
                      style={{ flex: 1 }}
                    />
                    <span style={{ color: '#9ca3af', fontSize: '14px', minWidth: '40px' }}>
                      {Math.round(settings.mainBackgroundImageOpacity * 100)}%
                    </span>
                  </div>
                </SettingRow>
              )}
            </Section>

            {/* Фон модалок */}
            <Section 
              title="Фон модальных окон" 
              icon={<FiMonitor size={18} />}
              expanded={expandedSections.modalBackground}
              onToggle={() => toggleSection('modalBackground')}
            >
              <SettingRow label="Цвет фона модалок">
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="color"
                    value={rgbaToHex(settings.modalBackground)}
                    onChange={(e) => handleChange('modalBackground', e.target.value)}
                    style={{
                      width: '60px',
                      height: '36px',
                      border: '1px solid rgba(75, 85, 99, 0.5)',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  />
                  <input
                    type="text"
                    value={settings.modalBackground}
                    onChange={(e) => handleChange('modalBackground', e.target.value)}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid rgba(75, 85, 99, 0.5)',
                      background: '#111827',
                      color: '#fff',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </SettingRow>

              <SettingRow label="Изображение фона">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      if (!file.type.startsWith('image/')) {
                        alert('Пожалуйста, выберите изображение');
                        return;
                      }
                      if (file.size > 5 * 1024 * 1024) {
                        alert('⚠️ Размер файла не должен превышать 5 МБ\n\nИспользуйте изображение меньшего размера или сожмите его перед загрузкой.');
                        return;
                      }
                      try {
                        const base64 = await loadImageAsBase64(file);
                        handleChange('modalBackgroundImage', base64);
                        
                        // Проверяем размер localStorage после загрузки
                        const storageInfo = checkLocalStorageSize();
                        if (storageInfo.percentUsed > 80) {
                          alert(
                            `⚠️ ВНИМАНИЕ: Память браузера заполнена на ${storageInfo.percentUsed.toFixed(0)}%\n\n` +
                            `Используется: ${storageInfo.totalMB.toFixed(2)} MB из ~5 MB\n\n` +
                            `💡 Рекомендация:\n` +
                            `- Удалите ненужные изображения\n` +
                            `- Или используйте изображения меньшего размера\n\n` +
                            `⚠️ При превышении лимита настройки не сохранятся!`
                          );
                        }
                      } catch (error) {
                        console.error('Ошибка загрузки изображения:', error);
                        alert('❌ Ошибка загрузки изображения: ' + error.message);
                      }
                    }}
                    style={{ display: 'none' }}
                    id="modal-bg-image-upload"
                  />
                  <label
                    htmlFor="modal-bg-image-upload"
                    style={{
                      padding: '10px 16px',
                      borderRadius: '8px',
                      border: '1px solid rgba(59, 130, 246, 0.5)',
                      background: 'rgba(59, 130, 246, 0.1)',
                      color: '#60a5fa',
                      cursor: 'pointer',
                      textAlign: 'center',
                      fontSize: '14px',
                      fontWeight: 600
                    }}
                  >
                    📁 Выбрать изображение
                  </label>
                  {settings.modalBackgroundImage && (
                    <button
                      onClick={() => handleChange('modalBackgroundImage', '')}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: '1px solid rgba(239, 68, 68, 0.5)',
                        background: 'rgba(239, 68, 68, 0.1)',
                        color: '#ef4444',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      🗑️ Удалить изображение
                    </button>
                  )}
                </div>
              </SettingRow>

              {settings.modalBackgroundImage && (
                <SettingRow label="Прозрачность изображения">
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={settings.modalBackgroundImageOpacity}
                      onChange={(e) => handleChange('modalBackgroundImageOpacity', parseFloat(e.target.value))}
                      style={{ flex: 1 }}
                    />
                    <span style={{ color: '#9ca3af', fontSize: '14px', minWidth: '40px' }}>
                      {Math.round(settings.modalBackgroundImageOpacity * 100)}%
                    </span>
                  </div>
                </SettingRow>
              )}
            </Section>

            {/* Стили сайдбара */}
            <Section 
              title="Стили сайдбара" 
              icon={<FiType size={18} />}
              expanded={expandedSections.sidebarStyles}
              onToggle={() => toggleSection('sidebarStyles')}
            >
              <div style={{ 
                padding: '16px',
                borderRadius: '10px',
                border: '1px solid rgba(59, 130, 246, 0.25)',
                background: 'rgba(59, 130, 246, 0.06)',
                marginBottom: '16px'
              }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#93c5fd', marginBottom: '12px' }}>
                  Блок аватара (шапка сайдбара)
                </div>
                <SettingRow label="Фон блока">
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={rgbaToHex(settings.sidebarHeaderBackground)}
                      onChange={(e) => handleChange('sidebarHeaderBackground', e.target.value)}
                      style={{ width: '60px', height: '36px', border: '1px solid rgba(75, 85, 99, 0.5)', borderRadius: '6px', cursor: 'pointer' }}
                    />
                    <input
                      type="text"
                      value={settings.sidebarHeaderBackground}
                      onChange={(e) => handleChange('sidebarHeaderBackground', e.target.value)}
                      style={{ flex: 1, padding: '8px 12px', borderRadius: '6px', border: '1px solid rgba(75, 85, 99, 0.5)', background: '#111827', color: '#fff', fontSize: '14px' }}
                    />
                  </div>
                </SettingRow>
                <div style={{ marginTop: '8px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 500, color: '#9ca3af' }}>
                      Готовые градиенты (светлые тона)
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
                      {[
                        'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                        'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                        'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)',
                        'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
                        'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
                        'linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%)',
                        'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                        'linear-gradient(135deg, #fefce8 0%, #fef9c3 100%)',
                        'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
                        'linear-gradient(135deg, #ecfeff 0%, #cffafe 100%)'
                      ].map((gradient, index) => {
                        const isSelected = settings.sidebarHeaderBackground === gradient ||
                          (settings.sidebarHeaderBackground.includes('gradient') && settings.sidebarHeaderBackground === gradient);
                        return (
                          <div
                            key={index}
                            onClick={() => handleChange('sidebarHeaderBackground', gradient)}
                            style={{
                              height: '40px',
                              background: gradient,
                              borderRadius: '6px',
                              cursor: 'pointer',
                              border: isSelected ? '3px solid #60a5fa' : '2px solid rgba(75, 85, 99, 0.3)',
                              transition: 'all 0.2s ease',
                              boxShadow: isSelected ? '0 0 0 2px rgba(96, 165, 250, 0.3)' : 'none'
                            }}
                            title={`Градиент ${index + 1}`}
                          />
                        );
                      })}
                    </div>
                  </div>

                  <div style={{ marginTop: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 500, color: '#9ca3af' }}>
                      Готовые градиенты (темные тона)
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
                      {[
                        'linear-gradient(135deg, #0f172a 0%, #1f2937 100%)',
                        'linear-gradient(135deg, #111827 0%, #0f172a 100%)',
                        'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
                        'linear-gradient(135deg, #0b1020 0%, #111827 100%)',
                        'linear-gradient(135deg, #1c1917 0%, #0f172a 100%)',
                        'linear-gradient(135deg, #0f172a 0%, #0b132b 100%)',
                        'linear-gradient(135deg, #111827 0%, #1f2937 100%)',
                        'linear-gradient(135deg, #0b1320 0%, #1e293b 100%)',
                        'linear-gradient(135deg, #1a1b1f 0%, #0f172a 100%)',
                        'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)'
                      ].map((gradient, index) => {
                        const isSelected = settings.sidebarHeaderBackground === gradient ||
                          (settings.sidebarHeaderBackground.includes('gradient') && settings.sidebarHeaderBackground === gradient);
                        return (
                          <div
                            key={index}
                            onClick={() => handleChange('sidebarHeaderBackground', gradient)}
                            style={{
                              height: '40px',
                              background: gradient,
                              borderRadius: '6px',
                              cursor: 'pointer',
                              border: isSelected ? '3px solid #60a5fa' : '2px solid rgba(75, 85, 99, 0.3)',
                              transition: 'all 0.2s ease',
                              boxShadow: isSelected ? '0 0 0 2px rgba(96, 165, 250, 0.3)' : 'none'
                            }}
                            title={`Градиент ${index + 1}`}
                          />
                        );
                      })}
                    </div>
                  </div>

                  <div style={{ marginTop: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 500, color: '#9ca3af' }}>
                      Готовые градиенты (цветные тона)
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
                      {[
                        'linear-gradient(135deg, #ff6b6b 0%, #f9a8d4 100%)',
                        'linear-gradient(135deg, #f97316 0%, #facc15 100%)',
                        'linear-gradient(135deg, #22c55e 0%, #14b8a6 100%)',
                        'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
                        'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                        'linear-gradient(135deg, #ec4899 0%, #f97316 100%)',
                        'linear-gradient(135deg, #10b981 0%, #84cc16 100%)',
                        'linear-gradient(135deg, #0ea5e9 0%, #22d3ee 100%)',
                        'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
                        'linear-gradient(135deg, #8b5cf6 0%, #06b6d4 100%)'
                      ].map((gradient, index) => {
                        const isSelected = settings.sidebarHeaderBackground === gradient ||
                          (settings.sidebarHeaderBackground.includes('gradient') && settings.sidebarHeaderBackground === gradient);
                        return (
                          <div
                            key={index}
                            onClick={() => handleChange('sidebarHeaderBackground', gradient)}
                            style={{
                              height: '40px',
                              background: gradient,
                              borderRadius: '6px',
                              cursor: 'pointer',
                              border: isSelected ? '3px solid #60a5fa' : '2px solid rgba(75, 85, 99, 0.3)',
                              transition: 'all 0.2s ease',
                              boxShadow: isSelected ? '0 0 0 2px rgba(96, 165, 250, 0.3)' : 'none'
                            }}
                            title={`Градиент ${index + 1}`}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 500, color: '#9ca3af', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={settings.sidebarUseHeaderBackgroundForButtons || false}
                      onChange={(e) => handleChange('sidebarUseHeaderBackgroundForButtons', e.target.checked)}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    Применить фон блока аватарки к кнопкам меню
                  </label>
                </div>
                <SettingRow label="Фоновая картинка блока">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        if (!file.type.startsWith('image/')) {
                          alert('Пожалуйста, выберите изображение');
                          return;
                        }
                        if (file.size > 5 * 1024 * 1024) {
                          alert('⚠️ Размер файла не должен превышать 5 МБ\n\nИспользуйте изображение меньшего размера или сожмите его перед загрузкой.');
                          return;
                        }
                        try {
                          const base64 = await loadImageAsBase64(file);
                          handleChange('sidebarHeaderBackgroundImage', base64);
                        } catch (error) {
                          console.error('Ошибка загрузки изображения:', error);
                          alert('❌ Ошибка загрузки изображения: ' + error.message);
                        }
                      }}
                      style={{ display: 'none' }}
                      id="sidebar-header-bg-image-upload"
                    />
                    <label
                      htmlFor="sidebar-header-bg-image-upload"
                      style={{
                        padding: '10px 16px',
                        borderRadius: '8px',
                        border: '1px solid rgba(59, 130, 246, 0.5)',
                        background: 'rgba(59, 130, 246, 0.1)',
                        color: '#60a5fa',
                        cursor: 'pointer',
                        textAlign: 'center',
                        fontSize: '14px',
                        fontWeight: 600
                      }}
                    >
                      📁 Выбрать изображение
                    </label>
                    {settings.sidebarHeaderBackgroundImage && (
                      <button
                        onClick={() => handleChange('sidebarHeaderBackgroundImage', '')}
                        style={{
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: '1px solid rgba(239, 68, 68, 0.5)',
                          background: 'rgba(239, 68, 68, 0.1)',
                          color: '#ef4444',
                          cursor: 'pointer',
                          fontSize: '13px'
                        }}
                      >
                        🗑️ Удалить изображение
                      </button>
                    )}
                  </div>
                </SettingRow>
                {settings.sidebarHeaderBackgroundImage && (
                  <SettingRow label="Прозрачность картинки">
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={settings.sidebarHeaderBackgroundImageOpacity}
                        onChange={(e) => handleChange('sidebarHeaderBackgroundImageOpacity', parseFloat(e.target.value))}
                        style={{ flex: 1 }}
                      />
                      <span style={{ color: '#9ca3af', fontSize: '14px', minWidth: '40px' }}>
                        {Math.round(settings.sidebarHeaderBackgroundImageOpacity * 100)}%
                      </span>
                    </div>
                  </SettingRow>
                )}
                <SettingRow label="Цвет имени пользователя">
                  <input
                    type="color"
                    value={rgbaToHex(settings.sidebarHeaderNameColor)}
                    onChange={(e) => handleChange('sidebarHeaderNameColor', e.target.value)}
                    style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }}
                  />
                </SettingRow>
                <SettingRow label="Цвет должности">
                  <input
                    type="color"
                    value={rgbaToHex(settings.sidebarHeaderDepartmentColor)}
                    onChange={(e) => handleChange('sidebarHeaderDepartmentColor', e.target.value)}
                    style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }}
                  />
                </SettingRow>
                <SettingRow label={`Сдвиг аватара по X (${settings.sidebarHeaderAvatarOffsetX || 0}px)`}>
                  <input
                    type="range"
                    min="-120"
                    max="120"
                    step="1"
                    value={settings.sidebarHeaderAvatarOffsetX || 0}
                    onChange={(e) => handleChange('sidebarHeaderAvatarOffsetX', parseInt(e.target.value, 10))}
                    style={{ width: '100%' }}
                  />
                </SettingRow>
                <SettingRow label={`Сдвиг аватара по Y (${settings.sidebarHeaderAvatarOffsetY || 0}px)`}>
                  <input
                    type="range"
                    min="-120"
                    max="120"
                    step="1"
                    value={settings.sidebarHeaderAvatarOffsetY || 0}
                    onChange={(e) => handleChange('sidebarHeaderAvatarOffsetY', parseInt(e.target.value, 10))}
                    style={{ width: '100%' }}
                  />
                </SettingRow>
              </div>
              <SettingRow label="Цвет фона кнопок">
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="color"
                    value={settings.sidebarButtonBackground}
                    onChange={(e) => handleChange('sidebarButtonBackground', e.target.value)}
                    style={{
                      width: '60px',
                      height: '36px',
                      border: '1px solid rgba(75, 85, 99, 0.5)',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  />
                  <input
                    type="text"
                    value={settings.sidebarButtonBackground}
                    onChange={(e) => handleChange('sidebarButtonBackground', e.target.value)}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid rgba(75, 85, 99, 0.5)',
                      background: '#111827',
                      color: '#fff',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </SettingRow>

              <SettingRow label="Изображение фона кнопок">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      if (!file.type.startsWith('image/')) {
                        alert('Пожалуйста, выберите изображение');
                        return;
                      }
                      if (file.size > 5 * 1024 * 1024) {
                        alert('⚠️ Размер файла не должен превышать 5 МБ\n\nИспользуйте изображение меньшего размера или сожмите его перед загрузкой.');
                        return;
                      }
                      try {
                        const base64 = await loadImageAsBase64(file);
                        handleChange('sidebarButtonBackgroundImage', base64);
                        
                        // Проверяем размер localStorage после загрузки
                        const storageInfo = checkLocalStorageSize();
                        if (storageInfo.percentUsed > 80) {
                          alert(
                            `⚠️ ВНИМАНИЕ: Память браузера заполнена на ${storageInfo.percentUsed.toFixed(0)}%\n\n` +
                            `Используется: ${storageInfo.totalMB.toFixed(2)} MB из ~5 MB\n\n` +
                            `💡 Рекомендация:\n` +
                            `- Удалите ненужные изображения\n` +
                            `- Или используйте изображения меньшего размера\n\n` +
                            `⚠️ При превышении лимита настройки не сохранятся!`
                          );
                        }
                      } catch (error) {
                        console.error('Ошибка загрузки изображения:', error);
                        alert('❌ Ошибка загрузки изображения: ' + error.message);
                      }
                    }}
                    style={{ display: 'none' }}
                    id="sidebar-button-bg-image-upload"
                  />
                  <label
                    htmlFor="sidebar-button-bg-image-upload"
                    style={{
                      padding: '10px 16px',
                      borderRadius: '8px',
                      border: '1px solid rgba(59, 130, 246, 0.5)',
                      background: 'rgba(59, 130, 246, 0.1)',
                      color: '#60a5fa',
                      cursor: 'pointer',
                      textAlign: 'center',
                      fontSize: '14px',
                      fontWeight: 600
                    }}
                  >
                    📁 Выбрать изображение
                  </label>
                  {settings.sidebarButtonBackgroundImage && (
                    <button
                      onClick={() => handleChange('sidebarButtonBackgroundImage', '')}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: '1px solid rgba(239, 68, 68, 0.5)',
                        background: 'rgba(239, 68, 68, 0.1)',
                        color: '#ef4444',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      🗑️ Удалить изображение
                    </button>
                  )}
                </div>
              </SettingRow>

              {settings.sidebarButtonBackgroundImage && (
                <SettingRow label="Прозрачность изображения кнопок">
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={settings.sidebarButtonBackgroundImageOpacity}
                      onChange={(e) => handleChange('sidebarButtonBackgroundImageOpacity', parseFloat(e.target.value))}
                      style={{ flex: 1 }}
                    />
                    <span style={{ color: '#9ca3af', fontSize: '14px', minWidth: '40px' }}>
                      {Math.round(settings.sidebarButtonBackgroundImageOpacity * 100)}%
                    </span>
                  </div>
                </SettingRow>
              )}


              <SettingRow label="Цвет текста">
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="color"
                    value={settings.sidebarTextColor}
                    onChange={(e) => handleChange('sidebarTextColor', e.target.value)}
                    style={{
                      width: '60px',
                      height: '36px',
                      border: '1px solid rgba(75, 85, 99, 0.5)',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  />
                  <input
                    type="text"
                    value={settings.sidebarTextColor}
                    onChange={(e) => handleChange('sidebarTextColor', e.target.value)}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid rgba(75, 85, 99, 0.5)',
                      background: '#111827',
                      color: '#fff',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </SettingRow>

              <SettingRow label="Размер текста">
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <input
                    type="range"
                    min="10"
                    max="20"
                    step="1"
                    value={parseInt(settings.sidebarTextSize)}
                    onChange={(e) => handleChange('sidebarTextSize', e.target.value + 'px')}
                    style={{ flex: 1 }}
                  />
                  <input
                    type="text"
                    value={settings.sidebarTextSize}
                    onChange={(e) => handleChange('sidebarTextSize', e.target.value)}
                    style={{
                      width: '60px',
                      padding: '8px',
                      borderRadius: '6px',
                      border: '1px solid rgba(75, 85, 99, 0.5)',
                      background: '#111827',
                      color: '#fff',
                      fontSize: '14px',
                      textAlign: 'center'
                    }}
                  />
                </div>
              </SettingRow>

              <SettingRow label="Выравнивание текста">
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => handleChange('sidebarTextAlign', 'left')}
                    style={{
                      flex: 1,
                      padding: '10px',
                      borderRadius: '8px',
                      border: settings.sidebarTextAlign === 'left' 
                        ? '2px solid #3b82f6' 
                        : '1px solid rgba(75, 85, 99, 0.5)',
                      background: settings.sidebarTextAlign === 'left'
                        ? 'rgba(59, 130, 246, 0.15)'
                        : '#111827',
                      color: settings.sidebarTextAlign === 'left' ? '#60a5fa' : '#9ca3af',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: settings.sidebarTextAlign === 'left' ? 600 : 400,
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                  >
                    ← Слева
                  </button>
                  <button
                    onClick={() => handleChange('sidebarTextAlign', 'center')}
                    style={{
                      flex: 1,
                      padding: '10px',
                      borderRadius: '8px',
                      border: settings.sidebarTextAlign === 'center' 
                        ? '2px solid #3b82f6' 
                        : '1px solid rgba(75, 85, 99, 0.5)',
                      background: settings.sidebarTextAlign === 'center'
                        ? 'rgba(59, 130, 246, 0.15)'
                        : '#111827',
                      color: settings.sidebarTextAlign === 'center' ? '#60a5fa' : '#9ca3af',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: settings.sidebarTextAlign === 'center' ? 600 : 400,
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                  >
                    ↔ По центру
                  </button>
                  <button
                    onClick={() => handleChange('sidebarTextAlign', 'right')}
                    style={{
                      flex: 1,
                      padding: '10px',
                      borderRadius: '8px',
                      border: settings.sidebarTextAlign === 'right' 
                        ? '2px solid #3b82f6' 
                        : '1px solid rgba(75, 85, 99, 0.5)',
                      background: settings.sidebarTextAlign === 'right'
                        ? 'rgba(59, 130, 246, 0.15)'
                        : '#111827',
                      color: settings.sidebarTextAlign === 'right' ? '#60a5fa' : '#9ca3af',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: settings.sidebarTextAlign === 'right' ? 600 : 400,
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                  >
                    Справа →
                  </button>
                </div>
              </SettingRow>

              <SettingRow label="Шрифт">
                <select
                  value={settings.sidebarFontFamily}
                  onChange={(e) => handleChange('sidebarFontFamily', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid rgba(75, 85, 99, 0.5)',
                    background: '#111827',
                    color: '#fff',
                    fontSize: '14px'
                  }}
                >
                  <option value="Inter, system-ui, -apple-system, sans-serif">Inter (по умолчанию)</option>
                  <option value="Arial, sans-serif">Arial</option>
                  <option value="'Helvetica Neue', Helvetica, sans-serif">Helvetica</option>
                  <option value="'Segoe UI', Tahoma, Geneva, Verdana, sans-serif">Segoe UI</option>
                  <option value="'Roboto', sans-serif">Roboto</option>
                  <option value="'Open Sans', sans-serif">Open Sans</option>
                  <option value="'Lato', sans-serif">Lato</option>
                  <option value="Georgia, serif">Georgia</option>
                  <option value="'Times New Roman', Times, serif">Times New Roman</option>
                  <option value="'Courier New', Courier, monospace">Courier New</option>
                </select>
              </SettingRow>
            </Section>
          </div>

          {/* Правая колонка - превью */}
          <div
            style={{
              width: '380px',
              backgroundColor: settings.mainBackgroundImage ? 'transparent' : settings.mainBackground,
              backgroundImage: settings.mainBackgroundImage ? `url(${settings.mainBackgroundImage})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              borderRadius: '12px',
              border: '2px solid rgba(59, 130, 246, 0.3)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Overlay для прозрачности изображения */}
            {settings.mainBackgroundImage && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: settings.mainBackground,
                  opacity: 1 - settings.mainBackgroundImageOpacity
                }}
              />
            )}

            {/* Превью сайдбара */}
            <div style={{ position: 'relative', zIndex: 1, padding: '16px' }}>
              <div
                style={{
                  background: settings.modalBackground,
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '12px'
                }}
              >
                <div style={{ 
                  fontSize: '16px', 
                  fontWeight: 700, 
                  color: '#60a5fa',
                  marginBottom: '12px'
                }}>
                  Превью сайдбара
                </div>
                
                {/* Примеры кнопок */}
                {['Чаты', 'Задачи', 'Новости', 'Календарь', 'Отпуска'].map((text, i) => {
                  // Формируем background правильно: если есть изображение, используем его, иначе цвет
                  const backgroundColor = settings.sidebarButtonBackground;
                  const backgroundImage = settings.sidebarButtonBackgroundImage 
                    ? `url(${settings.sidebarButtonBackgroundImage})` 
                    : 'none';
                  
                  return (
                    <div
                      key={i}
                      style={{
                        backgroundColor: backgroundImage !== 'none' ? 'transparent' : backgroundColor,
                        backgroundImage: backgroundImage,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        marginBottom: '8px',
                        color: settings.sidebarTextColor,
                        fontSize: settings.sidebarTextSize,
                        fontFamily: settings.sidebarFontFamily,
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: settings.sidebarTextAlign === 'center' 
                          ? 'center' 
                          : settings.sidebarTextAlign === 'right' 
                            ? 'flex-end' 
                            : 'flex-start',
                        gap: '8px',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                    >
                      {/* Overlay для прозрачности изображения кнопки */}
                      {settings.sidebarButtonBackgroundImage && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: settings.sidebarButtonBackground,
                            opacity: 1 - settings.sidebarButtonBackgroundImageOpacity,
                            pointerEvents: 'none'
                          }}
                        />
                      )}
                    <div style={{ 
                      width: '8px', 
                      height: '8px', 
                      borderRadius: '50%', 
                      background: settings.sidebarTextColor,
                      opacity: 0.5,
                      position: 'relative',
                      zIndex: 1
                      }} />
                      <span style={{ position: 'relative', zIndex: 1 }}>{text}</span>
                    </div>
                  );
                })}
              </div>

              {/* Превью модалки */}
              <div
                style={{
                  backgroundColor: settings.modalBackgroundImage ? 'transparent' : settings.modalBackground,
                  backgroundImage: settings.modalBackgroundImage ? `url(${settings.modalBackgroundImage})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  borderRadius: '12px',
                  padding: '16px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {/* Overlay для прозрачности изображения модалки */}
                {settings.modalBackgroundImage && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: settings.modalBackground,
                      opacity: 1 - settings.modalBackgroundImageOpacity,
                      pointerEvents: 'none'
                    }}
                  />
                )}
                
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <div style={{ 
                    fontSize: '14px', 
                    fontWeight: 600, 
                    color: '#9ca3af',
                    marginBottom: '8px'
                  }}>
                    Превью модального окна
                  </div>
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#6b7280',
                    lineHeight: 1.6
                  }}>
                    Здесь отображается, как будут выглядеть модальные окна с выбранным фоном.
                  </div>
                  <div
                    style={{
                      marginTop: '12px',
                      padding: '8px 12px',
                      background: 'rgba(59, 130, 246, 0.1)',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '6px',
                      color: '#60a5fa',
                      fontSize: '12px',
                      textAlign: 'center'
                    }}
                  >
                    Пример кнопки
                  </div>
                </div>
              </div>
            </div>
          </div>
            </>
          )}

          {/* Вкладки для других модалок */}
          {activeTab === 'calendar' && (
            <ModalSettingsSection
              modalId="calendar"
              modalName="Общий календарь"
              modalIcon={FiCalendar}
              settings={settings}
              onSettingsChange={handleSettingsChange}
            />
          )}

          {activeTab === 'chats' && (
            <ModalSettingsSection
              modalId="chats"
              modalName="Чаты"
              modalIcon={FiMessageSquare}
              settings={settings}
              onSettingsChange={handleSettingsChange}
            />
          )}

          {activeTab === 'leaves' && (
            <ModalSettingsSection
              modalId="leaves"
              modalName="Отгулы"
              modalIcon={FiUmbrella}
              settings={settings}
              onSettingsChange={handleSettingsChange}
            />
          )}

          {activeTab === 'tasks' && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Выделенный блок: стили таблицы и поле дедлайн */}
              <Section
                title="Стили таблицы полей и поле дедлайн"
                icon={<FiLayout size={18} />}
                expanded={expandedSections.tasksTableAndDeadlineStyles}
                onToggle={() => toggleSection('tasksTableAndDeadlineStyles')}
              >
                <div style={{
                  border: '2px solid #22c55e',
                  borderRadius: '12px',
                  padding: '16px',
                  background: 'rgba(34, 197, 94, 0.08)',
                  boxShadow: '0 0 0 2px rgba(34, 197, 94, 0.2)',
                  marginBottom: '12px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <FiCheckSquare size={18} color="#22c55e" />
                    <span style={{ color: '#22c55e', fontWeight: 700 }}>Стили таблицы и цвет дедлайна (с часиками)</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Ячейки таблицы — фон (обычное)</label>
                      <input type="color" value={rgbaToHex(settings.tableCellBackground)} onChange={(e) => handleChange('tableCellBackground', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Ячейки таблицы — текст (обычное)</label>
                      <input type="color" value={rgbaToHex(settings.tableCellTextColor)} onChange={(e) => handleChange('tableCellTextColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Ячейки таблицы — фон (при наведении)</label>
                      <input type="color" value={rgbaToHex(settings.tableCellHoverBackground)} onChange={(e) => handleChange('tableCellHoverBackground', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Ячейки таблицы — текст (при наведении)</label>
                      <input type="color" value={rgbaToHex(settings.tableCellHoverTextColor)} onChange={(e) => handleChange('tableCellHoverTextColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Поле «Дедлайн» (текст с часиками 🕒)</label>
                      <input type="color" value={rgbaToHex(settings.deadlineFieldTextColor)} onChange={(e) => handleChange('deadlineFieldTextColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                    </div>
                  </div>
                </div>
              </Section>
              <ModalSettingsSection
                modalId="tasks"
                modalName="Задачи"
                modalIcon={FiCheckSquare}
                settings={settings}
                onSettingsChange={handleSettingsChange}
              />
            </div>
          )}

          {activeTab === 'todo' && (
            <ModalSettingsSection
              modalId="todo"
              modalName="Список дел"
              modalIcon={FiList}
              settings={settings}
              onSettingsChange={handleSettingsChange}
            />
          )}

          {activeTab === 'news' && (
            <ModalSettingsSection
              modalId="news"
              modalName="Новости"
              modalIcon={FiFileText}
              settings={settings}
              onSettingsChange={handleSettingsChange}
            />
          )}

          {activeTab === 'employees' && (
            <ModalSettingsSection
              modalId="employees"
              modalName="Сотрудники компании"
              modalIcon={FiUsers}
              settings={settings}
              onSettingsChange={handleSettingsChange}
            />
          )}

          {activeTab === 'monitoring' && (
            <div style={{ flex: 1, display: 'flex', gap: '24px' }}>
              <div style={{ width: 320, display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <Section
                  title="📂 Отчеты мониторинга"
                  icon={<FiList size={18} />}
                  expanded={expandedSections.monitoringReportsList}
                  onToggle={() => toggleSection('monitoringReportsList')}
                >
                  <button
                    type="button"
                    onClick={() => toggleSection('monitoringReportsList')}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid rgba(75, 85, 99, 0.4)',
                      background: '#111827',
                      color: '#e5e7eb',
                      cursor: 'pointer',
                      fontWeight: 600
                    }}
                  >
                    Выбрать отчет
                  </button>
                  {expandedSections.monitoringReportsList && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {[
                        { id: 'app-usage-report', label: 'Отчет запусков приложения' },
                        { id: 'remote-report', label: 'Отчет удаленка' },
                        { id: 'local-pc-activity', label: 'Отчет активности локальных ПК' }
                      ].map(item => {
                        const isActive = settings.selectedMonitoringReport === item.id;
                        return (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => handleSettingsChange({ ...settings, selectedMonitoringReport: item.id })}
                            style={{
                              width: '100%',
                              padding: '10px 12px',
                              borderRadius: '8px',
                              border: isActive ? '2px solid #60a5fa' : '1px solid rgba(75, 85, 99, 0.4)',
                              background: isActive ? 'rgba(96, 165, 250, 0.15)' : '#111827',
                              color: isActive ? '#60a5fa' : '#d1d5db',
                              cursor: 'pointer',
                              textAlign: 'left',
                              fontWeight: isActive ? 700 : 500
                            }}
                          >
                            {item.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <div style={{
                    marginTop: '16px',
                    padding: '14px',
                    borderRadius: '10px',
                    background: 'rgba(67, 233, 123, 0.08)',
                    border: '1px solid rgba(67, 233, 123, 0.3)'
                  }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: '#9ca3af' }}>
                      Цвет заголовка окна «Мониторинг времени»
                    </label>
                    <input
                      type="color"
                      value={rgbaToHex(settings.modals?.monitoring?.titleColor ?? settings.modalHeaderTextColor ?? '#60a5fa')}
                      onChange={(e) => handleSettingsChange({
                        ...settings,
                        modals: {
                          ...settings.modals,
                          monitoring: {
                            ...(settings.modals?.monitoring || {}),
                            titleColor: e.target.value
                          }
                        }
                      })}
                      style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75, 85, 99, 0.5)', cursor: 'pointer' }}
                    />
                  </div>
                </Section>
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <Section
                  title="🛠️ Настройки выбранного отчета"
                  icon={<FiSettings size={18} />}
                  expanded={expandedSections.monitoringReportEditor}
                  onToggle={() => toggleSection('monitoringReportEditor')}
                >
                  <Section
                    title="Мониторинг времени (основное окно)"
                    icon={<FiClock size={18} />}
                    expanded={expandedSections.monitoringBase}
                    onToggle={() => toggleSection('monitoringBase')}
                  >
                    <div style={{ display: 'flex', gap: '24px' }}>
                      <ModalSettingsSection
                        modalId="monitoring"
                        modalName="Мониторинг времени"
                        modalIcon={FiClock}
                        settings={settings}
                        onSettingsChange={handleSettingsChange}
                      />
                    </div>
                  </Section>
                  {settings.selectedMonitoringReport === 'app-usage-report' && (
                    <>
                      <div style={{ display: 'flex', gap: '24px' }}>
                        <ModalSettingsSection
                          modalId="app-usage-report"
                          modalName="Отчет запусков приложения"
                          modalIcon={FiFileText}
                          settings={settings}
                          onSettingsChange={handleSettingsChange}
                        />
                      </div>
                      <Section
                        title="Подробнее"
                        icon={<FiList size={18} />}
                        expanded={expandedSections.monitoringAppUsageDetails}
                        onToggle={() => toggleSection('monitoringAppUsageDetails')}
                      >
                        <div style={{ display: 'flex', gap: '24px' }}>
                          <ModalSettingsSection
                            modalId="app-usage-details"
                            modalName="Подробнее"
                            modalIcon={FiList}
                            settings={settings}
                            onSettingsChange={handleSettingsChange}
                          />
                        </div>
                      </Section>
                    </>
                  )}
                  {settings.selectedMonitoringReport === 'remote-report' && (
                    <>
                      <div style={{ display: 'flex', gap: '24px' }}>
                        <ModalSettingsSection
                          modalId="remote-report"
                          modalName="Отчет удаленка"
                          modalIcon={FiClock}
                          settings={settings}
                          onSettingsChange={handleSettingsChange}
                        />
                      </div>
                      <Section
                        title="Карточки отчета"
                        icon={<FiFileText size={18} />}
                        expanded={expandedSections.monitoringRemoteReportCards}
                        onToggle={() => toggleSection('monitoringRemoteReportCards')}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                          <div style={{ background: '#111827', padding: '20px', borderRadius: '12px', border: '1px solid rgba(75, 85, 99, 0.3)' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '16px' }}>
                              <div>
                                <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Бордер карточки</label>
                                <input type="color" value={rgbaToHex(remoteReportSettings.reportCardBorderColor || '#000000')} onChange={(e) => updateModalSetting('remote-report', 'reportCardBorderColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                              </div>
                              <div>
                                <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Фон карточки</label>
                                <input type="color" value={rgbaToHex(remoteReportSettings.reportCardBackground || '#111827')} onChange={(e) => updateModalSetting('remote-report', 'reportCardBackground', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                              </div>
                              <div>
                                <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Цвет текста</label>
                                <input type="color" value={rgbaToHex(remoteReportSettings.reportTextColor || '#ffffff')} onChange={(e) => updateModalSetting('remote-report', 'reportTextColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                              </div>
                              <div>
                                <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Цвет имени</label>
                                <input type="color" value={rgbaToHex(remoteReportSettings.reportNameColor || '#43e97b')} onChange={(e) => updateModalSetting('remote-report', 'reportNameColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                              </div>
                              <div>
                                <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Цвет дат и времени</label>
                                <input type="color" value={rgbaToHex(remoteReportSettings.reportDateColor || '#ffffff')} onChange={(e) => updateModalSetting('remote-report', 'reportDateColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                              </div>
                            </div>
                          </div>
                          <div style={{ background: '#111827', padding: '20px', borderRadius: '12px', border: '1px solid rgba(75, 85, 99, 0.3)' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '16px' }}>
                              <div>
                                <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Кнопка "Подробности" — фон</label>
                                <input type="color" value={rgbaToHex(remoteReportSettings.reportDetailsButtonBackground || '#2193b0')} onChange={(e) => updateModalSetting('remote-report', 'reportDetailsButtonBackground', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                              </div>
                              <div>
                                <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Кнопка "Подробности" — бордер</label>
                                <input type="color" value={rgbaToHex(remoteReportSettings.reportDetailsButtonBorderColor || '#2193b0')} onChange={(e) => updateModalSetting('remote-report', 'reportDetailsButtonBorderColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                              </div>
                              <div>
                                <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Кнопка "Подробности" — текст</label>
                                <input type="color" value={rgbaToHex(remoteReportSettings.reportDetailsButtonTextColor || '#ffffff')} onChange={(e) => updateModalSetting('remote-report', 'reportDetailsButtonTextColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                              </div>
                            </div>
                          </div>
                        </div>
                      </Section>
                      <Section
                        title="Подробности"
                        icon={<FiList size={18} />}
                        expanded={expandedSections.monitoringRemoteReportDetails}
                        onToggle={() => toggleSection('monitoringRemoteReportDetails')}
                      >
                        <div style={{ background: '#111827', padding: '20px', borderRadius: '12px', border: '1px solid rgba(75, 85, 99, 0.3)' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '16px' }}>
                            <div>
                              <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Бордер блока</label>
                              <input type="color" value={rgbaToHex(remoteReportDetailsSettings.detailCardBorderColor || '#000000')} onChange={(e) => updateModalSetting('remote-report-details', 'detailCardBorderColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Фон блока</label>
                              <input type="color" value={rgbaToHex(remoteReportDetailsSettings.detailCardBackground || '#111827')} onChange={(e) => updateModalSetting('remote-report-details', 'detailCardBackground', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Цвет текста</label>
                              <input type="color" value={rgbaToHex(remoteReportDetailsSettings.detailTextColor || '#ffffff')} onChange={(e) => updateModalSetting('remote-report-details', 'detailTextColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Цвет даты и времени</label>
                              <input type="color" value={rgbaToHex(remoteReportDetailsSettings.detailDateColor || '#ffffff')} onChange={(e) => updateModalSetting('remote-report-details', 'detailDateColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                            </div>
                          </div>
                        </div>
                      </Section>
                    </>
                  )}
                  {settings.selectedMonitoringReport === 'local-pc-activity' && (
                    <>
                      <div style={{ display: 'flex', gap: '24px' }}>
                        <ModalSettingsSection
                          modalId="local-pc-activity"
                          modalName="Отчет активности локальных ПК"
                          modalIcon={FiActivity}
                          settings={settings}
                          onSettingsChange={handleSettingsChange}
                        />
                      </div>
                      <Section
                        title="Подробнее"
                        icon={<FiList size={18} />}
                        expanded={expandedSections.monitoringAppUsageDetails}
                        onToggle={() => toggleSection('monitoringAppUsageDetails')}
                      >
                        <div style={{ display: 'flex', gap: '24px' }}>
                          <ModalSettingsSection
                            modalId="local-pc-activity-details"
                            modalName="Подробнее (локальные ПК)"
                            modalIcon={FiList}
                            settings={settings}
                            onSettingsChange={handleSettingsChange}
                          />
                        </div>
                      </Section>
                    </>
                  )}
                </Section>
              </div>
            </div>
          )}

          {activeTab === 'worktime' && (
            <ModalSettingsSection
              modalId="worktime"
              modalName="Отработка отгулов"
              modalIcon={FiActivity}
              settings={settings}
              onSettingsChange={handleSettingsChange}
            />
          )}

          {activeTab === 'management' && (
            <ModalSettingsSection
              modalId="management"
              modalName="Управление"
              modalIcon={FiSettings}
              settings={settings}
              onSettingsChange={handleSettingsChange}
            />
          )}

          {activeTab === 'orders' && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <Section
                title="Модалка «Заказы»"
                icon={<FiList size={18} />}
                expanded={expandedSections.ordersModal}
                onToggle={() => toggleSection('ordersModal')}
              >
                {(() => {
                  const defaults = DEFAULT_SETTINGS.modals?.['warehouse-orders'] || {};
                  const current = settings.modals?.['warehouse-orders'] || {};
                  const val = (k) => (current[k] != null && current[k] !== '' ? current[k] : defaults[k]);
                  return (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '16px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Фон</label>
                        <input
                          type="text"
                          value={val('background') || ''}
                          onChange={(e) => updateModalSetting('warehouse-orders', 'background', e.target.value)}
                          placeholder="например: #111827 или linear-gradient(...)"
                          style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)', background: '#1f2937', color: '#e5e7eb', fontSize: '13px', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Цвет текста</label>
                        <input type="color" value={rgbaToHex(val('textColor') || '#1e293b')} onChange={(e) => updateModalSetting('warehouse-orders', 'textColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Цвет заголовков</label>
                        <input type="color" value={rgbaToHex(val('headerTextColor') || '#0f766e')} onChange={(e) => updateModalSetting('warehouse-orders', 'headerTextColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Фон кнопок</label>
                        <input type="color" value={rgbaToHex(val('buttonBackground') || '#ffffff')} onChange={(e) => updateModalSetting('warehouse-orders', 'buttonBackground', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>Текст кнопок</label>
                        <input type="color" value={rgbaToHex(val('buttonTextColor') || '#334155')} onChange={(e) => updateModalSetting('warehouse-orders', 'buttonTextColor', e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                      </div>
                    </div>
                  );
                })()}
              </Section>

              <Section
                title="Сбор товаров (внутри заказа)"
                icon={<FiCheckSquare size={18} />}
                expanded={expandedSections.pickingModal}
                onToggle={() => toggleSection('pickingModal')}
              >
                {(() => {
                  const defaults = DEFAULT_SETTINGS.modals?.['warehouse-picking'] || {};
                  const current = settings.modals?.['warehouse-picking'] || {};
                  const val = (k) => (current[k] != null && current[k] !== '' ? current[k] : defaults[k]);
                  const color = (label, key, fallback) => (
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#9ca3af' }}>{label}</label>
                      <input type="color" value={rgbaToHex(val(key) || fallback)} onChange={(e) => updateModalSetting('warehouse-picking', key, e.target.value)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)' }} />
                    </div>
                  );
                  return (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '16px' }}>
                      {color('Цвет текста', 'textColor', '#1e293b')}
                      {color('Цвет заголовков', 'headerTextColor', '#0f766e')}
                      {color('Цвет текста блока «Товар»', 'productBlockTextColor', '#1e293b')}
                      {color('Цвет фона блока «Товар»', 'productBlockBackground', '#ffffff')}
                      {color('Цвет фона строки товара', 'productRowBackground', '#ffffff')}
                      {color('Кнопка «Нет на складе» (цвет текста)', 'noStockButtonTextColor', '#b91c1c')}
                      {color('Счётчик (цвет текста)', 'counterTextColor', '#0f766e')}
                      {color('Счётчик (кнопки: фон)', 'counterButtonBackground', '#0d7377')}
                      {color('Счётчик (кнопки: текст)', 'counterButtonTextColor', '#ffffff')}
                      {color('Счётчик (инпут: фон)', 'counterInputBackground', '#ffffff')}
                    </div>
                  );
                })()}
              </Section>
            </div>
          )}

          {/* Вкладка: ВПР */}
          {activeTab === 'vpr' && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {(() => {
                const vprDefaults = DEFAULT_SETTINGS.modals?.vpr || {};
                const currentVpr = settings.modals?.vpr || {};
                const vprSettings = { ...vprDefaults };
                Object.keys(currentVpr).forEach((k) => {
                  const v = currentVpr[k];
                  if (v != null && v !== '') vprSettings[k] = v;
                });
                const discDefaults = DEFAULT_SETTINGS.modals?.['vpr-discrepancies'] || {};
                const discSettings = { ...discDefaults, ...(settings.modals?.['vpr-discrepancies'] || {}) };
                const updateVpr = (key, value) => {
                  const nextVpr = { ...vprDefaults };
                  Object.keys(vprSettings).forEach((k) => {
                    const v = k === key ? value : vprSettings[k];
                    if (v != null && v !== '') nextVpr[k] = v;
                  });
                  if (value != null && value !== '') nextVpr[key] = value;
                  const updatedModals = { ...settings.modals, vpr: nextVpr };
                  handleSettingsChange({ ...settings, modals: updatedModals });
                };
                const updateDisc = (key, value) => {
                  const updatedModals = { ...settings.modals, 'vpr-discrepancies': { ...discSettings, [key]: value } };
                  handleSettingsChange({ ...settings, modals: updatedModals });
                };
                const colorInput = (label, value, onChange) => (
                  <div key={label} style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>{label}</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input type="color" value={rgbaToHex(value || '#2193b0')} onChange={(e) => onChange(e.target.value)} style={{ width: '48px', height: '36px', border: '1px solid rgba(75,85,99,0.5)', borderRadius: '8px', cursor: 'pointer' }} />
                      <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)} style={{ flex: 1, padding: '8px 12px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)', background: '#1f2937', color: '#e5e7eb', fontSize: '13px' }} />
                    </div>
                  </div>
                );
                return (
                  <>
                    <Section title="Модалка ВПР" icon={<FiBarChart2 size={18} />} expanded={expandedSections.vprMain} onToggle={() => toggleSection('vprMain')}>
                      <div style={{ background: '#111827', padding: '16px', borderRadius: '12px', border: '1px solid rgba(75, 85, 99, 0.3)' }}>
                        <h4 style={{ margin: '0 0 12px', fontSize: '14px', color: '#60a5fa' }}>Фон модалки ВПР</h4>
                        <div style={{ marginBottom: '12px' }}>
                          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>Цвет фона (палитра)</label>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                            <input
                              type="color"
                              value={rgbaToHex((vprSettings.background || '').startsWith('linear-gradient') ? '#e8eef2' : (vprSettings.background || '#e8eef2'))}
                              onChange={(e) => updateVpr('background', e.target.value)}
                              style={{
                                width: '56px',
                                height: '44px',
                                border: '1px solid rgba(75, 85, 99, 0.5)',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                padding: 0,
                                flexShrink: 0
                              }}
                            />
                            <input
                              type="text"
                              value={vprSettings.background || ''}
                              onChange={(e) => updateVpr('background', e.target.value)}
                              placeholder="или вставьте gradient: linear-gradient(...)"
                              style={{ flex: 1, padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(75,85,99,0.5)', background: '#1f2937', color: '#e5e7eb', fontSize: '13px', boxSizing: 'border-box' }}
                            />
                          </div>
                          <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 500, color: '#9ca3af' }}>Готовые градиенты (светлые и тёмные)</label>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px', marginBottom: '12px' }}>
                            {[
                              'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                              'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                              'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)',
                              'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
                              'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
                              'linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%)',
                              'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                              'linear-gradient(135deg, #fefce8 0%, #fef9c3 100%)',
                              'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
                              'linear-gradient(135deg, #ecfeff 0%, #cffafe 100%)',
                              'linear-gradient(135deg, #e8eef2 0%, #b0d4dc 100%)',
                              'linear-gradient(135deg, #0f172a 0%, #1f2937 100%)',
                              'linear-gradient(135deg, #111827 0%, #0f172a 100%)',
                              'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
                              'linear-gradient(135deg, #0b1020 0%, #111827 100%)',
                              'linear-gradient(135deg, #1c1917 0%, #0f172a 100%)'
                            ].map((gradient, index) => {
                              const isSelected = vprSettings.background === gradient;
                              return (
                                <div
                                  key={index}
                                  onClick={() => updateVpr('background', gradient)}
                                  style={{
                                    height: '36px',
                                    background: gradient,
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    border: isSelected ? '3px solid #60a5fa' : '2px solid rgba(75, 85, 99, 0.3)',
                                    boxShadow: isSelected ? '0 0 0 2px rgba(96, 165, 250, 0.3)' : 'none'
                                  }}
                                  title="Выбрать градиент"
                                />
                              );
                            })}
                          </div>
                          <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 500, color: '#9ca3af' }}>Готовые градиенты (цветные)</label>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
                            {[
                              'linear-gradient(135deg, #ff6b6b 0%, #f9a8d4 100%)',
                              'linear-gradient(135deg, #f97316 0%, #facc15 100%)',
                              'linear-gradient(135deg, #22c55e 0%, #14b8a6 100%)',
                              'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
                              'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                              'linear-gradient(135deg, #ec4899 0%, #f97316 100%)',
                              'linear-gradient(135deg, #10b981 0%, #84cc16 100%)',
                              'linear-gradient(135deg, #0ea5e9 0%, #22d3ee 100%)',
                              'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
                              'linear-gradient(135deg, #8b5cf6 0%, #06b6d4 100%)'
                            ].map((gradient, index) => {
                              const isSelected = vprSettings.background === gradient;
                              return (
                                <div
                                  key={index}
                                  onClick={() => updateVpr('background', gradient)}
                                  style={{
                                    height: '36px',
                                    background: gradient,
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    border: isSelected ? '3px solid #60a5fa' : '2px solid rgba(75, 85, 99, 0.3)',
                                    boxShadow: isSelected ? '0 0 0 2px rgba(96, 165, 250, 0.3)' : 'none'
                                  }}
                                  title="Выбрать градиент"
                                />
                              );
                            })}
                          </div>
                        </div>
                        <h4 style={{ margin: '16px 0 12px', fontSize: '14px', color: '#60a5fa' }}>Заголовки</h4>
                        {colorInput('Цвет заголовков', vprSettings.headerColor, (v) => updateVpr('headerColor', v))}
                        <h4 style={{ margin: '16px 0 12px', fontSize: '14px', color: '#60a5fa' }}>Текст</h4>
                        {colorInput('Цвет текста', vprSettings.textColor, (v) => updateVpr('textColor', v))}
                        {colorInput('Цвет текста над кнопками «Выбрать» (1, 2, 3)', vprSettings.fileLabelColor, (v) => updateVpr('fileLabelColor', v))}
                        {colorInput('Цвет описания под полями (Столбец B…, ТТН формата…, A — дата…)', vprSettings.fileDescriptionColor, (v) => updateVpr('fileDescriptionColor', v))}
                        <h4 style={{ margin: '16px 0 12px', fontSize: '14px', color: '#60a5fa' }}>Кнопки (общие)</h4>
                        {colorInput('Фон кнопок', vprSettings.buttonBackground, (v) => updateVpr('buttonBackground', v))}
                        {colorInput('Цвет бордера кнопок', vprSettings.buttonBorderColor, (v) => updateVpr('buttonBorderColor', v))}
                        {colorInput('Цвет текста кнопок', vprSettings.buttonTextColor, (v) => updateVpr('buttonTextColor', v))}
                        <h4 style={{ margin: '16px 0 12px', fontSize: '14px', color: '#60a5fa' }}>Дата (календарик)</h4>
                        {colorInput('Цвет текста возле календаря (Дата)', vprSettings.dateLabelColor, (v) => updateVpr('dateLabelColor', v))}
                        {colorInput('Фон поля календаря', vprSettings.dateFieldBackground, (v) => updateVpr('dateFieldBackground', v))}
                        {colorInput('Цвет поля календаря', vprSettings.dateFieldColor, (v) => updateVpr('dateFieldColor', v))}
                        {colorInput('Бордер поля календаря', vprSettings.dateFieldBorderColor, (v) => updateVpr('dateFieldBorderColor', v))}
                        <h4 style={{ margin: '16px 0 12px', fontSize: '14px', color: '#60a5fa' }}>Кнопка «Найти расхождения»</h4>
                        {colorInput('Фон кнопки', vprSettings.findDiscrepanciesButtonBackground, (v) => updateVpr('findDiscrepanciesButtonBackground', v))}
                        {colorInput('Цвет текста кнопки', vprSettings.findDiscrepanciesButtonTextColor, (v) => updateVpr('findDiscrepanciesButtonTextColor', v))}
                        {colorInput('Бордер кнопки', vprSettings.findDiscrepanciesButtonBorderColor, (v) => updateVpr('findDiscrepanciesButtonBorderColor', v))}
                      </div>
                    </Section>
                    <Section title="Результаты расхождений (подменю)" icon={<FiBarChart2 size={18} />} expanded={expandedSections.vprDiscrepancies} onToggle={() => toggleSection('vprDiscrepancies')}>
                      <div style={{ background: '#111827', padding: '16px', borderRadius: '12px', border: '1px solid rgba(75, 85, 99, 0.3)' }}>
                        <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#9ca3af' }}>Фон модалки «Результаты расхождений» наследуется от модалки ВПР.</p>
                        <h4 style={{ margin: '0 0 12px', fontSize: '14px', color: '#60a5fa' }}>Вкладки</h4>
                        {colorInput('Фон вкладок', discSettings.tabBackground, (v) => updateDisc('tabBackground', v))}
                        {colorInput('Цвет текста вкладок', discSettings.tabTextColor, (v) => updateDisc('tabTextColor', v))}
                        {colorInput('Бордер вкладок', discSettings.tabBorderColor, (v) => updateDisc('tabBorderColor', v))}
                        {colorInput('Фон активной вкладки', discSettings.tabActiveBackground, (v) => updateDisc('tabActiveBackground', v))}
                        {colorInput('Цвет текста активной вкладки', discSettings.tabActiveTextColor, (v) => updateDisc('tabActiveTextColor', v))}
                        {colorInput('Бордер активной вкладки', discSettings.tabActiveBorderColor, (v) => updateDisc('tabActiveBorderColor', v))}
                        <h4 style={{ margin: '16px 0 8px', fontSize: '14px', color: '#60a5fa' }}>Ховер вкладок</h4>
                        {colorInput('Фон при наведении', discSettings.tabHoverBackground, (v) => updateDisc('tabHoverBackground', v))}
                        {colorInput('Бордер при наведении', discSettings.tabHoverBorderColor, (v) => updateDisc('tabHoverBorderColor', v))}
                        {colorInput('Цвет текста при наведении', discSettings.tabHoverTextColor, (v) => updateDisc('tabHoverTextColor', v))}
                        <h4 style={{ margin: '16px 0 12px', fontSize: '14px', color: '#60a5fa' }}>Модалка «Результаты расхождений»</h4>
                        {colorInput('Цвет заголовка', discSettings.headerColor, (v) => updateDisc('headerColor', v))}
                        {colorInput('Цвет текста', discSettings.textColor, (v) => updateDisc('textColor', v))}
                        <h4 style={{ margin: '16px 0 12px', fontSize: '14px', color: '#60a5fa' }}>Кнопки в этой модалке</h4>
                        {colorInput('Фон кнопок', discSettings.buttonBackground, (v) => updateDisc('buttonBackground', v))}
                        {colorInput('Цвет текста кнопок', discSettings.buttonTextColor, (v) => updateDisc('buttonTextColor', v))}
                        {colorInput('Бордер кнопок', discSettings.buttonBorderColor, (v) => updateDisc('buttonBorderColor', v))}
                        <div style={{ marginTop: '20px', padding: '16px', borderRadius: '12px', border: '2px solid rgba(45, 125, 74, 0.4)', background: 'linear-gradient(135deg, rgba(45, 125, 74, 0.08) 0%, rgba(26, 95, 46, 0.06) 100%)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)' }}>
                          <h4 style={{ margin: '0 0 12px', fontSize: '14px', color: '#2d7d4a', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FiCheckSquare size={18} /> Стили кнопок (каждая отдельно)
                          </h4>
                          <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#9ca3af' }}>Скачать файл, Отправить в Telegram, Назначить задачу.</p>
                          <h5 style={{ margin: '12px 0 8px', fontSize: '13px', color: '#60a5fa' }}>Скачать файл</h5>
                          {colorInput('Фон', discSettings.downloadButtonBackground, (v) => updateDisc('downloadButtonBackground', v))}
                          {colorInput('Цвет текста', discSettings.downloadButtonTextColor, (v) => updateDisc('downloadButtonTextColor', v))}
                          {colorInput('Бордер', discSettings.downloadButtonBorderColor, (v) => updateDisc('downloadButtonBorderColor', v))}
                          <h5 style={{ margin: '12px 0 8px', fontSize: '13px', color: '#60a5fa' }}>Отправить в Telegram</h5>
                          {colorInput('Фон', discSettings.telegramButtonBackground, (v) => updateDisc('telegramButtonBackground', v))}
                          {colorInput('Цвет текста', discSettings.telegramButtonTextColor, (v) => updateDisc('telegramButtonTextColor', v))}
                          {colorInput('Бордер', discSettings.telegramButtonBorderColor, (v) => updateDisc('telegramButtonBorderColor', v))}
                          <h5 style={{ margin: '12px 0 8px', fontSize: '13px', color: '#60a5fa' }}>Назначить задачу</h5>
                          {colorInput('Фон', discSettings.assignTaskButtonBackground, (v) => updateDisc('assignTaskButtonBackground', v))}
                          {colorInput('Цвет текста', discSettings.assignTaskButtonTextColor, (v) => updateDisc('assignTaskButtonTextColor', v))}
                          {colorInput('Бордер', discSettings.assignTaskButtonBorderColor, (v) => updateDisc('assignTaskButtonBorderColor', v))}
                        </div>
                        <h4 style={{ margin: '16px 0 12px', fontSize: '14px', color: '#60a5fa' }}>Блок результатов по каждой вкладке</h4>
                        {colorInput('Фон показа результатов', discSettings.resultsBackground, (v) => updateDisc('resultsBackground', v))}
                        {colorInput('Цвет текста результатов', discSettings.resultsTextColor, (v) => updateDisc('resultsTextColor', v))}
                        {colorInput('Бордер блока результатов', discSettings.resultsBorderColor, (v) => updateDisc('resultsBorderColor', v))}
                        <div style={{ marginTop: '20px', padding: '16px', borderRadius: '12px', border: '2px solid rgba(33, 147, 176, 0.5)', background: 'linear-gradient(135deg, rgba(33, 147, 176, 0.08) 0%, rgba(26, 95, 110, 0.06) 100%)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)' }}>
                          <h4 style={{ margin: '0 0 12px', fontSize: '14px', color: '#2193b0', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FiBarChart2 size={18} /> Краткая информация (первая вкладка)
                          </h4>
                          <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#9ca3af' }}>Цвета текста в первой вкладке результатов: подписи (Укр почта, Новая почта…), числа и строка «За дату:».</p>
                          {colorInput('Цвет подписей (Укр почта, Новая почта, RMP…)', discSettings.summaryLabelColor, (v) => updateDisc('summaryLabelColor', v))}
                          {colorInput('Цвет чисел расхождений', discSettings.summaryValueColor, (v) => updateDisc('summaryValueColor', v))}
                          {colorInput('Цвет строки «За дату:»', discSettings.summaryDateColor, (v) => updateDisc('summaryDateColor', v))}
                        </div>
                      </div>
                    </Section>
                  </>
                );
              })()}
            </div>
          )}

          {/* Вкладка: Видимость кнопок сайтбара */}
          {activeTab === 'sidebar-visibility' && canManageVisibility() && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <Section 
                title="👁️ Управление видимостью кнопок сайтбара" 
                icon={<FiEye size={18} />}
                expanded={true}
              >
                <div style={{ 
                  padding: '16px', 
                  background: 'rgba(59, 130, 246, 0.05)', 
                  borderRadius: '8px',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  marginBottom: '16px'
                }}>
                  <p style={{ margin: 0, color: '#9ca3af', fontSize: '14px', lineHeight: 1.6 }}>
                    Настройте видимость каждой кнопки сайтбара. Вы можете сделать кнопку видимой для всех пользователей 
                    или выбрать конкретные департаменты, которым она будет доступна. Администраторы и HR-менеджеры всегда видят все кнопки.
                  </p>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {SIDEBAR_BUTTONS.map(button => (
                    <SidebarButtonVisibilityControl
                      key={button.key}
                      button={button}
                      visibility={sidebarVisibility[button.key]}
                      onVisibilityChange={(key, value) => {
                        setSidebarVisibility(prev => ({
                          ...prev,
                          [key]: value
                        }));
                      }}
                      onHasChanges={setHasChanges}
                    />
                  ))}
                </div>

                <div style={{ 
                  marginTop: '20px', 
                  padding: '12px', 
                  background: 'rgba(251, 191, 36, 0.1)', 
                  borderRadius: '8px',
                  border: '1px solid rgba(251, 191, 36, 0.3)'
                }}>
                  <p style={{ margin: 0, color: '#fbbf24', fontSize: '13px', lineHeight: 1.6 }}>
                    ⚠️ <strong>Важно:</strong> Изменения вступят в силу после сохранения настроек. 
                    Все пользователи увидят обновленное меню сайтбара.
                  </p>
                </div>
              </Section>
            </div>
          )}
        </div>

        {/* Футер с кнопками */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '12px',
            padding: '16px 24px',
            borderTop: '1px solid rgba(75, 85, 99, 0.3)'
          }}
        >
          <button
            onClick={handleReset}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: '1px solid rgba(239, 68, 68, 0.5)',
              background: 'rgba(239, 68, 68, 0.1)',
              color: '#ef4444',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(239, 68, 68, 0.2)';
              e.target.style.borderColor = 'rgba(239, 68, 68, 0.7)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(239, 68, 68, 0.1)';
              e.target.style.borderColor = 'rgba(239, 68, 68, 0.5)';
            }}
          >
            ⚠️ Сбросить к дефолту
          </button>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={onClose}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: '1px solid rgba(75, 85, 99, 0.5)',
                background: 'transparent',
                color: '#9ca3af',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '14px'
              }}
            >
              Отмена
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: hasChanges ? '1px solid rgba(34, 197, 94, 0.5)' : '1px solid rgba(75, 85, 99, 0.5)',
                background: hasChanges ? 'rgba(34, 197, 94, 0.2)' : 'rgba(75, 85, 99, 0.1)',
                color: hasChanges ? '#4ade80' : '#6b7280',
                cursor: hasChanges ? 'pointer' : 'not-allowed',
                fontWeight: 700,
                fontSize: '14px'
              }}
            >
              Сохранить
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

// Вспомогательные компоненты
function Section({ title, icon, children, expanded = true, onToggle }) {
  return (
    <div
      style={{
        background: 'rgba(31, 41, 55, 0.5)',
        border: '1px solid rgba(75, 85, 99, 0.3)',
        borderRadius: '12px',
        padding: '16px',
        transition: 'all 0.3s ease'
      }}
    >
      <div 
        style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px', 
          marginBottom: expanded ? '16px' : '0',
          cursor: 'pointer',
          transition: 'margin 0.3s ease'
        }}
        onClick={onToggle}
      >
        <span style={{ color: '#60a5fa' }}>{icon}</span>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#e5e7eb', flex: 1 }}>{title}</h3>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggle && onToggle();
          }}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#60a5fa',
            cursor: 'pointer',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            transition: 'transform 0.3s ease',
            transform: expanded ? 'rotate(0deg)' : 'rotate(180deg)'
          }}
        >
          <FiChevronUp size={20} />
        </button>
      </div>
      {expanded && (
        <div 
          style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '14px',
            animation: 'slideDown 0.3s ease'
          }}
        >
          {children}
        </div>
      )}
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            max-height: 0;
          }
          to {
            opacity: 1;
            max-height: 1000px;
          }
        }
      `}</style>
    </div>
  );
}

function SettingRow({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: '13px', fontWeight: 600, color: '#9ca3af', marginBottom: '6px' }}>
        {label}
      </div>
      {children}
    </div>
  );
}


